{
  "common": {
    "retry": "Retry",
    "cancel": "Cancel",
    "back": "Back",
    "more": "More",
    "loading": "Loading",
    "language": "Language",
    "poweredBy": "Powered by {{name}}",
    "appUpdate": {
      "title": "A new version is ready",
      "subtitle": "Save your work, then reload to start using it.",
      "reload": "Reload"
    },
    "brandLogoAlt": "OOD Core Business Platform",
    "copyright": "© {{year}} {{holder}}. All rights reserved.",
    "delete": "Delete",
    "close": "Close",
    "remove": "Remove",
    "appVersion": "Version {{version}}",
    "appVersionShort": "v{{version}}"
  },
  "status": {
    "synced": "Synced",
    "pending_one": "{{count}} waiting to sync",
    "pending_other": "{{count}} waiting to sync",
    "offlineHo": "Offline. Sales are saved in the store.",
    "storeUnreachable": "Can't reach the store server. Check the network cable or Wi‑Fi.",
    "managerMode": "Manager"
  },
  "requiresHo": {
    "reason": "This needs a connection to HO. You can finish this order without it.",
    "membership": "Membership needs a connection to HO. You can finish this order without it."
  },
  "keypad": {
    "backspace": "Delete last digit",
    "ok": "OK",
    "entry": {
      "amount": "Amount",
      "quantity": "Quantity",
      "phone": "Phone number"
    }
  },
  "login": {
    "title": "Log in",
    "staffCode": "Staff code",
    "password": "Password",
    "submit": "Log in",
    "lockedTitle": "Terminal locked",
    "lockedHint": "Log in again to continue, or enter another staff code to switch user.",
    "logOff": "Log off",
    "sso": "Sign in with {{name}}",
    "breakGlass": "Break-glass sign-in with staff code and password",
    "mfa": {
      "title": "Two-step sign-in",
      "code": "Authentication code",
      "hint": "The 6-digit code from your authenticator app, or one of your recovery codes."
    }
  },
  "businessDay": {
    "title": "Business day",
    "close": "Close business day",
    "openedAt": "Opened at {{time}}",
    "noneOpen": "No business day is open. It opens with the first order.",
    "closeBlocked": "Close these first:",
    "closeBody": "The day's report becomes final. Orders paid but still being delivered can finish after it closes.",
    "printReport": "Print day report",
    "printRunning": "Print day report (not final)",
    "printed": "Day report sent to {{printer}}.",
    "notPrinted": "No receipt printer is set up for this terminal or store.",
    "closedPrint": "The business day is closed. Its day report is printing.",
    "endsAt": "This business day ends at {{time}}; orders after that go to the next day.",
    "rolledOver": "This business day ended at {{time}}. The next order starts the next day.",
    "daysWaiting_one": "{{count}} earlier business day is waiting to be closed.",
    "daysWaiting_other": "{{count}} earlier business days are waiting to be closed.",
    "toClose": "Days to close",
    "status": {
      "open": "Trading",
      "closing": "Waiting to close"
    },
    "closeWarnings": "You can still close it:",
    "closeDay": "Close business day {{date}}"
  },
  "errors": {
    "shifts.business_day_already_open": "A business day is already open.",
    "shifts.business_date_already_used": "That business date has already been used.",
    "shifts.business_day_close_blocked": "The business day can't be closed yet.",
    "shifts.open_shifts_one": "{{count}} shift is still open",
    "shifts.open_shifts_other": "{{count}} shifts are still open",
    "sync.ho_unavailable": "Head office can't be reached. You can finish this order without this feature.",
    "unknown": "The request didn't go through. Check the connection, then tap Retry.",
    "import.file_invalid": "The file isn't an Excel workbook (.xlsx) of at most 10 MB.",
    "import.brand_not_found": "The brand wasn't found.",
    "identity.invalid_credentials": "The staff code or password is not correct.",
    "identity.user_inactive": "This staff account is locked or disabled. Ask a manager or head office.",
    "identity.no_access_at_store": "You don't have access at this store. Ask a manager.",
    "identity.too_many_attempts": "Too many failed attempts. Wait a few minutes, then try again.",
    "identity.session_ended": "Your session has ended. Log in again.",
    "identity.approver_invalid_credentials": "The manager's staff code or password is not correct.",
    "identity.approver_not_permitted": "This manager can't approve this action.",
    "identity.approver_is_requester": "Another person must approve this action.",
    "too_many_requests": "Too many attempts. Wait a minute, then try again.",
    "common.version_conflict": "Someone else changed this record. Close the form, reload and try again.",
    "common.version_required": "The record version is missing. Reload and try again.",
    "common.duplicate": "A record with this code already exists.",
    "common.not_found": "This record no longer exists.",
    "validation.failed": "Check the highlighted fields.",
    "identity.no_ho_access": "You don't have a head office role. Ask an administrator.",
    "common.parent_not_found": "The record this belongs to was not found.",
    "common.invalid_combination": "Some values don't fit together. Check the fields and try again.",
    "common.reference_invalid": "A linked record doesn't exist.",
    "sync.store_not_paired": "This store isn't connected to head office yet. Connect it in the store admin portal.",
    "sync.store_not_found": "The store was not found.",
    "sync.invalid_ip": "Enter IP addresses or networks only, for example 203.0.113.10 or 203.0.113.0/28.",
    "sync.pairing_code_invalid": "The pairing code is wrong or has expired. Ask head office for a new code.",
    "sync.ip_not_allowed": "Head office doesn't accept connections from this store's internet address. Ask head office to add it.",
    "sync.store_key_invalid": "This store's head office key isn't valid any more. Pair the store again with a new code.",
    "sync.ho_unreachable": "Head office can't be reached at this address. Check the address and the store's internet connection.",
    "sync.ho_url_invalid": "Enter the head office sync address, for example https://sync.example.com.",
    "sync.paired_to_other_store": "This store server already holds another store's data. It can't be paired with a different store.",
    "sync.change_failed": "A change from head office couldn't be applied. Head office support can see the details.",
    "kitchen.store_required": "Choose a store first.",
    "kitchen.ticket_not_found": "This kitchen ticket doesn't exist.",
    "kitchen.no_printer": "This station has no printer to print on.",
    "kitchen.item_not_found": "This item doesn't exist.",
    "printing.job_superseded": "This job was already printed on the fallback printer, so it can't be retried.",
    "printing.job_not_failed": "Only a failed job can be retried.",
    "printing.job_not_found": "This print job doesn't exist.",
    "printing.device_inactive": "This printer is switched off.",
    "printing.device_not_found": "This printer doesn't exist.",
    "printing.not_a_printer": "This device is not a printer.",
    "payments.no_open_shift": "Open a shift before taking payments.",
    "ordering.open_orders_one": "{{count}} order is still open",
    "ordering.open_orders_other": "{{count}} orders are still open",
    "ordering.open_orders_carried_one": "{{count}} unpaid order stays open and carries over to the next day",
    "ordering.open_orders_carried_other": "{{count}} unpaid orders stay open and carry over to the next day",
    "ordering.unfulfilled_orders_one": "{{count}} paid order still waits to be handed over or delivered",
    "ordering.unfulfilled_orders_other": "{{count}} paid orders still wait to be handed over or delivered",
    "ordering.scheduled_orders_one": "{{count}} unpaid order is for a later day and waits for it",
    "ordering.scheduled_orders_other": "{{count}} unpaid orders are for a later day and wait for it",
    "shifts.off": "This store doesn't use shifts.",
    "shifts.already_open": "A shift is already open. Close it before opening the next one.",
    "shifts.not_found": "The shift was not found.",
    "shifts.not_open": "The shift is already closed.",
    "shifts.changed": "The shift was changed on another terminal. Reload and try again.",
    "shifts.amount_invalid": "Enter an amount of zero or more.",
    "shifts.movement_type_invalid": "Choose paid in, paid out or cash drop.",
    "shifts.reason_required": "Choose a reason or write why.",
    "shifts.terminal_required": "This store counts cash per terminal: do this on a terminal.",
    "shifts.counts_required": "Count the cash in every drawer before closing the shift.",
    "shifts.no_open_business_day": "Open the business day first.",
    "shifts.window_invalid": "The shift must end at a different time than it starts.",
    "shifts.days_required": "Choose at least one day for the shift.",
    "promotions.code_unknown": "This code doesn't exist. Check it and try again.",
    "promotions.code_not_active": "This code can't be used now: it isn't valid yet, has ended or was cancelled.",
    "promotions.code_used_up": "This code has already been used as many times as it may be.",
    "promotions.not_eligible": "This code can't be used on this order.",
    "promotions.not_stackable": "This code can't be combined with the code already on the order.",
    "promotions.too_many_attempts": "Too many wrong codes from this store. Wait a few minutes and try again.",
    "promotions.line_required": "This code is for one item: select the item first.",
    "ordering.coupon_code_invalid": "A code is 4 to 32 letters and digits.",
    "ordering.coupon_already_applied": "This code is already on the order.",
    "ordering.coupon_excluded_by_deal": "A deal on this order can't be combined with codes.",
    "payments.redemption_excluded_by_deal": "A deal on this order can't be paid with points or gift vouchers.",
    "vouchers.code_unknown": "This voucher doesn't exist. Check the code and try again.",
    "vouchers.not_active": "This voucher can't be used: it isn't valid yet, has expired, is used up or was cancelled.",
    "vouchers.no_balance": "Nothing is left on this voucher to spend now.",
    "vouchers.wrong_currency": "This voucher is in another currency.",
    "vouchers.too_many_attempts": "Too many wrong codes from this store. Wait a few minutes and try again.",
    "payments.voucher_code_required": "Enter or scan the voucher's code.",
    "vouchers.not_eligible": "This voucher can't be used on this order.",
    "config.template_invalid": "The template has an error. The preview shows where.",
    "config.template_type_not_supported": "Stores print templates for receipts, bills, kitchen tickets and shift reports only.",
    "config.template_footer_only_bills": "Footer text is for a bill or a receipt. Use a template for other documents.",
    "config.template_in_use": "This template is in use. Remove where it is used first.",
    "config.template_assignment_type": "An assignment is for its template's document.",
    "config.template_assignment_brand": "A brand's template can be used only for that brand and its stores.",
    "catalog.default_exists": "Another one is already the default. Clear it there first.",
    "catalog.text_option_name": "Type what the option says, at most 200 characters.",
    "catalog.text_option_no_defaults": "Mark a tax category and a unit as the default first (Catalog › Tax categories and Units): a text option takes them.",
    "catalog.text_option_group": "The option group was not found.",
    "catalog.text_option_group_brand": "The option is for the option group's brand.",
    "shifts.clocked_in_one": "{{count}} staff member is still clocked in: closing the day clocks them out",
    "shifts.clocked_in_other": "{{count}} staff members are still clocked in: closing the day clocks them out",
    "shifts.not_clocked_in": "You aren't clocked in.",
    "shifts.count_too_late": "A later shift was closed already, so this one's cash can't be counted now. Close it without a count.",
    "identity.sso_failed": "Signing in with your company account didn't work. Try again, or ask IT.",
    "identity.sso_no_staff": "Your company account isn't linked to a staff member at head office. Ask IT to add you.",
    "identity.sso_code_invalid": "This sign-in has expired. Sign in again.",
    "identity.sso_not_set_up": "This company doesn't sign in with its own account.",
    "identity.use_sso": "Your company signs in with its own account. Use the sign-in button for it.",
    "common.mfa_required": "This needs two-step sign-in. Set up an authenticator app, then sign in again."
  },
  "apps": {
    "kds": {
      "empty": "No tickets. New orders appear here when they are sent to the kitchen."
    }
  },
  "approval": {
    "title": "Manager approval",
    "hint": "A manager enters their staff code and password to approve.",
    "approve": "Approve",
    "reason": "Reason"
  },
  "admin": {
    "typeToSearch": "Type to search",
    "chooseFirst": "Choose {{field}} first",
    "clearableOnEdit": "Clear it to share this with every brand (the same as Share with every brand). It can't move to another brand, and once shared it stays shared.",
    "fixedOnEdit": "Can't change once created. To move it, remove it and add it again.",
    "search": "Search",
    "create": "Create",
    "edit": "Edit",
    "save": "Save",
    "delete": "Delete",
    "actions": "Actions",
    "deleteConfirm": "Delete this record?",
    "empty": "Nothing here yet. Use Create to add the first one.",
    "emptyTitle": "No records yet",
    "noResultsTitle": "No results",
    "noResultsHint": "Try a different search or change the filters.",
    "retry": "Retry",
    "rowAction": "{{action}} \"{{record}}\"",
    "required": "(required)",
    "optional": "(optional)",
    "saving": "Saving…",
    "datePlaceholder": "yyyy-mm-dd",
    "quickFilter": {
      "all": "All"
    },
    "validation": {
      "required": "This field is required."
    },
    "overview": {
      "hoHint": "How the stores are connected and trading.",
      "storeHint": "This store's link to head office and today's trading.",
      "asOf": "As of {{time}}",
      "refresh": "Refresh",
      "empty": "Nothing to show yet.",
      "noAccessTitle": "Nothing to show",
      "noAccess": "Your role has no overview widgets. Use the menu on the left.",
      "chart": {
        "view": "Show as",
        "chart": "Chart",
        "table": "Table"
      },
      "lag": {
        "label": "Finished orders only, may lag",
        "about": "About these figures",
        "help": "Head office counts an order once the store has finished it and synced it. Figures can trail the store by a few minutes, and longer while a store is offline."
      },
      "fleet": {
        "title": "Store fleet",
        "online": "Online",
        "offline": "Offline",
        "neverConnected": "Never connected",
        "total": "Stores"
      },
      "problems": {
        "title": "Sync problems",
        "empty": "No store has sync problems.",
        "detail": "Outbox {{outbox}}, failed {{failed}}, stopped {{dead}}, conflicts {{conflicts}}",
        "open": "Open {{store}} connection"
      },
      "sales": {
        "title": "Sales today",
        "orders": "Orders",
        "netSales": "Net sales",
        "averageTicket": "Average ticket",
        "openOrders": "Open orders",
        "empty": "No finished orders yet today.",
        "byHour": "Sales by hour",
        "hour": "Hour",
        "day": "Day",
        "seriesName": "Net sales",
        "summary": "Net sales by {{unit}}, {{total}} in total, highest {{peak}}"
      },
      "month": {
        "revenue": "Revenue per day this month",
        "orders": "Completed orders per day this month",
        "ordersSeries": "Completed orders",
        "ordersSummary": "Completed orders by {{unit}}, {{total}} in total, most {{peak}}"
      },
      "status": {
        "title": "Orders by status",
        "status": "Status",
        "count": "Orders",
        "empty": "No orders yet today."
      },
      "payments": {
        "title": "Payments by provider",
        "provider": "Provider",
        "method": "Tender",
        "count": "Payments",
        "paid": "Paid",
        "refunded": "Refunded",
        "empty": "No payments yet."
      },
      "deliveries": {
        "title": "Delivery failures",
        "provider": "Provider",
        "total": "Deliveries",
        "failed": "Failed",
        "empty": "No failed deliveries.",
        "own": "Own drivers"
      },
      "link": {
        "title": "Head office link",
        "connected": "Connected to head office.",
        "offline": "Working offline. Orders and payments are unaffected.",
        "reason": "Reason: {{reason}}",
        "lastContact": "Last contact {{time}}",
        "waiting": "{{count}} messages waiting to sync",
        "manage": "Head office connection"
      },
      "day": {
        "title": "Business day",
        "open": "Open since {{time}}",
        "date": "Business date {{date}}",
        "closed": "No business day is open.",
        "closedHint": "It opens with the first order at the POS."
      },
      "biz": {
        "period": "Period",
        "periods": {
          "today": "Today",
          "7d": "Last 7 days",
          "month": "This month",
          "30d": "Last 30 days"
        },
        "kpis": "Key figures",
        "netSales": "Total sales",
        "vsPrevious": "vs the period before",
        "ofSales": "{{share}} of item sales",
        "openReport": "Open report",
        "thisPeriod": "This period",
        "previousPeriod": "Period before",
        "trend": "Sales trend",
        "trendToday": "Sales by hour today",
        "trendSummary": "Sales over the period, {{total}} in total, against the period before",
        "channels": "Sales by channel",
        "channel": "Channel",
        "share": "Share",
        "channelsSummary": "Sales split across {{count}} channels",
        "payments": "Payments",
        "method": "Payment method",
        "net": "Net",
        "paymentsSummary": "Payments split across {{count}} methods",
        "topItems": "Top 10 items",
        "item": "Item",
        "topItemsSummary": "The ten best-selling items by sales, led by {{name}}",
        "stores": "Stores by sales",
        "change": "Change",
        "storesSummary": "Stores ranked by sales, led by {{name}}",
        "busyHours": "Busy hours",
        "busyHoursSummary": "Sales by weekday and hour; darker cells are busier",
        "noSales": "No sales in this period",
        "noPayments": "No payments in this period"
      },
      "members": {
        "title": "Members",
        "members": "Members",
        "asOf": "Counted at {{time}}",
        "newMembers": "New members",
        "newToday": "New members today",
        "pointsHeld": "Points members hold",
        "trend": "New members per day",
        "noneJoined": "No one joined in this period or the one before",
        "trendSummary_one": "{{total}} new member in this period",
        "trendSummary_other": "{{total}} new members in this period",
        "byTier": "Members by tier",
        "noMembers": "No members yet",
        "tier": "Tier",
        "noTier": "No tier",
        "byTierSummary": "The largest tier is {{name}}"
      }
    },
    "errors": {
      "forbidden": "You don't have permission for this. Ask an administrator.",
      "forbiddenTitle": "No access",
      "notFound": "This page doesn't exist."
    },
    "fields": {
      "code": "Code",
      "name": "Name",
      "codeHelper": "Letters, digits, - or _. Saved in capitals.",
      "updatedAt": "Last changed"
    },
    "kitchen": {
      "addition": "Addition",
      "fallback": "Printed on fallback printer",
      "store": "Store",
      "storePlaceholder": "Choose a store",
      "columns": {
        "ticket": "Ticket",
        "store": "Store",
        "station": "Station",
        "order": "Order",
        "label": "Table or label",
        "saleType": "Sale type",
        "items": "Items",
        "addition": "Addition",
        "status": "Status",
        "printStatus": "Printing",
        "firedAt": "Sent at"
      },
      "filters": {
        "station": "Station",
        "status": "Status",
        "printStatus": "Print status",
        "store": "Store",
        "firedAt": "Sent at"
      },
      "ticket": {
        "new": "New",
        "in_progress": "In progress",
        "ready": "Ready",
        "bumped": "Done",
        "recalled": "Recalled",
        "voided": "Voided"
      },
      "print": {
        "none": "Not printed",
        "queued": "Waiting to print",
        "printed": "Printed",
        "failed": "Print failed"
      },
      "job": {
        "open": "Open",
        "queued": "Queued",
        "printing": "Printing",
        "printed": "Printed",
        "failed": "Failed",
        "cancelled": "Cancelled"
      },
      "printer": {
        "online": "Online",
        "offline": "Offline",
        "error": "Error",
        "unknown": "Unknown"
      },
      "stationType": {
        "kitchen": "Kitchen",
        "bar": "Bar",
        "pastry": "Pastry",
        "expo": "Expo",
        "other": "Other"
      },
      "tickets": {
        "title": "Kitchen tickets",
        "hint": "Every send to the kitchen, one ticket per station. Updates every 15 seconds.",
        "hoHint": "Tickets of finished orders from all stores.",
        "search": "Search by order number or table",
        "empty": "No kitchen tickets",
        "hoEmpty": "Head office receives a ticket when its order is finished and synced.",
        "back": "Back to kitchen tickets"
      },
      "detail": {
        "title": "{{station}} ticket {{number}}",
        "reprint": "Reprint",
        "reprinted": "The ticket was sent to the printer again.",
        "printedAt": "Printed at",
        "reprints": "Reprints",
        "orderNote": "Order note",
        "items": "Items",
        "quantity": "Qty",
        "item": "Item",
        "options": "Options",
        "note": "Note",
        "allergy": "Allergy note",
        "partOf": "(in {{name}})",
        "attempts": "Print attempts",
        "noAttempts": "No print attempts yet",
        "time": "Time"
      },
      "jobs": {
        "title": "Print queue",
        "hint": "Print jobs of this store. Updates every 15 seconds.",
        "storeOnly": "The print queue is kept in each store. Open the store admin portal to see it.",
        "createdAt": "Created",
        "device": "Printer",
        "status": "Status",
        "attempts": "Attempts",
        "error": "Last error",
        "nextAttempt": "Next attempt",
        "empty": "No print jobs",
        "retry": "Retry",
        "retried": "The job is queued again.",
        "superseded": "This job was already printed on the fallback printer, so it can't be retried.",
        "fallback": "Fallback",
        "reprint": "Reprint"
      },
      "board": {
        "title": "Kitchen stations",
        "hint": "Each station with its printers and today's tickets. Updates every 15 seconds.",
        "hoHint": "Stations of one store and today's tickets.",
        "pickStore": "Choose a store",
        "pickStoreHint": "Head office shows the stations of one store at a time.",
        "empty": "No kitchen stations",
        "emptyHint": "Head office sets up stations for the store.",
        "primary": "Printer",
        "fallback": "Fallback printer",
        "noPrinter": "none",
        "lastSeen": "Last seen {{time}}",
        "neverSeen": "Not seen yet",
        "testPrint": "Test print",
        "testPrinted": "Test page printed on {{name}}.",
        "testFailed": "Test page did not print on {{name}}.",
        "queued": "Queued jobs",
        "failed": "Failed jobs",
        "ticketsToday": "Tickets today",
        "lastTicket": "Last ticket",
        "none": "None yet",
        "notPrinted_one": "{{count}} ticket not printed",
        "notPrinted_other": "{{count}} tickets not printed"
      },
      "routing": {
        "title": "Kitchen routing",
        "hint": "Which station makes which item, as head office set it up. Read only.",
        "hoHint": "Check where an item goes in a store. Rules are edited in the station settings.",
        "test": "Test routing",
        "saleType": "Sale type",
        "item": "Item",
        "itemPlaceholder": "Choose an item",
        "run": "Test",
        "station": "Station",
        "reason": "Why",
        "nowhere": "No station: it would not be printed",
        "source": {
          "item": "Item rule",
          "category": "Category rule",
          "default": "Default station",
          "none": "No station"
        },
        "reasons": {
          "item_rule": "A rule for this item",
          "category_rule": "A rule for its category",
          "default_station": "No rule: the default station",
          "no_station": "No rule and no default station"
        },
        "rules": "Routing rules",
        "noRules": "No routing rules",
        "noRulesHint": "Without rules every item goes to the default station.",
        "appliesTo": "Applies to",
        "priority": "Priority",
        "ruleItem": "Item: {{name}}",
        "ruleCategory": "Category {{name}}",
        "allSaleTypes": "All sale types"
      },
      "order": {
        "tab": "Kitchen",
        "empty": "Not sent to the kitchen",
        "emptyHint": "Kitchen tickets appear here once the order is sent.",
        "unrouted_one": "{{count}} line had no routing rule",
        "unrouted_other": "{{count}} lines had no routing rule",
        "unroutedHint": "They went to the default station. Check the station routing in head office.",
        "send": "Send {{number}} at {{time}}",
        "station": "Station",
        "tickets": "Tickets"
      },
      "widget": {
        "title": "Kitchen",
        "notPrinted": "Tickets not printed"
      }
    },
    "nav": {
      "overview": "Overview",
      "brands": "Brands",
      "organisation": "Organisation",
      "orders": "Orders",
      "sales": "Sales",
      "payments": "Payments",
      "shipping": "Shipping",
      "storeConnections": "Store connections",
      "system": "System",
      "hoConnection": "Head office connection",
      "tableQuickSetup": "Quick table setup",
      "auditLog": "Audit log",
      "syncLogs": "Sync logs",
      "deliveryServices": "Delivery services",
      "deliveryProviders": "Delivery providers",
      "locationImport": "Import locations",
      "kitchen": "Kitchen",
      "kitchenStations": "Stations",
      "kitchenTickets": "Kitchen tickets",
      "printQueue": "Print queue",
      "kitchenRouting": "Routing",
      "shiftHistory": "Shift history",
      "memberPoints": "Member points",
      "einvoices": "Invoices",
      "einvoiceProviders": "Providers",
      "images": "Images",
      "tableQr": "Table QR codes",
      "messagingAccounts": "Messaging accounts",
      "messageTemplates": "Message templates",
      "customerMessages": "Customer messages",
      "translations": "Translations",
      "menuImport": "Import menu data",
      "paymentAccounts": "Payment accounts",
      "paymentProfiles": "Payment profiles",
      "paymentReconciliation": "Payment reconciliation",
      "masterDataPublish": "Publish master data",
      "issueRewards": "Issue rewards",
      "customerAccounts": "Customers",
      "cmsForms": "Forms",
      "formSubmissions": "Form submissions",
      "onlineOrders": "Orders",
      "dayReports": "Day reports",
      "reports": "Reports",
      "reportSales": "Sales",
      "reportProducts": "Product mix",
      "reportHours": "Sales by hour",
      "reportPayments": "Payments and tax",
      "reportQr": "QR reconciliation",
      "reportControls": "Voids and discounts",
      "reportJournal": "Sales journal",
      "reportEinvoices": "E-invoice reconciliation",
      "reportMenu": "Menu engineering",
      "reportStaff": "Staff performance",
      "reportOperations": "Operations",
      "reportCustomers": "Customers and promotions",
      "stockBalances": "Stock on hand",
      "stockMovements": "Movements",
      "stockEntries": "Goods received and waste",
      "stockCounts": "Counts",
      "stockTransfers": "Transfers",
      "stockSettings": "Stock settings",
      "reportStockBalances": "Stock on hand",
      "reportStockMovements": "Stock movements",
      "reportStockTransfers": "Stock transfers",
      "reportStockCounts": "Stock counts",
      "posApp": "POS app",
      "attendance": "Attendance",
      "branding": "Branding",
      "integrationLogs": "Integration logs",
      "auditSettings": "Audit log retention",
      "signIn": "Sign-in",
      "customerSecurity": "Customer account security"
    },
    "ho": {
      "title": "Head office admin"
    },
    "store": {
      "title": "Store admin",
      "noAccessTitle": "No access",
      "noAccess": "You don't have access to the store admin portal. Ask your manager."
    },
    "account": {
      "title": "Account",
      "staffCode": "Staff code: {{code}}",
      "tenant": "Company {{code}}",
      "store": "Store {{code}}"
    },
    "details": "Details",
    "yes": "Yes",
    "no": "No",
    "none": "None",
    "choose": "Choose",
    "saved": "Saved",
    "jsonInvalid": "Enter a valid JSON object, e.g. {}.",
    "weekdays": {
      "mon": "Mon",
      "tue": "Tue",
      "wed": "Wed",
      "thu": "Thu",
      "fri": "Fri",
      "sat": "Sat",
      "sun": "Sun"
    },
    "everyDay": "Every day",
    "setPassword": "Set password",
    "newPassword": "New password",
    "passwordHint": "At least 8 characters. The staff member uses it at every store after the next sync.",
    "passwordSet": "Password saved.",
    "passwordTooShort": "Use at least 8 characters.",
    "sync": {
      "tab": "Connection",
      "title": "Store connections",
      "hint": "How each store server connects to head office. Stores call head office; head office never calls stores.",
      "status": {
        "not_paired": "Not paired",
        "pairing": "Waiting for pairing",
        "paired": "Paired",
        "revoked": "Key revoked"
      },
      "online": "Online",
      "offline": "Offline",
      "never": "Never",
      "network": "Network",
      "publicIps": "Allowed internet addresses",
      "publicIpsHelp": "One per line: the store's public IP (203.0.113.10) or network (203.0.113.0/28). Head office accepts the store's calls only from these.",
      "anyAddress": "No addresses yet: head office accepts this store's key from any address.",
      "lanHost": "Store server on the store network",
      "lanHostHelp": "For example 192.168.1.10. Terminals and devices in the store connect here.",
      "lanPort": "Port",
      "saveNetwork": "Save network settings",
      "saved": "Network settings saved.",
      "pairing": "Pairing",
      "pairingHint": "Create a code, then enter it on the store server: store admin portal › Connect to head office. The code works once, for 30 minutes.",
      "createCode": "Create pairing code",
      "code": "Pairing code",
      "codeValid": "Valid until {{time}}. It's shown only now.",
      "copyCode": "Copy pairing code",
      "copied": "Copied",
      "empty": "No stores to show.",
      "repairWarning": "Using a new code replaces the store's current key.",
      "key": "Store key",
      "keyIssued": "Key {{prefix}}…, issued {{time}}",
      "revoke": "Revoke key",
      "revokeTitle": "Revoke this store's key?",
      "revokeBody": "The store stops syncing at once. Sales continue at the store and sync after it's paired again.",
      "revokedAt": "Revoked {{time}}",
      "health": "Health",
      "appVersion": "Version",
      "outboxDepth": "Waiting to send",
      "oldestPending": "Oldest waiting since",
      "lastHeartbeat": "Last heartbeat",
      "lastPush": "Last data received",
      "lastPull": "Master data position",
      "lastSeenIp": "Last called from",
      "lanAddresses": "Store server addresses",
      "lastError": "Last error reported",
      "columns": {
        "store": "Store",
        "status": "Status",
        "online": "Connection",
        "lastHeartbeat": "Last heartbeat",
        "appVersion": "Version",
        "pending": "Waiting",
        "ip": "Last called from"
      },
      "lastRelay": "Last relay poll (online orders)",
      "heldChanges": "Changes waiting for an upgrade",
      "heldChangesValue": "{{count}} ({{types}}) since {{since}}"
    },
    "connect": {
      "title": "Connect to head office",
      "hint": "This store server isn't connected to head office yet. Ask head office for a pairing code for this store, then enter it here.",
      "hoUrl": "Head office sync address",
      "hoUrlHelp": "For example https://sync.example.com",
      "code": "Pairing code",
      "codeHelp": "Eight characters, for example K7QP-2M9X.",
      "submit": "Connect",
      "connected": "Connected to head office as {{store}}. Staff can log in once their accounts arrive, usually within a minute.",
      "continue": "Continue to login"
    },
    "hoConnection": {
      "title": "Head office connection",
      "pairedAs": "Connected as {{store}}",
      "hoUrl": "Head office sync address",
      "key": "Store key",
      "pairedAt": "Paired",
      "reachable": "Head office answers.",
      "unreachable": "Head office can't be reached. Sales continue; data syncs when it's back.",
      "lastContact": "Last contact",
      "lastPull": "Master data updated",
      "lastPush": "Data sent",
      "pending": "Waiting to send",
      "oldestPending": "Oldest waiting since",
      "lanUrls": "Terminals and devices connect to",
      "test": "Test connection",
      "testOk": "Head office answered. It sees this store at {{ip}}.",
      "repair": "Pair again",
      "repairHint": "Only when head office asks: pair this store server again with a new code.",
      "never": "Not yet",
      "held": "Waiting for orders to finish",
      "syncNow": "Sync now",
      "syncNowDone_one": "Up to date: {{count}} change from head office taken.",
      "syncNowDone_other": "Up to date: {{count}} changes from head office taken.",
      "syncNowMore": "Changes taken. More are coming in the background.",
      "onDemandHint": "Menus, prices and setup from head office arrive when head office publishes them, or when you press Sync now.",
      "heldChanges_one": "{{count}} change from head office is waiting for this store to be upgraded",
      "heldChanges_other": "{{count}} changes from head office are waiting for this store to be upgraded",
      "heldChangesBody": "This store’s release can’t take them yet ({{types}}). Everything else is in. Install the latest store setup: they go in by themselves after the upgrade."
    },
    "filters": {
      "title": "Filters",
      "button": "Filters",
      "buttonCount_one": "Filters ({{count}})",
      "buttonCount_other": "Filters ({{count}})",
      "close": "Close filters",
      "any": "Any",
      "from": "From",
      "to": "To",
      "online": "Connection",
      "status": "Status",
      "reset": "Reset filters",
      "apply": "Apply filters"
    },
    "shifts": {
      "history": "Shift history",
      "historyHint": "The store's shifts, newest first: cash expected and counted, and who opened and closed them.",
      "shift": "Shift",
      "opened": "Opened",
      "closed": "Closed",
      "status": "Status",
      "expected": "Expected cash",
      "counted": "Counted",
      "variance": "Over or short",
      "open": "Open",
      "closedStatus": "Closed",
      "forced": "Closed without a count",
      "unscheduled": "Unscheduled shift",
      "empty": "No shifts yet",
      "drawer": "Drawer",
      "store": "Store",
      "float": "Opening float",
      "cashSales": "Cash taken",
      "cashRefunds": "Cash refunded",
      "paidIn": "Paid in",
      "paidOut": "Paid out",
      "drops": "Cash drops",
      "tenders": "Taken by method",
      "method": "Method",
      "paid": "Paid",
      "refunded": "Refunded",
      "count": "Payments",
      "movements": "Cash in and out",
      "type": "Type",
      "amount": "Amount",
      "note": "Note",
      "at": "At",
      "print": "Print shift report",
      "printed": "Sent to {{printer}}.",
      "notPrinted": "No receipt printer is set up.",
      "movement": {
        "opening_float": "Opening float",
        "paid_in": "Paid in",
        "paid_out": "Paid out",
        "cash_drop": "Cash drop",
        "no_sale": "No sale (drawer opened)",
        "float_adjust": "Float adjustment"
      },
      "noteLabel": "Note: {{note}}",
      "orders": "Orders by status",
      "orderCount": "Orders",
      "total": "Total",
      "net": "Net",
      "noOrders": "No orders in this shift",
      "noTakings": "No payments taken in this shift",
      "takingsTotal": "Total taken: {{amount}}",
      "openOrders_one": "{{count}} order is still open. It carries over to the next shift unless it is paid or voided.",
      "openOrders_other": "{{count}} orders are still open. They carry over to the next shift unless they are paid or voided.",
      "figures": "Guests {{guests}} · average check {{average}} · voids {{voids}} ({{voidAmount}})",
      "methodCounted": "Counted",
      "noSales": "No-sale drawer opens: {{count}}",
      "ended": "Ended"
    },
    "masterData": {
      "title": "Publish master data",
      "hint": "Changes made at head office (menus, prices, items, setup) reach stores and the website only when you publish them here. A store that is offline takes them when it's back.",
      "website": "Website and table QR",
      "websitePublishedAt": "Last published {{time}}",
      "websiteNever": "Not published yet",
      "publishWebsite": "Publish to website",
      "publishAll": "Publish to all stores",
      "publish": "Publish",
      "alsoWebsite": "Also publish to the website",
      "confirmStore": "Publish to {{store}}?",
      "confirmAll_one": "Publish to {{count}} store?",
      "confirmAll_other": "Publish to all {{count}} stores?",
      "confirmWebsite": "Publish to the website and the stores that sell online?",
      "confirmBody": "Everything changed at head office so far goes out now. Stores take it within seconds while they are online.",
      "publishedStores_one": "Published to {{count}} store.",
      "publishedStores_other": "Published to {{count}} stores.",
      "publishedWebsite": "Published to the website.",
      "publishedBoth_one": "Published to {{count}} store and the website.",
      "publishedBoth_other": "Published to {{count}} stores and the website.",
      "changesWaiting": "Changes waiting",
      "upToDate": "Up to date",
      "never": "Never",
      "empty": "There are no stores yet.",
      "columns": {
        "store": "Store",
        "changes": "Head office changes",
        "published": "Last published",
        "status": "Publish",
        "contact": "Last contact"
      },
      "status": {
        "never": "Not published",
        "pending": "Waiting for the store",
        "current": "Taken"
      },
      "publishGroup": "Publish to a store group",
      "confirmGroup_one": "Publish to {{group}}: {{count}} store?",
      "confirmGroup_other": "Publish to {{group}}: {{count}} stores?",
      "groupEmpty": "{{group}} has no stores yet. Add stores to the group first."
    },
    "addressLookup": {
      "label": "Find the latitude and longitude",
      "placeholder": "e.g. 12 Lê Lợi, Bến Nghé, Hồ Chí Minh",
      "help": "Type the store's address and pick a suggestion, or press Find. It fills the latitude and longitude, the street and, when the names match, the province and ward.",
      "find": "Find",
      "finding": "Finding the address…",
      "pick": "Pick the address",
      "none": "No address found. Add the ward and province, or check the spelling.",
      "unavailable": "Finding the address isn't available. Fill in the latitude and longitude yourself."
    },
    "dayReport": {
      "title": "Day reports",
      "hint": "Each business day's sales, tax, discounts, voids, takings and cash, from this store's own data. A day still open is not final.",
      "empty": "No business days yet",
      "date": "Business day",
      "status": "Status",
      "opened": "Opened",
      "closedAt": "Closed",
      "closed": "Closed",
      "openNotFinal": "Open, not final",
      "print": "Print day report",
      "downloadCsv": "Download CSV",
      "printed": "Day report sent to {{printer}}.",
      "notPrinted": "No receipt printer is set up for this store.",
      "total": "Total sales",
      "totalCaption": "Completed orders",
      "orders": "Orders",
      "guestsCaption_one": "{{count}} guest",
      "guestsCaption_other": "{{count}} guests",
      "averageCheck": "Average check",
      "voids": "Voids",
      "voidsCaption_one": "{{count}} void",
      "voidsCaption_other": "{{count}} voids",
      "salesSummary": "Sales",
      "subtotal": "Items",
      "discounts": "Discounts",
      "charges": "Charges",
      "tax": "Tax",
      "rounding": "Rounding",
      "guests": "Guests",
      "invoiceRequests_one": "{{count}} order asked for a VAT invoice.",
      "invoiceRequests_other": "{{count}} orders asked for a VAT invoice.",
      "byHour": "Sales by hour",
      "hour": "Hour",
      "amount": "Amount",
      "quantity": "Quantity",
      "name": "Name",
      "byCategory": "Sales by category",
      "topItems": "Best sellers",
      "bySaleType": "Sales by sale type",
      "byChannel": "Sales by channel",
      "deals": "Deals",
      "other": "Other",
      "noSales": "No sales this day",
      "taxes": "Tax by rate",
      "taxable": "Taxable",
      "discountsTitle": "Discounts",
      "source": "From",
      "reason": "Reason",
      "count": "Count",
      "noDiscounts": "No discounts this day",
      "discountSource": {
        "manual": "Manual",
        "promotion": "Promotion",
        "coupon": "Code",
        "member": "Member",
        "marketplace": "Marketplace",
        "points": "Points"
      },
      "voidsCapped": "Showing the latest {{shown}} of {{count}} voids.",
      "at": "Time",
      "order": "Order",
      "item": "Item",
      "wholeOrder": "Whole order",
      "kitchen": "Kitchen",
      "afterSend": "After sending",
      "by": "By",
      "approved": "approved",
      "noVoids": "No voids this day",
      "tenders": "Takings by payment method",
      "nonRefundable": "Non-refundable vouchers",
      "guestTypes": "Guests by type",
      "guestType": "Guest type",
      "forfeited": "Excess kept",
      "method": "Method",
      "paid": "Paid",
      "refunded": "Refunded",
      "net": "Net",
      "noTakings": "No payments this day",
      "cash": "Shifts",
      "shift": "Shift",
      "noShifts": "No shifts this day",
      "movements": "Paid in {{paidIn}} · paid out {{paidOut}} · cash drops {{drops}}",
      "takings": "Takings",
      "outsideShifts_one": "Not in a shift ({{count}} payment)",
      "outsideShifts_other": "Not in a shift ({{count}} payments)"
    },
    "pickerFirst": "Showing the first {{count}}. Type to find others.",
    "pickerMatches": "Showing the first {{count}} matches. Type more to narrow them.",
    "stock": {
      "balances": "Stock on hand",
      "balancesHint": "What the store holds of each stock-tracked item, in its base unit, valued at standard cost. Select an item to see its movements.",
      "movements": "Stock movements",
      "movementsHint": "Every change to stock, newest first: sales, goods received, waste, adjustments, counts and transfers.",
      "entries": "Goods received and waste",
      "entriesHint": "Goods received, waste and adjustments recorded at this store. Each is posted when saved.",
      "counts": "Stock counts",
      "countsHint": "Count what is on the shelf; posting the count records the differences.",
      "transfers": "Stock transfers",
      "transfersHint": "Stock sent to or received from other stores of the brand.",
      "settings": "Stock settings",
      "settingsHint": "How stock is handled at this store.",
      "settingsSaved": "Stock settings saved",
      "autoSoldOut": "Mark items sold out when stock runs out",
      "autoSoldOutHint": "When a tracked item that is sold goes to zero or below, it is marked sold out on the POS; it becomes available again when stock is added. Items marked sold out by hand are not changed.",
      "on": "On",
      "off": "Off",
      "code": "Code",
      "item": "Item",
      "items": "Items",
      "onHand": "On hand",
      "unitCost": "Unit cost",
      "value": "Value",
      "lastMovement": "Last movement",
      "searchItems": "Search items",
      "noBalances": "No stock-tracked items",
      "noBalancesHint": "Items appear here once HO marks them as stock-tracked.",
      "when": "When",
      "type": "Type",
      "quantity": "Quantity",
      "by": "By",
      "note": "Note",
      "noMovements": "No movements",
      "number": "Number",
      "reason": "Reason",
      "reference": "Reference",
      "referenceHint": "For example the supplier's delivery note number.",
      "lineCount": "Lines",
      "postedAt": "Posted",
      "createdAt": "Created",
      "sentAt": "Sent",
      "receivedAt": "Received",
      "newEntry": "New entry",
      "newEntryHint": "Record goods received, waste or an adjustment. Quantities can be entered in any of the item's units.",
      "noEntries": "No entries yet",
      "chooseReason": "Choose a reason",
      "reasonRequired": "Choose a reason for waste or an adjustment.",
      "linesRequired": "Add at least one item, each with a quantity.",
      "post": "Post",
      "lines": "Lines",
      "baseQuantity": "In base unit",
      "addItem": "Add item",
      "unit": "Unit",
      "removeLine": "Remove {{name}}",
      "entryTypeHint": {
        "receipt": "Adds stock. Enter the cost per unit if it is known.",
        "waste": "Takes stock out: spoiled, dropped or expired.",
        "adjustment": "Corrects stock up or down; use a negative quantity to take stock out."
      },
      "status": "Status",
      "counted": "Counted",
      "expected": "Expected",
      "difference": "Difference",
      "countedOf": "Counted quantity of {{name}}",
      "newCount": "New count",
      "startCount": "Start count",
      "noCounts": "No counts yet",
      "itemsRequired": "Choose at least one item.",
      "spotOneItem": "A spot count is for one item.",
      "countType": {
        "full": "Full count",
        "partial": "Partial count",
        "spot": "Spot check"
      },
      "countTypeHint": {
        "full": "Every stock-tracked item of the store.",
        "partial": "The items you choose; more can be added while counting.",
        "spot": "One item, checked now."
      },
      "countSaved": "Count saved",
      "cancelCount": "Cancel count",
      "postCount": "Post count",
      "postCountTitle": "Post count {{number}}?",
      "postCountBody": "{{counted}} of {{total}} lines are counted. The differences of the counted lines are posted to stock; lines not counted are left as they are. The count can't be changed afterwards.",
      "cancelCountTitle": "Cancel count {{number}}?",
      "cancelCountBody": "Nothing is posted to stock, and the count can't be used again.",
      "state": {
        "draft": "Draft",
        "in_progress": "In progress",
        "posted": "Posted",
        "cancelled": "Cancelled",
        "sent": "Sent",
        "received": "Received"
      },
      "direction": "Direction",
      "directions": {
        "out": "Sent",
        "in": "Received"
      },
      "otherStore": "Other store",
      "fromStore": "From",
      "toStore": "To",
      "newTransfer": "New transfer",
      "newTransferHint": "Send stock to another store of the brand. It is saved as a draft; sending takes the stock out of this store.",
      "noTransfers": "No transfers yet",
      "chooseStore": "Choose a store",
      "storeRequired": "Choose the store to send to.",
      "storesUnavailable": "The list of stores comes from head office. Try again when the store is online.",
      "saveDraft": "Save draft",
      "send": "Send",
      "cancelTransfer": "Cancel transfer",
      "receive": "Receive",
      "quantitySent": "Sent",
      "quantityReceived": "Received",
      "receivedOf": "Received quantity of {{name}}",
      "receiveHint": "Enter what arrived of each line, then select Receive. A transfer can be received once.",
      "confirm": {
        "send": "Send transfer {{number}}?",
        "cancel": "Cancel transfer {{number}}?",
        "receive": "Receive transfer {{number}}?"
      },
      "confirmBody": {
        "send": "The stock is taken out of this store now and the transfer goes to {{store}} when the store is online. It can't be changed afterwards.",
        "cancel": "The draft is cancelled; nothing is taken out of stock.",
        "receive": "What arrived is added to this store's stock. Differences from what {{store}} sent are kept at this store and at head office."
      },
      "txn": {
        "sale": "Sale",
        "receipt": "Goods received",
        "waste": "Waste",
        "adjustment": "Adjustment",
        "count": "Count",
        "transfer_out": "Transfer out",
        "transfer_in": "Transfer in"
      }
    },
    "posApp": {
      "title": "POS apps for Windows and tablets",
      "intro": "Devices with a POS app connect to this store server. Show this page on the device you are setting up, or read the address from it.",
      "qrLabel": "QR code with this store's address",
      "address": "Store address",
      "stepInstall": "Install the POS app on the device: the Windows installer on a till, the tablet POS on a tablet (from your IT team).",
      "stepConnect": "Open the app and scan this code (tablet), or type the address (Windows).",
      "stepTerminal": "A manager logs in on the device and chooses which terminal it is.",
      "downloads": {
        "title": "Download the app",
        "version": "Version {{version}}, from this store's package.",
        "windows": "Windows installer",
        "tablet": "Tablet POS (APK)",
        "rootCertificate": "Store root certificate",
        "none": "This store's package has no POS app yet. Ask your IT team for the Windows installer or the tablet POS."
      }
    },
    "attendance": {
      "title": "Attendance",
      "hint": "Who clocked in and out, and when. Open a row to correct its times or clock someone out.",
      "staff": "Staff",
      "in": "Clocked in",
      "out": "Clocked out",
      "clockedIn": "Still in",
      "reason": "Clocked out by",
      "reasons": {
        "self": "Themselves",
        "manager": "A manager",
        "day_close": "Closing the day"
      },
      "edited": "Correction",
      "corrected": "Corrected",
      "search": "Search staff on this page",
      "empty": "Nobody has clocked in yet.",
      "correct": "Correct attendance",
      "outHint": "Leave empty while they are still at work.",
      "note": "Why it was corrected"
    },
    "branding": {
      "title": "Branding",
      "hint": "What sign-in screens and footers show in every app: the product's name, the copyright holder and the “Powered by” line. Empty fields keep each app's own. Stores receive changes with their next sync; set each brand's logo on the brand.",
      "productName": "Product name",
      "productNameHelp": "The name of the system, as staff know it. Empty: the app's own.",
      "copyright": "Copyright holder",
      "copyrightHelp": "Shown in footers as “© year holder”. Empty: the “Powered by” name, then the product name.",
      "poweredByName": "Powered by",
      "poweredByNameHelp": "Who provides the platform, shown as “Powered by …” in footers and on sign-in screens.",
      "poweredByUrl": "Powered by link",
      "poweredByUrlHelp": "Optional web address the “Powered by” line links to, starting with https://.",
      "preview": "Preview",
      "previewNone": "Nothing set: each app shows its own branding.",
      "saved": "Branding saved. Stores show it after their next sync."
    },
    "history": {
      "title": "History",
      "when": "When",
      "who": "Who",
      "what": "What",
      "record": "Record",
      "changes": "Changes",
      "system": "System",
      "emptyTitle": "No changes recorded",
      "empty": "Changes staff make are recorded here, with who made them and when.",
      "actions": {
        "create": "Created",
        "update": "Changed",
        "delete": "Removed",
        "restore": "Restored"
      },
      "storeHint": "Every change staff made in this store's portal, with logins and approvals: who, when, and what changed."
    },
    "auditSettings": {
      "title": "Audit log retention",
      "hint": "How long the audit log keeps who changed what. Set it to your retention policy; entries older than this are removed daily at head office, and every change to this setting is itself recorded.",
      "keepAll": "Keep everything",
      "months": "Keep for (months)",
      "monthsHelp": "1 to 600 months.",
      "monthsInvalid": "Enter whole months from 1 to 600.",
      "default": "Not set yet: 120 months (10 years), the minimum for accounting records in Vietnam.",
      "saved": "Saved. Entries older than this are removed with the next daily clean-up."
    },
    "removed": {
      "show": "Show",
      "current": "Current records",
      "view": "Removed",
      "restore": "Restore",
      "confirm": "Restore this record?",
      "confirmBody": "It comes back as it was when it was removed, and reaches the stores with their next sync. Restoring is recorded in the audit log.",
      "emptyTitle": "Nothing removed",
      "empty": "Records removed from this list are kept here, and can be restored."
    }
  },
  "carbon": {
    "itemsPerPage": "Items per page:",
    "itemRange": "{{min}}–{{max}} of {{total}} items",
    "pageRange": "of {{total}} pages",
    "itemRangeMany": "{{min}}–{{max}} of more than {{total}} items",
    "pageOnly": "Page {{page}}",
    "previousPage": "Previous page",
    "nextPage": "Next page",
    "pageNumber": "Page number",
    "close": "Close",
    "showPassword": "Show password",
    "hidePassword": "Hide password",
    "skipToContent": "Skip to main content",
    "clearSearch": "Clear search",
    "openMenu": "Open menu",
    "closeMenu": "Close menu",
    "error": "Error",
    "warning": "Warning",
    "info": "Information"
  },
  "md": {
    "fields": {
      "account_id": "Member account",
      "account_no": "Account number",
      "account_prefix": "Account number prefix",
      "action_code": "Action",
      "address": "Address (IP or port)",
      "allow_ai_crawlers": "Let AI search crawlers in",
      "allow_cod": "Cash on delivery",
      "allow_item_modifiers": "Allow item options",
      "allow_pay_at_pickup": "Pay at the counter for pickup",
      "allow_quantity": "Allow quantities",
      "allow_repeat": "Same choice more than once",
      "allows_change": "Gives change",
      "amount_entry": "Amount entered at the POS",
      "applies_after_tax": "Apply after VAT",
      "apply_rules": "When it applies",
      "attendance_mode": "Clock in and out",
      "audience": "Who can use it",
      "audience_segment_id": "Segment",
      "auto_accept": "Accept orders automatically",
      "auto_apply": "Apply automatically",
      "available_again_at": "Available again at",
      "background_url": "Background image URL",
      "balance": "Balance",
      "balance_reserved": "Held on open orders",
      "barcode": "Barcode",
      "base_behavior": "Works like",
      "base_distance_m": "Distance at the base fee (m)",
      "base_fee": "Base fee",
      "base_uom_id": "Base unit",
      "batch_id": "Batch",
      "birth_date": "Birth date",
      "blind_count": "Blind count (hide the expected cash)",
      "body": "Template",
      "brand_id": "Brand",
      "busy_extra_minutes": "Busy: extra minutes",
      "busy_until": "Busy until",
      "canvas_height": "Height",
      "canvas_width": "Width",
      "capacity": "Seats",
      "card_digits": "Digits in a card number",
      "card_no": "Card number",
      "card_prefix": "Card number prefix",
      "cash_count_enabled": "Count cash at close",
      "category_id": "Category",
      "channel_type": "Channel type",
      "charge_type_id": "Charge",
      "closes_at": "Closes at",
      "club_id": "Club",
      "code": "Code",
      "code3": "Code (3 letters)",
      "col_span": "Columns wide",
      "combine_match": "People in",
      "component_type": "Type",
      "conditions": "Who qualifies",
      "config": "Settings (JSON)",
      "connection_type": "Connection",
      "consent_at": "Agreed on",
      "contact_id": "Contact",
      "contact_no": "Contact number",
      "contact_prefix": "Contact number prefix",
      "content": "Content",
      "count_scope": "Count cash",
      "country_code": "Country",
      "country_id": "Country",
      "cover_url": "Cover image",
      "currency_code": "Currency",
      "day_close_open_orders": "Open orders at day close",
      "day_cutoff_at": "Business day ends at",
      "day_of_week": "Day",
      "days_of_week": "Days",
      "deal_component_item_id": "Deal choice",
      "deal_id": "Deal",
      "decimals": "Decimals",
      "default_language": "Default language",
      "default_opening_float": "Default opening float",
      "default_sale_type_id": "Default sale type",
      "delivery_sale_type_id": "Sale type of delivery orders",
      "delivery_service_id": "Delivery service",
      "density": "Density",
      "deposit_percent": "Deposit for orders for a later day (%)",
      "description": "Description",
      "description_i18n": "Description translations",
      "device_type": "Device type",
      "dine_in_sale_type_id": "Sale type of dine-in orders",
      "discount_value": "Discount value",
      "display_category_id": "Shown under category",
      "display_name": "Name",
      "district_code": "District",
      "division_id": "Area",
      "document_type": "Document",
      "effective_from": "Effective from",
      "effective_to": "Effective to",
      "email": "Email",
      "email_buyer": "Email the invoice to the buyer",
      "end_time": "To",
      "ends_at": "Ends at",
      "engine": "Template engine",
      "excludes_promotions": "No other promotions on the order",
      "excludes_redemptions": "No points or voucher redemption on the order",
      "expiry_reminder_days": "Remind members this many days before points expire (e.g. 30, 7; empty: never)",
      "external_store_id": "Store id at the channel",
      "face_value": "Face value",
      "factor": "Factor",
      "factor_to_base": "Base units in one",
      "fallback_device_id": "Fallback printer",
      "fee_mode": "Fee",
      "fixed_fee": "Fixed fee",
      "free_from_amount": "Free delivery from",
      "from_uom_id": "From unit",
      "full_name": "Full name",
      "full_name_i18n": "Full name translations",
      "funded_by": "Funded by",
      "gender": "Gender",
      "grid_col": "Column",
      "grid_cols": "Columns",
      "grid_row": "Row",
      "grid_rows": "Rows",
      "has_changes": "Unpublished changes",
      "height": "Height",
      "hold_minutes": "Send after payment (minutes)",
      "host": "Host name",
      "icon": "Icon",
      "icon_url": "Icon (web address)",
      "image_url": "Image URL",
      "ingredient_item_id": "Ingredient",
      "invoice_kind": "Invoice kind",
      "invoice_series": "Invoice series",
      "is_active": "Active",
      "is_available": "Available",
      "is_base": "Is the base unit",
      "is_default": "Default",
      "is_default_sale": "Default selling unit",
      "is_enabled": "Enabled",
      "is_free": "Free (not assigned)",
      "is_main": "Main contact",
      "is_open_price": "Price entered at the POS",
      "is_primary": "Primary",
      "is_refundable": "Can be refunded",
      "is_required": "Required",
      "is_skippable": "Can be skipped",
      "is_stackable": "Can combine with other codes",
      "is_stock_tracked": "Track stock",
      "is_system": "System role",
      "is_tax_inclusive": "Prices include tax",
      "is_taxable": "Taxable",
      "is_visible": "Visible",
      "item_category_id": "Category",
      "item_group_id": "Item group",
      "item_id": "Item",
      "item_type": "Item type",
      "joined_at": "Joined",
      "joined_store_id": "Joined at store",
      "kds_enabled": "Kitchen display",
      "kds_late_minutes": "Late after (minutes)",
      "kds_warn_minutes": "Warn after (minutes)",
      "keeps_excess": "Keeps the excess, no change",
      "kind": "Kind",
      "kitchen_name": "Kitchen name",
      "label": "Label",
      "label_i18n": "Label translations",
      "label_override": "Label on button",
      "language": "Language",
      "languages": "Languages (codes, e.g. vi,en)",
      "large_text": "Large text",
      "last_run_at": "Worked out",
      "latitude": "Latitude",
      "legal_entity_id": "Legal entity",
      "legal_name": "Legal name",
      "level": "Level",
      "limits": "Limits (JSON)",
      "line_detail": "Invoice lines",
      "list_filter": "List filter (JSON)",
      "locale": "Locale",
      "logo_url": "Logo (web address)",
      "longitude": "Longitude",
      "manual_confirm_approval_above": "Manager approval above",
      "maps_url": "Google Maps link",
      "marketing_consent": "Agrees to marketing",
      "match": "Match",
      "max_cod_amount": "Most cash on delivery per order",
      "max_discount": "Maximum discount",
      "max_distance_m": "Farthest delivered (m)",
      "max_open_orders": "Most orders in progress at once",
      "max_quantity": "Maximum quantity",
      "max_redeem_percent": "Most of the items before VAT paid with points (%)",
      "max_select": "Maximum choices",
      "max_select_override": "Maximum choices (override)",
      "max_uses": "Uses per code",
      "max_value": "Maximum",
      "member_account_id": "Issued to member",
      "member_count": "People",
      "menu_id": "Menu",
      "menu_layout_id": "Menu layout",
      "method": "Method",
      "method_type": "Payment type",
      "min_points": "Points to reach it",
      "min_redeem_points": "Fewest points to pay with",
      "min_select": "Minimum choices",
      "min_select_override": "Minimum choices (override)",
      "minimum_order_amount": "Minimum order",
      "model": "Model",
      "modifier_group_id": "Modifier group",
      "name": "Name",
      "name_i18n": "Name translations",
      "next_account_no": "Next account number",
      "next_card_no": "Next card number",
      "next_contact_no": "Next contact number",
      "note": "Note",
      "notify": "Tell the member",
      "og_image_url": "Image when shared",
      "open_mode": "Opening",
      "opened_on": "Opened on",
      "opens_at": "Opens at",
      "opens_drawer": "Opens cash drawer",
      "order_flow_id": "Order flow",
      "palette_key": "Colour",
      "paper_width_mm": "Paper width (mm)",
      "parent_id": "Parent category",
      "parent_page_id": "Parent page",
      "part_role": "Use",
      "part_segment_id": "Segment",
      "paused_reason": "Why it is paused",
      "payment_method_name": "Payment method on the invoice",
      "per_member_limit": "Times per member",
      "permission_code": "Permission",
      "phone": "Phone",
      "phone_code": "Phone prefix",
      "phone_normalized": "Phone",
      "phrase": "Word or phrase",
      "pickup_sale_type_id": "Sale type of pickup orders",
      "place_name": "Place",
      "placement": "Placement",
      "point_value": "Value of a point",
      "points": "Points",
      "points_balance": "Points",
      "points_expiry_months": "Months until earned points expire (0: never)",
      "points_reserved": "Points held on open orders",
      "points_with_other_discounts": "Points with other discounts on the same bill",
      "port": "Port",
      "pos_layout_id": "POS layout",
      "pos_layout_section_id": "POS section",
      "pos_profile_id": "POS profile",
      "pos_x": "X",
      "pos_y": "Y",
      "postal_code": "Postal code",
      "postal_code_pattern": "Postal code pattern",
      "preferred_language": "Language",
      "prefix": "Code prefix",
      "prep_minutes": "Preparation time (minutes)",
      "price": "Price",
      "pricing_method": "Pricing",
      "primary_device_id": "Printer",
      "priority": "Priority",
      "privacy_notice_version": "Privacy notice version",
      "prompt_text": "Prompt text",
      "provider_account_id": "Provider account",
      "provider_code": "Provider code",
      "provider_name": "Provider",
      "province_code": "Province",
      "published_at": "Published",
      "published_version": "Version on the website",
      "quantity": "Quantity",
      "question": "Question",
      "question_i18n": "Question translations",
      "rate": "Rate (%)",
      "reason": "Reason",
      "reason_required": "Reason required",
      "reason_type": "Used for",
      "refresh_requested_at": "Refresh asked",
      "region_id": "Region",
      "require_mode": "Shift for payments",
      "requires_address": "Needs an address",
      "requires_approval": "Needs manager approval",
      "requires_customer": "Needs a customer",
      "requires_ho": "Needs head office",
      "requires_reference": "Needs a reference",
      "requires_table": "Needs a table",
      "reward_method": "Discount type",
      "reward_scope": "Applies to",
      "reward_settings": "Reward settings",
      "reward_type": "Reward",
      "reward_value": "Discount",
      "role": "Button role",
      "role_id": "Role",
      "rotation": "Rotation",
      "row_span": "Rows high",
      "rule": "Rule (JSON)",
      "rule_type": "Rule type",
      "rules": "When it can be used",
      "sale_type_id": "Sale type",
      "sales_channel_id": "Sales channel",
      "schedule_days": "Days ahead customers may order (0: as soon as possible only)",
      "scheme_id": "Scheme",
      "scheme_since": "In this scheme since",
      "scope": "Applies to",
      "screen_class": "Screen",
      "section_type": "Section type",
      "segment_id": "Only members of segment",
      "seller_tax_code": "Seller tax code",
      "send_by": "Send the day's invoices by (store time)",
      "seo_description_i18n": "Description for search engines",
      "seo_title_i18n": "Title for search engines",
      "service_type": "Provider service",
      "settings": "Settings (JSON)",
      "settings_override": "Settings override (JSON)",
      "shape": "Shape",
      "short_name": "Short name",
      "short_name_i18n": "Short name translations",
      "shortcut_key": "Shortcut key",
      "site_name": "Name customers see",
      "slot_limit": "Most orders per time slot",
      "slot_minutes": "Time slot length (minutes)",
      "slug": "Key",
      "sort_order": "Order",
      "source": "Imported from",
      "spend_amount": "For every amount spent",
      "staff_code": "Staff code",
      "standard_cost": "Standard cost",
      "start_time": "From",
      "starts_at": "Starts at",
      "station_type": "Station type",
      "status": "Status",
      "step_fee": "Fee per step",
      "step_m": "Then per (m)",
      "step_type": "Step",
      "store_group_id": "Store group",
      "store_id": "Store",
      "street_address": "Street address",
      "summary_i18n": "Summary",
      "support_email": "Support email",
      "support_phone": "Support phone",
      "table_area_id": "Area",
      "target_page_id": "Opens page",
      "target_type": "Button opens",
      "tax_category_id": "Tax category",
      "tax_code": "Tax code",
      "template_code": "Invoice template",
      "terminal_id": "Terminal",
      "terminal_type": "Terminal type",
      "theme": "Theme",
      "tier_counts_adjustments": "Points adjusted by staff count toward the tier",
      "tier_counts_orders": "Points from orders count toward the tier",
      "tier_counts_rewards": "Points from rewards programs count toward the tier",
      "tier_period_months": "Months counted for a scheme",
      "tier_points": "Tier points",
      "time_zone": "Time zone",
      "title_i18n": "Title",
      "to_uom_id": "To unit",
      "trigger_event": "Ask when",
      "trigger_settings": "Trigger settings",
      "trigger_type": "Trigger",
      "unit_type_id": "Unit type",
      "uom_id": "Unit",
      "upcharge": "Upcharge",
      "use_item_groups": "Show variants as one item",
      "uses_confirmed": "Used",
      "uses_reserved": "Held on open orders",
      "valid_from": "Valid from",
      "valid_to": "Valid to",
      "value": "Value",
      "variance_tolerance": "Variance allowed without a manager",
      "ward_code": "Ward",
      "waste_percent": "Waste (%)",
      "width": "Width"
    },
    "enums": {
      "day_of_week": {
        "1": "Monday",
        "2": "Tuesday",
        "3": "Wednesday",
        "4": "Thursday",
        "5": "Friday",
        "6": "Saturday",
        "7": "Sunday"
      },
      "gender": {
        "female": "Female",
        "male": "Male",
        "other": "Other"
      },
      "invoice_kind": {
        "vat": "VAT invoice",
        "sales": "Sales invoice"
      },
      "line_detail": {
        "combined": "One line per item or deal, its options and contents in the name",
        "detailed": "Options and deal contents on lines of their own"
      },
      "entry_type": {
        "earn": "Earned",
        "reverse": "Reversed",
        "redeem": "Paid with points",
        "refund": "Refunded",
        "adjust": "Adjusted"
      },
      "reward_method": {
        "percent": "Percent",
        "amount": "Amount"
      },
      "reward_scope": {
        "order": "The whole order",
        "line": "One item"
      },
      "funded_by": {
        "brand": "Brand",
        "store": "Store",
        "partner": "Partner"
      },
      "audience": {
        "anyone": "Anyone with the code",
        "members": "Any member with the code",
        "holder": "Only the member it was issued to",
        "segment": "People in a segment"
      },
      "require_mode": {
        "required": "Required: no payment without an open shift",
        "optional": "Optional: linked when one is open",
        "off": "Off: shifts not used"
      },
      "open_mode": {
        "manual": "By hand",
        "auto": "Automatically: start and hand over by the shift times"
      },
      "day_close_open_orders": {
        "block": "Block closing: pay, void or move them first",
        "carry_over": "Carry over to the next day"
      },
      "count_scope": {
        "store": "One count for the store",
        "terminal": "Per terminal"
      },
      "slot_minutes": {
        "15": "15 minutes",
        "30": "30 minutes",
        "60": "1 hour"
      },
      "attendance_mode": {
        "off": "Off",
        "on_login": "Logging in clocks staff in",
        "separate": "Clock in and out on its own"
      },
      "kind": {
        "internal": "Own drivers",
        "external": "Provider booked by head office",
        "rules": "By conditions",
        "list": "Uploaded list",
        "combined": "Other segments combined",
        "site": "Site settings",
        "home": "Home page",
        "page": "Page",
        "promotion": "Promotion",
        "news": "News",
        "form": "Form"
      },
      "combine_match": {
        "all": "All the included segments",
        "any": "Any included segment"
      },
      "part_role": {
        "include": "Include",
        "exclude": "Leave out"
      },
      "fee_mode": {
        "manual": "Cashier enters it",
        "fixed": "Fixed amount",
        "quote": "Quoted by the provider"
      },
      "base_behavior": {
        "dine_in": "Dine-in",
        "takeaway": "Takeaway",
        "delivery": "Delivery",
        "pickup": "Pickup"
      },
      "channel_type": {
        "pos": "POS",
        "website": "Website",
        "app": "Mobile app",
        "zalo_mini_app": "Zalo mini app",
        "marketplace": "Marketplace",
        "api": "Open API",
        "qr": "Table QR"
      },
      "display": {
        "buttons": "Buttons",
        "swatches": "Colour swatches",
        "list": "List"
      },
      "component_type": {
        "auto": "Always included",
        "choice": "Customer chooses"
      },
      "connection_type": {
        "tcp": "Network (TCP)",
        "usb": "USB",
        "serial": "Serial",
        "bluetooth": "Bluetooth",
        "provider_sdk": "Provider SDK",
        "browser": "On the terminal (PDF or screen)"
      },
      "density": {
        "terminal": "Terminal",
        "tablet": "Tablet",
        "handheld": "Handheld"
      },
      "screen_class": {
        "terminal": "Terminal",
        "tablet": "Tablet",
        "handheld": "Handheld"
      },
      "device_type": {
        "receipt_printer": "Receipt printer",
        "kitchen_printer": "Kitchen printer",
        "label_printer": "Label printer",
        "cash_drawer": "Cash drawer",
        "customer_display": "Customer display",
        "scanner": "Scanner",
        "payment_terminal": "Card terminal",
        "kds_screen": "Kitchen display"
      },
      "document_type": {
        "receipt": "Receipt",
        "bill": "Bill",
        "kitchen_ticket": "Kitchen ticket",
        "delivery_slip": "Delivery slip",
        "shift_report": "Shift report",
        "day_report": "Day report",
        "label": "Label"
      },
      "engine": {
        "scriban": "Template",
        "text": "Footer text only"
      },
      "item_type": {
        "sellable": "Sold",
        "modifier_only": "Only as an option",
        "ingredient": "Ingredient"
      },
      "method": {
        "percent": "Percent",
        "fixed": "Fixed amount",
        "amount": "Amount"
      },
      "method_type": {
        "cash": "Cash",
        "card": "Card",
        "qr": "QR code",
        "bank_transfer": "Bank transfer",
        "e_wallet": "E-wallet",
        "voucher": "Voucher",
        "points": "Points",
        "pay_later": "Pay later",
        "other": "Other"
      },
      "palette_key": {
        "neutral": "Neutral",
        "blue": "Blue",
        "cyan": "Cyan",
        "teal": "Teal",
        "green": "Green",
        "purple": "Purple",
        "magenta": "Magenta",
        "red": "Red",
        "warm-gray": "Warm grey"
      },
      "paper_width_mm": {
        "58": "58 mm",
        "80": "80 mm"
      },
      "placement": {
        "order_rail": "Order actions",
        "line_rail": "Line actions",
        "command_bar": "Command bar",
        "more_sheet": "More",
        "order_header": "Order header",
        "table_sheet": "Table actions"
      },
      "pricing_method": {
        "fixed": "Fixed price",
        "sum_of_components": "Sum of items",
        "sum_minus_percent": "Sum minus a percent",
        "sum_minus_amount": "Sum minus an amount",
        "sum_minus_cheapest": "Cheapest item free"
      },
      "reason_type": {
        "void": "Void",
        "discount": "Discount",
        "refund": "Refund",
        "cash_movement": "Cash in/out",
        "order_reject": "Rejected online order",
        "table_reset": "Table reset",
        "stock_adjustment": "Stock adjustment",
        "correction": "Paid bill correction"
      },
      "role": {
        "send": "Send",
        "pay": "Pay",
        "bill": "Bill",
        "danger": "Danger",
        "tool": "Tool"
      },
      "scope": {
        "order": "Whole order",
        "line": "One line",
        "both": "Order or line"
      },
      "section_type": {
        "table_map": "Table map",
        "menu_listing": "Menu",
        "order_list": "Order list",
        "online_orders": "Online orders",
        "functions": "Functions",
        "reservations": "Reservations",
        "table_qr": "QR table orders"
      },
      "match": {
        "word": "Whole words",
        "anywhere": "Anywhere, inside words too"
      },
      "shape": {
        "rect": "Rectangle",
        "square": "Square",
        "round": "Round"
      },
      "station_type": {
        "kitchen": "Kitchen",
        "bar": "Bar",
        "pastry": "Pastry",
        "expo": "Expo",
        "other": "Other"
      },
      "default_language": {
        "vi": "Vietnamese",
        "en": "English"
      },
      "paused_reason": {
        "store_offline": "Store offline",
        "staff_paused": "Paused by staff",
        "capacity": "Too busy",
        "outside_hours": "Outside opening hours",
        "max_orders": "Too many open orders"
      },
      "status": {
        "open": "Open",
        "active": "Active",
        "inactive": "Inactive",
        "locked": "Locked",
        "disabled": "Disabled",
        "setup": "Being set up",
        "closed": "Closed",
        "pending": "Waiting to enrol",
        "revoked": "Revoked",
        "draft": "Draft",
        "paused": "Paused",
        "ended": "Ended",
        "used_up": "Used up",
        "expired": "Expired",
        "cancelled": "Cancelled",
        "blocked": "Blocked",
        "lost": "Lost",
        "published": "Published",
        "unpublished": "Taken down"
      },
      "step_type": {
        "select_table": "Select table",
        "enter_guests": "Enter guests",
        "select_customer": "Select customer",
        "prompt": "Ask a question",
        "take_order": "Take order",
        "send_to_kitchen": "Send to kitchen",
        "print_bill": "Print bill",
        "take_payment": "Take payment",
        "assign_driver": "Assign driver",
        "close": "Close",
        "accept_order": "Accept online order",
        "mark_ready": "Mark ready",
        "hand_over": "Hand over or serve",
        "out_for_delivery": "Hand to driver",
        "deliver": "Delivered"
      },
      "target_type": {
        "item": "An item",
        "deal": "A deal",
        "page": "Another page"
      },
      "terminal_type": {
        "fixed_pos": "Fixed POS",
        "tablet": "Tablet",
        "handheld": "Handheld",
        "kds": "Kitchen display",
        "kiosk": "Kiosk"
      },
      "theme": {
        "g10": "Light",
        "g100": "Dark"
      },
      "trigger_event": {
        "order_start": "Order starts",
        "item_added": "Item added",
        "before_payment": "Before payment"
      }
    },
    "resources": {
      "org_brand": "Brands",
      "org_legal_entity": "Legal entities",
      "org_store": "Stores",
      "org_store_opening_hours": "Opening hours",
      "org_store_group": "Store groups",
      "org_store_group_member": "Stores",
      "org_app_user": "Staff",
      "org_user_store_role": "Role assignments",
      "org_role": "Roles",
      "org_role_permission": "Permissions",
      "geo_country": "Countries",
      "geo_admin_level": "Address levels",
      "geo_admin_unit_type": "Unit types",
      "geo_region": "Regions",
      "geo_admin_division": "Provinces, districts and wards",
      "geo_postal_code": "Postal codes",
      "catalog_item": "Items",
      "catalog_item_uom": "Selling units",
      "catalog_item_group": "Item groups",
      "catalog_item_barcode": "Barcodes",
      "catalog_item_category_map": "Categories",
      "catalog_item_modifier_group": "Modifier groups",
      "catalog_recipe": "Recipe",
      "catalog_store_recipe": "Store recipes",
      "catalog_item_cost": "Standard cost",
      "catalog_item_category": "Categories",
      "catalog_modifier_group": "Modifier groups",
      "catalog_modifier": "Options",
      "catalog_deal": "Deals",
      "catalog_deal_cost": "Standard cost",
      "catalog_deal_component": "Components",
      "catalog_deal_component_item": "Choices",
      "catalog_deal_availability": "When and where",
      "catalog_deal_rule": "Rules",
      "catalog_tax_category": "Tax categories",
      "catalog_tax_rate": "Tax rates",
      "catalog_uom": "Units",
      "catalog_uom_conversion": "Unit conversions",
      "menu_menu": "Menus",
      "menu_menu_entry": "Items on the menu",
      "menu_menu_assignment": "Where and when",
      "menu_menu_layout": "Menu layouts",
      "menu_menu_page": "Pages",
      "menu_menu_button": "Buttons",
      "menu_price_list": "Price lists",
      "menu_price_list_entry": "Prices",
      "menu_menu_price_list": "Menus",
      "menu_price_list_assignment": "Where and when",
      "config_sales_channel": "Sales channels",
      "config_sale_type": "Sale types",
      "config_order_flow": "Order flows",
      "config_order_flow_step": "Steps",
      "config_pos_layout": "POS layouts",
      "config_pos_layout_section": "Sections",
      "config_pos_profile": "POS profiles",
      "config_pos_profile_sale_type": "Sale types",
      "config_pos_button": "Buttons",
      "config_payment_method": "Payment methods",
      "config_payment_method_assignment": "Where it is enabled",
      "config_delivery_service": "Delivery services",
      "config_delivery_service_assignment": "Where it is offered",
      "config_charge_type": "Charges and fees",
      "config_brand_site": "Brand websites",
      "config_brand_site_domain": "Domains",
      "config_delivery_fee_rule": "Delivery fees",
      "online_channel_store": "Online stores",
      "online_blocked_word": "Blocked words",
      "online_channel_store_hours": "Online ordering hours",
      "membership_member_club": "Member clubs",
      "membership_member_scheme": "Member schemes",
      "membership_point_rule": "Point rules",
      "membership_member_account": "Member accounts",
      "membership_member_contact": "Member contacts",
      "membership_member_card": "Member cards",
      "promotions_promotion": "Coupons",
      "promotions_coupon_batch": "Code batches",
      "promotions_coupon": "Codes",
      "membership_gift_voucher_batch": "Gift vouchers",
      "membership_gift_voucher": "Vouchers",
      "membership_reward_program": "Rewards programs",
      "crm_segment": "Segments",
      "crm_segment_part": "Combined from",
      "cms_page": "Pages",
      "config_discount_preset": "Discount presets",
      "config_reason_code": "Reason codes",
      "config_guest_type": "Guest types",
      "config_prompt": "Prompts",
      "config_prompt_answer": "Answers",
      "config_prompt_trigger": "When to ask",
      "config_document_template": "Receipt and ticket templates",
      "config_template_assignment": "Where it is used",
      "einvoice_einvoice_profile": "E-invoice profiles",
      "einvoice_profile_store": "Stores",
      "store_terminal": "Terminals",
      "store_device": "Printers and devices",
      "store_station": "Kitchen stations",
      "store_station_routing": "What goes here",
      "store_table_area": "Table areas",
      "store_floor_plan": "Floor plans",
      "store_dining_table": "Tables",
      "store_section_floor_plan": "Shown in POS sections",
      "store_shift_definition": "Shifts",
      "store_shift_settings": "Shift settings",
      "store_item_availability": "Sold out items",
      "store_delivery_service_availability": "Paused delivery services",
      "org_permission": "Permissions",
      "config_pos_action": "POS actions",
      "integration_provider_account": "E-invoice accounts"
    },
    "groups": {
      "org": "Organisation",
      "catalog": "Catalog",
      "menu": "Menus and prices",
      "pos": "POS setup",
      "tables": "Tables",
      "geo": "Locations",
      "promotions": "Coupons and vouchers",
      "loyalty": "Loyalty",
      "marketing": "Marketing",
      "cms": "CMS",
      "einvoice": "E-invoices",
      "online": "Online ordering",
      "devices": "Devices and printing",
      "stock": "Availability",
      "shifts": "Shifts"
    }
  },
  "orders": {
    "title": "Orders",
    "create": {
      "title": "New order",
      "saleType": "Sale type",
      "menu": "Menu",
      "searchItems": "Search items",
      "cart": "This order",
      "emptyCart": "Add items from the menu.",
      "totalHint": "The total is calculated when the order is saved.",
      "add": "Add",
      "noPrice": "No price",
      "unit": "Unit",
      "openPrice": "Price",
      "submit": "Create order",
      "chooseSaleType": "Choose a sale type to see the menu.",
      "min": "Choose at least {{count}}",
      "max": "Choose up to {{count}}"
    },
    "back": "Orders",
    "search": "Search by order number, customer or phone",
    "all": "All",
    "readOnly": "Orders are changed at the store. This is head office's copy.",
    "hoEmpty": "Orders appear here once stores send them to head office.",
    "filters": {
      "status": "Status",
      "saleType": "Sale type",
      "store": "Store",
      "from": "From",
      "to": "To",
      "openedAt": "Opened",
      "member": "Member",
      "withMember": "With a member",
      "withoutMember": "Without a member",
      "payment": "Payment",
      "kitchen": "Kitchen",
      "fulfilment": "Fulfilment"
    },
    "columns": {
      "number": "Order",
      "openedAt": "Opened",
      "saleType": "Sale type",
      "guests": "Guests",
      "customer": "Customer",
      "status": "Status",
      "total": "Total",
      "balance": "To pay",
      "store": "Store",
      "member": "Member"
    },
    "status": {
      "open": "Open",
      "completed": "Completed",
      "voided": "Voided",
      "merged": "Merged",
      "cancelled": "Cancelled"
    },
    "paymentStatus": {
      "unpaid": "Unpaid",
      "partially_paid": "Partly paid",
      "paid": "Paid",
      "overpaid": "Overpaid"
    },
    "kitchenStatus": {
      "not_sent": "Not sent",
      "partially_sent": "Partly sent",
      "sent": "Sent to kitchen",
      "in_progress": "Cooking",
      "ready": "Ready in kitchen",
      "served": "Served"
    },
    "fulfilmentStatus": {
      "waiting": "Waiting",
      "ready": "Ready",
      "out_for_delivery": "Out for delivery",
      "handed_over": "Handed over",
      "delivered": "Delivered",
      "served": "Served",
      "failed": "Delivery failed"
    },
    "statusKinds": {
      "order": "Order status",
      "payment": "Payment status",
      "kitchen": "Kitchen status",
      "fulfilment": "Fulfilment status"
    },
    "summary": {
      "subtotal": "Items",
      "discounts": "Discounts",
      "charges": "Charges",
      "taxIncluded": "Tax included",
      "tax": "Tax",
      "rounding": "Rounding",
      "total": "Total",
      "paid": "Paid",
      "balance": "To pay",
      "guests": "Guests",
      "openedAt": "Opened",
      "paidAt": "Paid",
      "promisedAt": "Promised for",
      "customer": "Customer",
      "customerNote": "Customer note",
      "kitchenNote": "Kitchen note",
      "menu": "Menu",
      "priceList": "Price list",
      "member": "Member",
      "openMember": "Open the member's account"
    },
    "tabs": {
      "items": "Items",
      "payments": "Payments",
      "delivery": "Delivery",
      "history": "History"
    },
    "actions": {
      "addItems": "Add items",
      "editDetails": "Edit details",
      "changeSaleType": "Change sale type",
      "addDiscount": "Add discount",
      "addCharge": "Add charge",
      "recordPayment": "Record payment",
      "voidOrder": "Void order",
      "editLine": "Edit line",
      "voidLine": "Void line",
      "remove": "Remove",
      "refund": "Refund",
      "dispatch": "Send out",
      "updateStatus": "Update status",
      "more": "More actions"
    },
    "lines": {
      "item": "Item",
      "amount": "Amount",
      "unit": "Unit",
      "tax": "Tax rate",
      "taxAmount": "Tax amount",
      "quantity": "Qty",
      "unitPrice": "Unit price",
      "discount": "Discount",
      "total": "Total",
      "note": "Note",
      "options": "Options",
      "status": "Status",
      "empty": "No items.",
      "statusValues": {
        "new": "Not sent",
        "sent": "Sent",
        "voided": "Voided"
      }
    },
    "discounts": {
      "title": "Discounts",
      "preset": "Preset",
      "manual": "Enter a discount",
      "name": "Name",
      "method": "Type",
      "percent": "Percent",
      "amount": "Amount",
      "value": "Value",
      "reason": "Reason",
      "appliesTo": "Applies to",
      "wholeOrder": "Whole order"
    },
    "charges": {
      "title": "Charges",
      "type": "Charge"
    },
    "details": {
      "guests": "Guests",
      "customer": "Customer",
      "name": "Name",
      "phone": "Phone",
      "province": "Province or city",
      "ward": "Ward",
      "street": "Street and number",
      "addressNote": "Address note",
      "promisedAt": "Promised time",
      "customerNote": "Customer note",
      "kitchenNote": "Kitchen note"
    },
    "saleType": {
      "title": "Change sale type",
      "hint": "Prices, charges and tax follow the new sale type."
    },
    "void": {
      "orderTitle": "Void order",
      "lineTitle": "Void line",
      "reason": "Reason",
      "note": "Note",
      "unsentHint": "This line hasn't been sent to the kitchen, so it is simply removed.",
      "orderHint": "The order is kept with its history and marked voided."
    },
    "payments": {
      "method": "Method",
      "amount": "Amount",
      "tendered": "Cash received",
      "change": "Change",
      "reference": "Reference",
      "kind": "Type",
      "paidAt": "Time",
      "receivedBy": "Taken by",
      "refundable": "Can refund",
      "status": "Status",
      "empty": "No payments yet.",
      "kinds": {
        "payment": "Payment",
        "refund": "Refund"
      },
      "changeDue": "Change to give: {{amount}}",
      "balanceDue": "Left to pay: {{amount}}",
      "posOnly": "Taken at the POS"
    },
    "methodTypes": {
      "cash": "Cash",
      "card": "Card",
      "qr": "QR",
      "bank_transfer": "Bank transfer",
      "e_wallet": "E-wallet",
      "voucher": "Voucher",
      "points": "Points",
      "pay_later": "Pay later",
      "other": "Other"
    },
    "refund": {
      "title": "Refund payment",
      "amount": "Amount to refund",
      "reason": "Reason",
      "max": "At most {{amount}}",
      "hint": "A refund is a new record; the payment and the paid order stay as they were."
    },
    "delivery": {
      "empty": "This order hasn't been sent out yet.",
      "notDelivery": "Only delivery orders are sent out.",
      "method": "Method",
      "methods": {
        "own_driver": "Own driver",
        "provider": "Delivery provider"
      },
      "driverName": "Driver",
      "driverPhone": "Driver phone",
      "providerCode": "Provider code",
      "providerReference": "Booking reference",
      "trackingUrl": "Tracking link",
      "track": "Track",
      "fee": "Delivery fee",
      "address": "Address",
      "status": "Status",
      "newStatus": "New status",
      "failureReason": "Why it failed",
      "note": "Note",
      "attempt": "Delivery {{number}}",
      "statuses": {
        "pending": "Waiting for a driver",
        "assigned": "Driver assigned",
        "picked_up": "Picked up",
        "out_for_delivery": "Out for delivery",
        "delivered": "Delivered",
        "failed": "Failed",
        "cancelled": "Cancelled",
        "searching_driver": "Finding a driver",
        "no_driver": "No driver found",
        "cancel_requested": "Cancelling",
        "returned": "Returned"
      },
      "knownProvider": "Provider used before",
      "providerName": "Provider name",
      "booking": "Booking",
      "bookingModes": {
        "provider": "Through the provider",
        "manual": "Entered by hand"
      },
      "eta": "Estimated time",
      "etaMinutes": "about {{minutes}} min"
    },
    "history": {
      "empty": "No history yet.",
      "by": "by {{name}}",
      "unknownUser": "unknown user",
      "events": {
        "order.created": "Order created",
        "order.line_added": "Item added",
        "order.line_changed": "Item changed",
        "order.line_voided": "Item voided",
        "order.discount_added": "Discount added",
        "order.discount_removed": "Discount removed",
        "order.charge_added": "Charge added",
        "order.charge_removed": "Charge removed",
        "order.details_changed": "Details changed",
        "order.sale_type_changed": "Sale type changed",
        "order.payment_recorded": "Payment recorded",
        "order.paid": "Order paid",
        "order.refunded": "Refunded",
        "order.voided": "Order voided",
        "order.delivery_created": "Sent out for delivery",
        "order.delivery_status_changed": "Delivery status changed",
        "order.sent": "Sent to kitchen",
        "order.payment_removed": "Payment removed"
      }
    },
    "approval": {
      "voidLine": "Void {{name}}",
      "voidOrder": "Void order {{number}}",
      "discount": "Discount on order {{number}}",
      "refund": "Refund {{amount}}"
    },
    "createOrder": "Create order",
    "memberOnly": "Member {{member}}'s orders",
    "allMembers": "Show every order",
    "einvoice": {
      "label": "E-invoice",
      "loading": "Asking head office…",
      "number": "No. {{number}}",
      "seller": "Seller tax code {{taxCode}}",
      "lookupCode": "Lookup code {{code}}",
      "lookUp": "Look it up",
      "status": {
        "waiting": "Not yet at head office",
        "pending": "Being issued",
        "issued": "Issued",
        "failed": "Head office is correcting it",
        "not_configured": "Store has no e-invoice profile yet",
        "cancelled": "Not invoiced (bill voided)",
        "not_reissued": "Not invoiced again: the original bill’s invoice stands and accounting adjusts it"
      }
    },
    "afterTax": "after VAT",
    "correction": {
      "menu": "Correct the paid bill",
      "changeMethod": "Change payment method",
      "changeMethodHint": "Moves this payment to the method it was really paid with, the same day: a refund and a payment of the same amount. The order total doesn’t change.",
      "methodChanged": "Payment method changed.",
      "guests": "Correct guests",
      "guestCount": "Guests",
      "guestsCorrected": "Guests corrected.",
      "voidRedo": "Void and redo",
      "voidRedoHint": "Every payment is refunded to its method and the bill voided; the same items open as a new order to fix and pay. Nothing goes to the kitchen again.",
      "voidedNoRedo": "The bill was voided, but it couldn’t be rung again. Create a new order.",
      "reason": "Reason",
      "note": "Note (optional)"
    }
  },
  "transactions": {
    "filters": {
      "provider": "Provider",
      "from": "From",
      "to": "To",
      "store": "Store",
      "status": "Status",
      "kind": "Type",
      "methodType": "Tender",
      "all": "All",
      "noProvider": "No provider (cash, own transfer)",
      "ownDriver": "Own drivers",
      "active": "In progress",
      "date": "Date"
    },
    "payments": {
      "title": "Payment transactions",
      "search": "Search by order number, reference or card last 4",
      "columns": {
        "paidAt": "Time",
        "order": "Order",
        "store": "Store",
        "provider": "Provider",
        "method": "Method",
        "kind": "Type",
        "amount": "Amount",
        "reference": "Reference",
        "status": "Status",
        "confirmation": "Confirmed by",
        "receivedBy": "Taken by"
      },
      "summary": {
        "count": "{{count}} transactions",
        "paid": "Paid",
        "refunded": "Refunded",
        "net": "Net"
      },
      "confirmation": {
        "auto": "Automatic",
        "terminal": "Card terminal",
        "provider_callback": "Provider",
        "manual": "Staff"
      },
      "statuses": {
        "pending": "Pending",
        "approved": "Approved",
        "declined": "Declined",
        "voided": "Voided",
        "failed": "Failed"
      },
      "empty": "No payments match these filters."
    },
    "shipping": {
      "title": "Shipping transactions",
      "search": "Search by order number, booking reference, driver or customer",
      "columns": {
        "createdAt": "Sent",
        "order": "Order",
        "store": "Store",
        "provider": "Provider",
        "reference": "Booking",
        "driver": "Driver",
        "customer": "Customer",
        "fee": "Fee",
        "status": "Status",
        "deliveredAt": "Delivered"
      },
      "summary": {
        "count": "{{count}} deliveries",
        "active": "In progress",
        "delivered": "Delivered",
        "failed": "Failed",
        "fees": "Fees"
      },
      "empty": "No deliveries match these filters."
    },
    "detail": {
      "history": "History",
      "back": "Back",
      "paymentTitle": "Payment {{reference}}",
      "refundTitle": "Refund {{reference}}",
      "shippingTitle": "Delivery for {{number}}",
      "facts": {
        "order": "Order",
        "store": "Store",
        "provider": "Provider",
        "method": "Method",
        "kind": "Type",
        "amount": "Amount",
        "tendered": "Cash received",
        "change": "Change",
        "reference": "Reference",
        "qrReference": "QR reference",
        "card": "Card",
        "status": "Status",
        "confirmation": "Confirmed by",
        "receivedBy": "Taken by",
        "paidAt": "Time",
        "refundable": "Can refund",
        "refundOf": "Refund of",
        "driver": "Driver",
        "customer": "Customer",
        "address": "Address",
        "fee": "Fee",
        "createdAt": "Sent",
        "pickedUpAt": "Picked up",
        "deliveredAt": "Delivered",
        "failureReason": "Why it failed",
        "tracking": "Tracking"
      },
      "events": {
        "recorded": "Recorded",
        "approved": "Approved",
        "declined": "Declined",
        "voided": "Voided",
        "failed": "Failed",
        "refunded": "Refunded",
        "removed": "Removed"
      },
      "refunds": "Refunds",
      "noRefunds": "No refunds.",
      "otherAttempts": "Other deliveries of this order",
      "timeline": "Timeline",
      "noEvents": "No history yet.",
      "by": "by {{name}}",
      "system": "system",
      "open": "View history"
    }
  },
  "pos": {
    "menu": {
      "emptyPage": "No items on this page.",
      "soldOut": "Sold out",
      "noPrice": "No price",
      "search": "Search items",
      "results": "Search results",
      "noResults": "No items match “{{query}}”.",
      "categories": "Categories",
      "scanned": "Added {{name}}",
      "scanUnknown": "No item with barcode {{code}}.",
      "dealUnavailable": "Not available now",
      "trail": "You are here",
      "sections": "Menu sections",
      "groups": "Groups",
      "sectionCount": "{{count}} on the order"
    },
    "ticket": {
      "label": "Order ticket",
      "tools": "Order actions",
      "dealItem": "{{quantity}} × {{name}}",
      "option": "{{quantity}} × {{name}}",
      "empty": "Tap items on the menu to start the order.",
      "voided": "Voided",
      "held": "Held",
      "subtotal": "Subtotal",
      "discounts": "Discounts",
      "taxIncluded": "Tax included",
      "tax": "Tax",
      "rounding": "Rounding",
      "total": "Total",
      "paid": "Paid",
      "balance": "Balance",
      "discount": "Discount",
      "itemDiscounts": "Item discounts",
      "deliveryCharge": "Delivery charge",
      "sent": "Sent",
      "notSent": "Not sent",
      "decrease": "One less",
      "increase": "One more",
      "remove": "Remove",
      "lineActions": "Line actions"
    },
    "modifiers": {
      "optional": "Optional",
      "upTo_one": "Up to {{count}}",
      "upTo_other": "Up to {{count}}",
      "choose_one": "Choose {{count}}",
      "choose_other": "Choose {{count}}",
      "atLeast_one": "At least {{count}}",
      "atLeast_other": "At least {{count}}",
      "between": "Choose {{min}}–{{max}}",
      "groups": "Option groups",
      "needsChoice": "Make a choice to continue",
      "quantity": "Quantity",
      "less": "One less",
      "more": "One more",
      "lessHint": "Right-click, press and hold, or press Delete to take one away.",
      "times": "× {{count}}",
      "add": "Add · {{price}}",
      "save": "Save · {{price}}"
    },
    "deal": {
      "includedOptions": "Options for {{name}}, included in the deal",
      "chooseOptions": "Choose options",
      "included": "Included",
      "chosen": "Chosen",
      "remove": "Remove {{name}}",
      "add": "Add · {{price}}",
      "addNoPrice": "Add deal",
      "choosePrompt": "Choose the items to see the price",
      "pricing": "Pricing…",
      "times": "× {{count}}",
      "freeChoice": "Included in the price",
      "includes": "Includes: {{items}}"
    },
    "actions": {
      "back": "Back",
      "more": "More",
      "notAvailable": "Not available on this terminal yet.",
      "labels": {
        "send": "Send",
        "pay": "Pay",
        "print_bill": "Print bill",
        "preview_bill": "Preview bill",
        "hold_fire": "Hold and fire",
        "order_note": "Order note",
        "order_discount": "Discount",
        "add_charge": "Add charge",
        "remove_charge": "Remove charge",
        "void_order": "Void order",
        "split_bill": "Split bill",
        "merge_bills": "Merge bills",
        "change_sale_type": "Sale type",
        "guest_count": "Guests",
        "member_lookup": "Member",
        "redeem_points": "Points",
        "apply_code": "Code",
        "line_quantity": "Quantity",
        "line_note": "Note",
        "line_seat": "Seat",
        "line_course": "Course",
        "line_discount": "Discount",
        "edit_modifiers": "Options",
        "repeat_line": "Repeat",
        "void_line": "Void line",
        "transfer_table": "Transfer table",
        "merge_tables": "Merge tables",
        "split_table": "Split table",
        "take_over_table": "Take over",
        "reset_table": "Reset table",
        "no_sale": "No sale",
        "refund": "Refund",
        "reprint_receipt": "Reprint receipt",
        "order_customer": "Customer",
        "order_invoice": "VAT invoice",
        "change_payment_method": "Change payment method",
        "correct_guests": "Correct guests",
        "void_redo": "Void and redo"
      },
      "sendCount_one": "Send · {{count}} new",
      "sendCount_other": "Send · {{count}} new",
      "payAmount": "Pay · {{amount}}"
    },
    "payment": {
      "title": "Payment",
      "balanceDue": "Balance due",
      "quickCash": "Quick cash",
      "exact": "Exact",
      "tenders": "Tenders",
      "applied": "Paid so far",
      "remove": "Remove {{tender}}",
      "reference": "Reference for {{tender}}",
      "confirm": "Confirm {{tender}}",
      "back": "Back to menu",
      "completed": "Paid",
      "changeDue": "Change due",
      "noChange": "No change due",
      "nextOrder": "Next order",
      "done": "Done",
      "returning": "Returning to {{section}}…",
      "stay": "Stay here",
      "amount": "Amount to pay",
      "remaining": "{{amount}} still to pay after this",
      "useBalance": "Use full balance",
      "payWith": "Pay with",
      "fullHint": "The full balance. Use the keypad to enter another amount.",
      "overpay": "More than the balance: cash gives {{amount}} change, a voucher that keeps the excess is taken whole with no change, other tenders take only the balance.",
      "voucherCode": "Gift voucher code",
      "voucherPartial": "The voucher covered {{amount}}; {{due}} is still due.",
      "quantityMax": "At most {{max}}.",
      "pointsAvailable": "{{points}} points to spend",
      "pointValue": "1 point = {{amount}}",
      "pointsMin": "at least {{points}}",
      "pointsMaxShare": "up to {{percent}}% of the items before VAT",
      "pointsNoOtherDiscounts": "not with other discounts",
      "redemptionExcludedByDeal": "A deal on this order can't be paid with gift vouchers.",
      "qr": {
        "title": "Scan to pay",
        "amount": "Amount to pay",
        "preparing": "Preparing the QR…",
        "waiting": "Waiting for the customer to pay with {{provider}}",
        "expiresIn": "Payable for {{time}}",
        "check": "Check payment",
        "checkSoon": "Check payment in {{seconds}} s",
        "cancel": "Cancel QR",
        "paid": "Paid",
        "failed": "The payment failed. Take another tender.",
        "expired": "The QR expired before it was paid. Take another tender.",
        "cancelled": "QR cancelled. Take another tender.",
        "refunded": "The customer paid after the QR ended; head office refunds it. Take another tender.",
        "otherTender": "Choose another tender",
        "codeLabel": "QR code to pay with {{provider}}"
      },
      "paidAmount": "Amount",
      "tendersHint": "Tap a tender to pay the amount on the right"
    },
    "home": {
      "newOrder": "New order",
      "empty": "No open orders here. Tap New order to start one.",
      "loadFailed": "The orders couldn't be loaded. They retry in a few seconds.",
      "quickSale": "Start a quick sale",
      "quickSaleHint": "Opens a new {{saleType}} order.",
      "filters": {
        "label": "Show",
        "active": "Open orders",
        "today": "All today"
      },
      "columns": {
        "number": "Order",
        "customer": "Customer",
        "status": "Status",
        "time": "Opened",
        "amount": "Total",
        "saleType": "Sale type"
      },
      "businessDay": "Business day",
      "changeTerminal": "Change terminal",
      "logOff": "Log off",
      "noSections": "This POS profile has no sections. Ask head office to add them.",
      "search": "Find by order number or customer",
      "hoDataTaken": "Last taken {{time}}",
      "hoDataNever": "No head office data taken yet",
      "syncNow": "Sync now",
      "syncNowDone_one": "Up to date: {{count}} change taken.",
      "syncNowDone_other": "Up to date: {{count}} changes taken.",
      "syncNowMore": "Changes taken. More are coming in the background.",
      "showMore_one": "Show more ({{count}} left)",
      "showMore_other": "Show more ({{count}} left)"
    },
    "terminal": {
      "title": "Set up this terminal",
      "hint": "A manager logs in and chooses which terminal this device is. The choice is saved on this device.",
      "choose": "Choose the terminal",
      "none": "No POS terminals are set up for this store. Add one in the store admin portal under Devices.",
      "notAllowed": "Only a manager who can set up devices can choose the terminal.",
      "use": "Use {{code}}",
      "cancel": "Keep the current terminal",
      "app": "This device's POS app",
      "forgetStore": "Connect to another store",
      "exitApp": "Close the POS app"
    },
    "order": {
      "newOrder": "New order",
      "back": "Back",
      "sent": "Sent to kitchen",
      "removed": "Removed {{name}}",
      "undo": "Undo",
      "conflict": "Someone else changed this order. It has been reloaded; check it and try again.",
      "readOnly": "Can't reach the store server. The order is read-only until it's back.",
      "quantity": "Quantity for {{name}}",
      "note": "Note for {{name}}",
      "orderNote": "Note for the kitchen",
      "save": "Save",
      "reason": "Reason",
      "discountTitle": "Discount",
      "lineDiscountTitle": "Discount on {{name}}",
      "presets": "Presets",
      "enterDiscount": "Or enter a discount",
      "percent": "Percent",
      "amount": "Amount",
      "apply": "Apply discount",
      "chargeTitle": "Add a charge",
      "saleTypeTitle": "Change sale type",
      "saleTypeHint": "Prices, charges and tax follow the new sale type.",
      "voidOrderTitle": "Void order {{number}}",
      "voidOrderHint": "Choose a reason. The order is kept with its history.",
      "voidLineTitle": "Void {{name}}",
      "voidLineHint": "This line was sent to the kitchen. Choose a reason.",
      "confirmVoid": "Void order",
      "guests_one": "{{count}} guest",
      "guests_other": "{{count}} guests",
      "cancel": "Cancel",
      "noLines": "Add items before sending.",
      "nothingNew": "Nothing new to send.",
      "paid": "This order is paid. Correct it the same day with Change payment method, Correct guests or Void and redo; later, refund it in the store admin portal.",
      "ticket": "Ticket · {{count}}",
      "menu": "Menu",
      "removeNote": "Remove note",
      "appliedDiscounts": "Applied discounts",
      "removeDiscount": "Remove {{name}}",
      "removeDiscountTitle": "Remove discount",
      "removeDiscountBody": "{{name}} is taken off the order.",
      "noteRemoved": "Note removed",
      "quantityPrefix": "Quantity {{count}}",
      "notApplicable": "Doesn't apply",
      "onOrder": "Already on the order",
      "presetOrderOnly": "Only for the whole order.",
      "presetLineOnly": "Only for a single item.",
      "adjustmentRemoved": "{{name}} was removed: {{reason}}",
      "chargeEnter": "Enter amount",
      "chargeValueTitle": "{{name}}: enter the amount",
      "chargeValueHint": "Type the amount for this order.",
      "chargeValueMax": "Type the amount for this order, up to {{max}}.",
      "chargeApply": "Add {{value}}",
      "chargeApplyEmpty": "Add charge",
      "presetEnterAmount": "Enter amount",
      "presetEnterPercent": "Enter percent",
      "discountValueTitle": "{{name}}: enter the discount",
      "discountValueHint": "Type the discount amount.",
      "discountValuePercentHint": "Type the discount percent.",
      "discountValueMax": "Type the discount amount, up to {{max}}.",
      "discountValuePercentMax": "Type the discount percent, up to {{max}}.",
      "discountApplyValue": "Apply {{value}}",
      "discountReasonTitle": "{{name}}: choose a reason",
      "discountReasonHint": "This discount needs a reason.",
      "discountReasonNone": "This discount needs a reason, but none is set up. Ask head office to add a discount reason.",
      "overpaid": "More was paid than this order now costs: refund {{amount}} before it can be completed.",
      "afterTax": "After VAT",
      "appliedCharges": "Applied charges",
      "addCharge": "Add a charge",
      "removeCharge": "Remove {{name}}",
      "removeChargeTitle": "Remove charge",
      "removeChargeBody": "{{name}} is taken off the order.",
      "discountSubtitle": "Apply a discount to this order",
      "lineDiscountSubtitle": "Apply a discount to this item",
      "discountAmount": "Discount amount",
      "presetsTitle": "Preset discounts",
      "presetsHint": "Tap to apply a preset discount",
      "manualTitle": "Or enter a discount manually",
      "manualHint": "Choose percent or amount, then enter a value",
      "presetCustom": "Custom",
      "chargeSubtitle": "Add a charge or fee to this order",
      "chargeAmount": "Charge amount",
      "chargeTypesTitle": "Charges",
      "chargeTypesHint": "Tap to add a charge",
      "chargeEntryNone": "A charge you enter an amount for opens its keypad here."
    },
    "guests": {
      "label": "Guests",
      "invalid": "Enter 1 to {{max}} guests.",
      "title": "Number of guests",
      "start": "Open table",
      "byType": "Guests by type",
      "total_one": "{{count}} guest",
      "total_other": "{{count}} guests"
    },
    "tables": {
      "table": "Table {{code}}",
      "status": {
        "available": "Free",
        "seated": "Seated",
        "ordered": "Ordered",
        "bill_printed": "Bill printed",
        "cleaning": "Cleaning",
        "reserved": "Reserved",
        "blocked": "Blocked"
      },
      "guests_one": "{{count}} guest",
      "guests_other": "{{count}} guests",
      "minutes_one": "{{count}} min",
      "minutes_other": "{{count}} min",
      "hoursMinutes": "{{hours}} h {{minutes}}",
      "search": "Find a table",
      "floorPlans": "Floor plans",
      "guestsFor": "How many guests at {{code}}?",
      "noPlans": "No floor plans for this section yet. Head office sets them up under Tables.",
      "noMatch": "No tables match. Choose All to see every table.",
      "loadFailed": "The table map couldn't be loaded. It retries in a few seconds.",
      "moves": {
        "split": "Seat another party",
        "splitGuests": "Another party at {{code}}: number of guests",
        "pickTransfer": "Choose a free table to move {{code}} to.",
        "pickMerge": "Choose the table to merge with {{code}}.",
        "notFree": "Not free",
        "thisParty": "This party",
        "confirmTitle": "Merge {{from}} into {{into}}?",
        "confirmBody": "{{from}}'s items join {{into}}'s bill and its guests are added. The two tables then have one order.",
        "transferred": "{{from}} moved to {{to}}.",
        "merged": "Tables merged: {{tables}}.",
        "chargesDropped": "Charges not carried over: {{names}}.",
        "choose": {
          "transfer": "Choose the table to move.",
          "merge": "Choose the table to merge another table into.",
          "split": "Choose the table to seat another party at.",
          "unmerge": "Choose the table to take out of its merged party."
        },
        "notInUse": "Not in use",
        "splitTitle": "Seat another party at {{code}}?",
        "splitBody": "The new party gets its own order; the table then shows a tile for each party.",
        "label": "Table moves",
        "unmerge": "Unmerge tables",
        "notMerged": "Not merged",
        "unmergeTitle": "Take {{code}} out of {{tables}}?",
        "unmergeBody": "{{code}} is free again. The party stays at {{rest}} with its order and guests; to give part of it its own bill, use Split bill.",
        "unmerged": "{{code}} is free again. The party sits at {{tables}}."
      },
      "toolbar": "Tables"
    },
    "header": {
      "label": "POS",
      "terminal": "POS {{code}} · {{name}}",
      "notifications": "Notifications",
      "noNotifications": "Nothing needs your attention.",
      "profile": "Your account",
      "lock": "Lock screen"
    },
    "bill": {
      "title": "Bill preview",
      "notice": "Preview, not printed.",
      "order": "Order {{number}}",
      "table": "Table {{code}}",
      "opened": "Opened {{time}}",
      "empty": "There are no items on this order.",
      "back": "Back to menu",
      "sentTo": "Bill sent to {{printer}}",
      "reprinted": "Bill reprinted (reprint {{number}}) on {{printer}}",
      "noPrinter": "No receipt printer is set up for this terminal. The bill is marked as printed, but nothing was printed.",
      "notPrinted": "The bill could not be sent to a printer. Check the print queue."
    },
    "flow": {
      "waiting": "{{action}} comes after: {{step}}.",
      "notSent_one": "{{count}} item not sent to the kitchen yet: send it first.",
      "notSent_other": "{{count}} items not sent to the kitchen yet: send them first.",
      "readyToPay": "Everything is sent. Ready to pay.",
      "action": {
        "take_payment": "Payment",
        "send_to_kitchen": "Sending to the kitchen",
        "take_order": "Taking the order",
        "accept_order": "Accepting",
        "mark_ready": "Marking ready",
        "out_for_delivery": "Handing to the driver",
        "hand_over": "Handing over",
        "deliver": "Marking delivered"
      },
      "step": {
        "take_order": "take the order",
        "send_to_kitchen": "send to the kitchen",
        "take_payment": "take payment",
        "select_customer": "Customer",
        "print_bill": "print the bill",
        "assign_driver": "assign a driver",
        "accept_order": "accept the online order",
        "mark_ready": "mark it ready",
        "out_for_delivery": "hand it to the driver",
        "hand_over": "hand it over",
        "deliver": "mark it delivered"
      },
      "printBill": "Print bill",
      "billPrinted": "Bill printed",
      "assignDriver": "Assign driver",
      "run": {
        "mark_ready": "Ready",
        "out_for_delivery": "Hand to driver",
        "hand_over": "Handed over",
        "served": "Served",
        "deliver": "Delivered"
      },
      "done": {
        "mark_ready": "Marked ready. The customer is told.",
        "out_for_delivery": "With the driver. The customer is told.",
        "hand_over": "Handed over. The order is complete.",
        "deliver": "Delivered. The order is complete."
      }
    },
    "form": {
      "required": "{{label}} (required)",
      "optional": "{{label}} (optional)",
      "requiredToSave": "Required before you can save."
    },
    "customer": {
      "title": "Customer",
      "required": "This order needs the customer before items are added.",
      "optional": "Optional. A name or phone makes the order easier to find.",
      "skip": "Skip",
      "name": "Name",
      "phone": "Phone",
      "street": "Address",
      "ward": "Ward",
      "province": "District / city",
      "note": "Note",
      "known": "Existing customer: {{name}}",
      "new": "New customer. They are saved for next time.",
      "blocked": "This customer is blocked.",
      "addresses": "Saved addresses",
      "search": "Search",
      "matches": "Matching customers",
      "email": "Email",
      "emailInvalid": "Enter an email like name@example.com.",
      "phoneOrEmail": "A phone or an email is needed."
    },
    "driver": {
      "title": "Assign driver",
      "hint": "Enter your own driver, or choose a shipping provider.",
      "name": "Driver name",
      "phone": "Driver phone",
      "own": "Own driver",
      "providers": "Shipping providers",
      "saved": "Our drivers",
      "add": "New driver",
      "edit": "Edit",
      "remove": "Remove from list",
      "vehicle": "Vehicle or plate",
      "saveDriver": "Save driver",
      "section": "Driver",
      "addDriver": "Add driver",
      "actions": "Actions for {{name}}",
      "removeTitle": "Remove driver",
      "recentFees": "Recent fees",
      "removeBody": "{{name}} is removed from the list of drivers. Deliveries already made keep their driver.",
      "loadFailed": "The drivers or recent fees couldn't be loaded."
    },
    "invoice": {
      "title": "VAT invoice",
      "hint": "Company details for a VAT e-invoice. Leave it cleared for a retail invoice.",
      "taxCode": "Tax code",
      "taxCodeInvalid": "Enter 10 digits, or 13 for a branch.",
      "companyName": "Company name",
      "address": "Company address",
      "email": "Email to send the invoice to",
      "emailInvalid": "Enter a valid email address.",
      "clear": "Clear",
      "tag": "VAT invoice"
    },
    "delivery": {
      "title": "Delivery",
      "chooseHint": "Choose how this order is delivered.",
      "ownDrivers": "Our own driver",
      "ownHint": "Choose who delivers this order and set the delivery fee.",
      "assign": "Assign driver",
      "offlineHint": "No connection to head office: you can still choose a provider and enter the fee and delivery number by hand.",
      "quoting": "Asking the provider for the fee…",
      "quote": "Fee {{fee}}",
      "eta": "about {{minutes}} min",
      "etaLabel": "Estimated time",
      "fixedFee": "Fee {{fee}}",
      "fee": "Delivery fee",
      "number": "Delivery order number",
      "numberHint": "The number the provider gave you.",
      "handHint": "Booked outside the system: enter the fee and, if there is one, the delivery order number.",
      "hoDown": "No connection to head office. Enter the fee by hand, or try again later.",
      "requestDriver": "Request driver",
      "byHand": "Enter by hand",
      "askHo": "Ask the provider",
      "entered": "Entered by hand",
      "refresh": "Refresh",
      "cancel": "Cancel delivery",
      "change": "Change service",
      "track": "Track the delivery",
      "history": "History",
      "noDriverHint": "No driver was found. Cancel, or choose another service.",
      "cancelPending": "Cancelling… waiting for the provider to confirm.",
      "status": {
        "pending": "New",
        "searching_driver": "Finding a driver",
        "no_driver": "No driver found",
        "assigned": "Driver assigned",
        "picked_up": "Picked up",
        "out_for_delivery": "On the way",
        "delivered": "Delivered",
        "failed": "Failed",
        "cancel_requested": "Cancelling",
        "cancelled": "Cancelled",
        "returned": "Returned"
      },
      "orderStatus": "Status",
      "providerStatus": "Provider status",
      "driverTitle": "Driver",
      "deliveryTitle": "Delivery",
      "driverPending": "Not assigned yet",
      "driverVehicle": "Vehicle",
      "trackingUrl": "Tracking link",
      "copyLink": "Open",
      "ownFeeHint": "Charged to the customer and added to the bill.",
      "ownTitle": "Assign your own driver",
      "updateStatus": "Update status",
      "updateHint": "The provider's updates still come in and move it further.",
      "failureReason": "Why it was not delivered",
      "confirmFailed": "Mark as failed"
    },
    "shift": {
      "title": "Shift",
      "none": "No shift is open.",
      "noneRequired": "No shift is open. Open one before taking payments.",
      "unscheduled": "Unscheduled shift",
      "window": "{{from}} – {{to}}",
      "openedBy": "Opened at {{time}} by {{name}}",
      "openedAt": "Opened at {{time}}",
      "due": "Due now: {{name}} ({{window}})",
      "next": "Next: {{name}} at {{time}}",
      "overdue": "{{name}} was due to end at {{time}}. Count the cash and close it.",
      "open": "Open shift",
      "openTitle": "Open {{name}}",
      "float": "Opening float",
      "floatTerminal": "Opening float, terminal {{code}}",
      "cashMove": "Cash in or out",
      "paidIn": "Paid in",
      "paidOut": "Paid out",
      "cashDrop": "Cash drop",
      "movementType": "Type",
      "amount": "Amount",
      "reason": "Reason",
      "reasonNone": "Other (write why)",
      "note": "Why",
      "record": "Record",
      "close": "Close shift",
      "closeTitle": "Count the cash and close {{name}}",
      "closeTitleNoCount": "Close {{name}}",
      "counted": "Counted cash",
      "countedTerminal": "Counted cash, terminal {{code}}",
      "store": "Store",
      "expected": "Expected",
      "variance": "Over or short",
      "over": "Over {{amount}}",
      "short": "Short {{amount}}",
      "even": "Even",
      "tolerance": "More than {{amount}} over or short needs a manager.",
      "closeNote": "Note",
      "noCount": "This store doesn't count cash at close.",
      "empty": "No orders or payments in this shift yet.",
      "forceClose": "Close without counting",
      "forceReason": "Why is it closed without a count?",
      "printReport": "Print shift report",
      "printed": "Shift report sent to {{printer}}.",
      "notPrinted": "No receipt printer is set up for this terminal or store.",
      "closed": "Shift closed. Expected {{expected}}, counted {{counted}}.",
      "closedNoCount": "Shift closed.",
      "approve": "Accept the cash count of {{name}}: {{variance}}",
      "cancel": "Cancel",
      "takings": "Taken by method",
      "movements": "Cash in and out",
      "reportTitle": "Shift report: {{name}}",
      "closedAt": "Closed at {{time}}",
      "done": "Done",
      "orders": "Orders by status",
      "status": "Status",
      "count": "Orders",
      "total": "Total",
      "allOrders": "All orders",
      "noOrders": "No orders in this shift.",
      "noTakings": "No payments taken in this shift.",
      "method": "Payment method",
      "payments": "Payments",
      "refunded": "Refunded",
      "net": "Amount",
      "openOrders_one": "{{count}} order is still open. Pay or void it, or it carries over to the next shift.",
      "openOrders_other": "{{count}} orders are still open. Pay or void them, or they carry over to the next shift.",
      "figures": "Guests {{guests}} · average check {{average}} · voids {{voids}} ({{voidAmount}})",
      "otherMethods": "Other methods",
      "otherMethodsHint": "Optional: enter what the card terminal's batch or the provider shows for this shift. A difference is kept on the report.",
      "methodShows": "{{method}}: shows",
      "methodTook": "Taken on the POS",
      "methodCounted": "Counted",
      "noSale": "Open drawer",
      "noSaleHint": "Opens the cash drawer without a sale. It is recorded with your name, the time and the reason, and counted on the shift report.",
      "noSaleOpen": "Open drawer",
      "noSaleApprove": "Open the cash drawer without a sale",
      "noSaleOpened": "Drawer opened. The no-sale is recorded.",
      "noSaleNoPrinter": "The no-sale is recorded, but no receipt printer is set up to open the drawer.",
      "noSales": "No-sale drawer opens: {{count}}",
      "noneAuto": "No shift is open. One opens with the next payment.",
      "handsOver": "{{name}} takes over at {{time}}.",
      "endedTitle": "Ended shifts to close",
      "endedAt": "Ended at {{time}}",
      "countAndClose": "Count and close",
      "countLater": "The next shift has used this drawer since. Count all the cash in it now; the till works out this shift's difference.",
      "blindCount": "Count the cash without looking at the figures; the difference shows once the shift is closed.",
      "approveBlind": "Accept the cash count of {{name}}",
      "endedNotice_one": "{{count}} ended shift is waiting for its cash count.",
      "endedNotice_other": "{{count}} ended shifts are waiting for their cash count."
    },
    "split": {
      "title": "Split bill {{number}}",
      "hint": "Mark items, then move them to a new bill whole or one at a time, or share them evenly. Each bill is paid on its own.",
      "thisBill": "This bill",
      "bill": "Bill {{number}}",
      "bills": "New bills",
      "addBill": "Add bill",
      "tools": "Split actions",
      "markAll": "Mark all",
      "move": "Move to {{bill}}",
      "moveOne": "Move 1 to {{bill}}",
      "moveBack": "Move back",
      "evenly": "Share evenly ({{count}} bills)",
      "allEvenly": "Split all evenly",
      "byAmount": "By amount",
      "reset": "Start again",
      "save_one": "Make {{count}} new bill",
      "save_other": "Make {{count}} new bills",
      "empty": "No items",
      "guestsTitle": "Split all evenly",
      "guestsHint": "How many guests? Each gets the same share of every item; this bill keeps any rounding.",
      "amountTitle": "Split by amount",
      "amountHint_one": "The amount the new bill should come to, less than {{total}}. It takes the same share of the marked item.",
      "amountHint_other": "The amount the new bill should come to, less than {{total}}. It takes the same share of every marked item, or of every item when none is marked.",
      "apply": "Apply",
      "billsTitle": "Bills at this table",
      "billsHint": "Open a bill to pay it.",
      "billNumber": "Bill {{number}}",
      "payNext": "Pay bill {{number}}",
      "tableBills": "Bills at table {{code}}"
    },
    "points": {
      "title": "Redeem points",
      "hint": "{{name}}'s points come off the order as a discount. Head office holds them until the order is paid.",
      "label": "Points to redeem",
      "apply": "Redeem",
      "appliedTitle": "Points on this order",
      "remove": "Take off {{name}}",
      "removeTitle": "Take the points off",
      "removeBody": "{{name}} comes off the order and the points go back to the member.",
      "applied": "{{name}}: {{amount}} off the order.",
      "removed": "The points are off the order and back with the member.",
      "needMember": "Find the member first: points are theirs."
    },
    "coupon": {
      "title": "Apply a code",
      "hint": "Type or scan the coupon code. Head office checks it and holds it for this order.",
      "lineHint": "A code for one item applies to {{name}}.",
      "label": "Code",
      "apply": "Apply code",
      "applied": "Code applied.",
      "appliedTitle": "Codes on this order",
      "remove": "Remove {{name}}",
      "removeTitle": "Remove code",
      "removeBody": "Take {{name}} off the order? The code can be used again.",
      "removed": "Code removed."
    },
    "member": {
      "title": "Member",
      "hint": "Find the member by phone or card number. Head office keeps members and their points.",
      "enrolHint": "Enrol the guest at head office. Ask before ticking marketing consent.",
      "query": "Phone or card number",
      "queryHelp": "Type or scan the card.",
      "search": "Search",
      "matches": "Members found",
      "use": "Use member",
      "enrol": "Enrol new member",
      "enrolSave": "Enrol",
      "backToSearch": "Back to search",
      "name": "Name",
      "phone": "Phone",
      "email": "Email (optional)",
      "cardNo": "Card number (optional)",
      "cardNoHelp": "For a pre-printed card. Leave empty and head office issues a number.",
      "consent": "The guest agrees to receive offers and news",
      "remove": "Remove member",
      "change": "Change member",
      "points": "{{points}} points to spend",
      "tag": "Member: {{name}}",
      "attached": "{{name}} is on the order.",
      "removed": "Member removed from the order."
    },
    "pdf": {
      "ready": {
        "bill": "Bill {{number}} is ready as PDF",
        "kitchen_ticket": "Kitchen ticket {{number}} is ready as PDF",
        "shift_report": "The shift report is ready as PDF",
        "day_report": "The day report is ready as PDF",
        "test_page": "The test page is ready as PDF",
        "other": "A document is ready as PDF",
        "receipt": "Receipt {{number}} is ready as PDF"
      },
      "save": "Save PDF",
      "failed": "The PDF couldn't be saved. Try again."
    },
    "online": {
      "title": "Requests from tables",
      "listLabel": "Table QR requests waiting",
      "empty": "No requests or calls from tables. New ones show here with a chime.",
      "loadFailed": "The requests couldn't be loaded. They retry in a few seconds.",
      "notAllowed": "Requests from tables need the permission to manage online orders. Ask a manager.",
      "unknownTable": "?",
      "unknownItem": "Item not on this store's menu",
      "unknownOption": "Option not on this store's menu",
      "waiting": "Waiting {{time}}",
      "justNow": "under 1 min",
      "hours": "{{hours}} h {{minutes}}",
      "guestNote": "Guest's note: {{note}}",
      "confirming": "Confirming",
      "confirmed": "Added to table {{table}}'s order. Open it to send it to the kitchen.",
      "reject": "Reject",
      "rejected": "The request from table {{table}} was rejected. The guest sees the reason.",
      "rejectTitle": "Reject the request from table {{table}}?",
      "reason": "Reason (the guest sees it)",
      "reasons": {
        "sold_out": "Sold out",
        "kitchen_busy": "Kitchen is busy",
        "wrong_table": "Wrong table",
        "duplicate": "Sent twice",
        "store_closed": "Store is closing",
        "other": "Other"
      },
      "note": "Note for the guest (optional)",
      "noteHelper": "For example, what to order instead.",
      "tableBadge_one": "{{count}} QR request waiting",
      "tableBadge_other": "{{count}} QR requests waiting",
      "waitingNotice_one": "{{count}} request from a table is waiting.",
      "waitingNotice_other": "{{count}} requests from tables are waiting.",
      "channels": {
        "label": "Online channels",
        "why": "Why stop online orders?",
        "pauseAll": "Pause online orders",
        "pause": "Pause",
        "resume": "Take online orders again",
        "unknown": "Online channels: head office can't be reached",
        "hoUnavailable": "Head office can't be reached, so online orders can't be paused or opened now. Requests already here can still be confirmed.",
        "reasons": {
          "staff_paused": "Paused by staff",
          "capacity": "Too busy right now"
        },
        "status": {
          "open": "{{channel}}: taking orders",
          "closed": "{{channel}}: closed",
          "staff_paused": "{{channel}}: paused by staff",
          "capacity": "{{channel}}: paused, too busy",
          "store_offline": "{{channel}}: paused, store offline",
          "outside_hours": "{{channel}}: paused, outside opening hours",
          "max_orders": "{{channel}}: too many open orders"
        },
        "someStopped": "Some online channels are paused",
        "allStopped": "Not taking online orders",
        "busy": "Busy",
        "busyHint": "Online orders take longer for a while: customers see the longer time and later slots.",
        "busyMinutes": "How much longer",
        "busyFor": "For how long",
        "plusMinutes_one": "+{{count}} min",
        "plusMinutes_other": "+{{count}} min",
        "forHours_one": "{{count}} hour",
        "forHours_other": "{{count}} hours",
        "busyStart": "Set busy",
        "busyEnd": "Back to normal",
        "busyUntil": "Busy: orders take {{minutes}} min longer until {{time}}."
      },
      "web": {
        "title": "Online orders",
        "listLabel": "Website and app orders waiting for an answer",
        "empty": "No website or app orders waiting. New ones show here with a chime.",
        "loadFailed": "Online orders couldn't be loaded. They retry in a few seconds.",
        "heading": "{{number}} · {{fulfilment}}",
        "fulfilment": {
          "delivery": "Delivery",
          "pickup": "Pickup",
          "dine_in": "Dine in"
        },
        "payment": {
          "cod": "cash on delivery",
          "pay_at_pickup": "pay at the counter",
          "online": "online payment"
        },
        "total": "{{total}}, {{payment}}",
        "note": "Customer's note: {{note}}",
        "answerWithin": "Answer within {{time}}",
        "answerBy": "Answer by {{time}}",
        "timeUp": "Time is up",
        "accept": "Accept",
        "acceptWith_one": "Accept, ready in {{count}} min",
        "acceptWith_other": "Accept, ready in {{count}} min",
        "acceptTitle": "Accept order {{number}}?",
        "prep": "Ready in (the customer is told)",
        "accepted": "Order {{number}} accepted. Send it to the kitchen when you are ready. The customer is told it's ready in {{minutes}} min.",
        "reject": "Reject",
        "rejectTitle": "Reject order {{number}}?",
        "reason": "Reason (the customer sees it)",
        "reasons": {
          "sold_out": "Sold out",
          "too_busy": "Too busy",
          "closing": "Store is closing",
          "out_of_area": "Can't deliver there",
          "other": "Other"
        },
        "rejectNote": "Note for the customer (optional)",
        "rejected": "Order {{number}} was rejected. The customer sees the reason.",
        "answered": "Done today",
        "status": {
          "accepted": "Accepted",
          "acceptedFor_one": "Accepted, {{count}} min",
          "acceptedFor_other": "Accepted, {{count}} min",
          "rejected": "Rejected",
          "cancelled": "Not answered in time",
          "ready": "Ready",
          "completed": "Handed over",
          "outForDelivery": "On its way"
        },
        "waitingNotice_one": "{{count}} online order is waiting for an answer.",
        "waitingNotice_other": "{{count}} online orders are waiting for an answer.",
        "customerLanguage": "Customer's language: {{language}}",
        "inProgress": "In progress",
        "ready": "Ready",
        "handedOver": "Handed over",
        "readyDone": "Order {{number}} is ready. The customer is told.",
        "handedOverDone": "Order {{number}} was handed over.",
        "handedToDriver": "Handed to driver",
        "handedToDriverDone": "Order {{number}} is on its way. The customer is told.",
        "delivered": "Delivered",
        "deliveredDone": "Order {{number}} was delivered.",
        "takePayment": "Take payment",
        "notPaid": "Not paid",
        "served": "Served",
        "servedDone": "Order {{number}} was served.",
        "paid": "Paid",
        "customerCancelled": "The customer cancelled this order before it was accepted. Anything paid online is refunded in full.",
        "refundAtCounter": "It was paid at the counter: refund the customer there.",
        "dueNotice_one": "{{count}} order for later is due to start: send it to the kitchen.",
        "dueNotice_other": "{{count}} orders for later are due to start: send them to the kitchen."
      },
      "list": {
        "online": "Online · {{channel}}",
        "waiting": "Accept within {{time}}",
        "timeUp": "Time to accept is up",
        "status": {
          "pending": "Waiting for you",
          "accepted": "Accepted",
          "ready": "Ready",
          "out_for_delivery": "With the driver",
          "completed": "Completed",
          "rejected": "Rejected",
          "cancelled": "Not answered",
          "customerCancelled": "Cancelled by the customer"
        },
        "columns": {
          "type": "Order type",
          "payment": "Payment",
          "prep": "Ready in"
        },
        "upcoming": "Upcoming",
        "scheduledOn": "For {{time}}",
        "scheduledFor": "For {{time}}",
        "startBy": "For {{time}} · start {{start}}"
      },
      "calls": {
        "kinds": {
          "staff": "Calls staff",
          "bill": "Asks for the bill"
        },
        "tableBadge_one": "{{count}} call from the table",
        "tableBadge_other": "{{count}} calls from the table",
        "waitingNotice_one": "{{count}} guest is calling from a table.",
        "waitingNotice_other": "{{count}} guests are calling from tables.",
        "doneShort": "Done"
      },
      "openOrder": "Open the order",
      "confirmShort": "Confirm",
      "request": "Request {{number}} · {{time}}",
      "qrRequests_one": "{{count}} request",
      "qrRequests_other": "{{count}} requests",
      "qrCalls_one": "{{count}} call",
      "qrCalls_other": "{{count}} calls"
    },
    "receipt": {
      "print": "Print receipt",
      "reprint": "Reprint receipt",
      "sentTo": "Receipt sent to {{printer}}",
      "reprinted": "Receipt reprinted (reprint {{number}}) on {{printer}}",
      "noPrinter": "No receipt printer is set up for this terminal. Nothing was printed.",
      "notPrinted": "The receipt could not be sent to a printer. Check the print queue."
    },
    "variants": {
      "choose": "Choose · {{price}}",
      "chooseNoPrice": "Choose",
      "notWithAbove": "Not with the choice above",
      "unavailable": "Sold out or not on sale"
    },
    "reservations": {
      "title": "Reservations",
      "intro": "Table bookings for the day: online ones to confirm, phone and walk-in bookings, and the guests to seat.",
      "previousDay": "Previous day",
      "nextDay": "Next day",
      "today": "Today",
      "day": "Day",
      "off": "This store doesn't take table bookings. Head office turns them on.",
      "newBooking": "New booking",
      "toConfirm": "To confirm",
      "bookings": "Bookings",
      "none": "No bookings this day.",
      "noneHint": "Take a phone or walk-in booking with New booking; online bookings show up here to confirm.",
      "slots": "Slots",
      "slotLoad_one": "{{time}} · {{count}} booking · {{guests}} guests",
      "slotLoad_other": "{{time}} · {{count}} bookings · {{guests}} guests",
      "full": "Full",
      "when_one": "{{time}} · {{count}} guest",
      "when_other": "{{time}} · {{count}} guests",
      "status": {
        "pending": "To confirm",
        "confirmed": "Confirmed",
        "declined": "Declined",
        "expired": "Not answered",
        "cancelled": "Cancelled",
        "no_show": "No-show",
        "seated": "Seated"
      },
      "source": {
        "website": "Website",
        "app": "App",
        "pos": "At the store"
      },
      "answerBy": "Answer by {{time}}",
      "dishes": "Chosen ahead: {{items}}",
      "confirm": "Confirm",
      "decline": "Decline",
      "edit": "Change",
      "seat": "Seat guests",
      "noShow": "No-show",
      "guestCancelled": "Guest cancelled",
      "save": "Save booking",
      "newTitle": "New booking",
      "editTitle": "Change booking: {{name}}",
      "confirmTitle": "Confirm booking {{reference}}",
      "declineTitle": "Decline booking {{reference}}",
      "seatTitle_one": "Seat {{name}}, {{count}} guest",
      "seatTitle_other": "Seat {{name}}, {{count}} guests",
      "date": "Date",
      "time": "Time",
      "party": "Guests",
      "name": "Name",
      "phone": "Phone",
      "note": "Note",
      "reason": "Reason",
      "reasons": {
        "full": "Fully booked",
        "closed": "Closed at that time",
        "other": "Other reason"
      },
      "table": "Free table",
      "seats_one": "{{count}} seat",
      "seats_other": "{{count}} seats",
      "noFreeTable": "No table is free right now.",
      "needsHo": "Confirming and declining online bookings needs head office. Try again when it is reachable.",
      "dayBookings_one": "{{count}} booking",
      "dayBookings_other": "{{count}} bookings",
      "dayGuests_one": "{{count}} guest",
      "dayGuests_other": "{{count}} guests",
      "finished": "Seated and ended",
      "noneLeft": "No more guests to come this day.",
      "view": "View",
      "viewDay": "Day",
      "viewMonth": "Month",
      "previousMonth": "Previous month",
      "nextMonth": "Next month",
      "more_one": "+{{count}} more",
      "more_other": "+{{count}} more",
      "calendarTruncated": "Too many bookings to show them all here. Open a day to see its full list."
    },
    "attendance": {
      "title": "Clock in or out",
      "punch": "Clock in or out",
      "backToLogin": "Back to log in",
      "clockedIn": "{{name}} clocked in at {{time}}.",
      "clockedOut": "{{name}} clocked out at {{time}}.",
      "openOrders_one": "{{count}} of your orders is still open: hand it over to a colleague.",
      "openOrders_other": "{{count}} of your orders are still open: hand them over to a colleague.",
      "since": "Clocked in since {{time}}.",
      "notIn": "You aren't clocked in.",
      "day": "Today: {{orders}} orders completed, {{sales}}.",
      "clockIn": "Clock in",
      "clockOut": "Clock out"
    },
    "customerDisplay": {
      "title": "Customer display",
      "welcome": "Welcome",
      "yourOrder": "Your order",
      "due": "Amount due",
      "scanToPay": "Scan to pay with {{provider}}",
      "qrLabel": "QR code to pay with {{provider}}",
      "thanks": "Thank you!",
      "paid": "Paid {{amount}}",
      "change": "Your change"
    },
    "functions": {
      "dayOpen": "Open",
      "dayNotOpen": "Not open",
      "daysToClose_one": "{{count}} day to close",
      "daysToClose_other": "{{count}} days to close",
      "shiftOpen": "Open",
      "shiftNone": "No shift",
      "shiftOverdue": "Overdue",
      "clockedIn": "Clocked in",
      "notClockedIn": "Not clocked in",
      "hoData": "Head office data",
      "thisDevice": "This device",
      "terminal": "Terminal {{code}} · {{name}}",
      "store": "Store server"
    },
    "correction": {
      "paymentTitle": "Change payment method",
      "paymentHint": "Choose the payment taken in the wrong method.",
      "noPayment": "No payment here can change method: one paid by QR, gift voucher or with a kept voucher excess is corrected by voiding and redoing the bill.",
      "methodTitle": "Change payment method",
      "methodHint": "{{method}} {{amount}}: choose how it was really paid.",
      "reference": "{{method}} reference",
      "guestsTitle": "Correct guests",
      "reasonHint": "Choose why the paid bill is corrected.",
      "voidRedoTitle": "Void and redo bill {{number}}",
      "voidRedoHint": "Every payment is refunded to its method and the bill voided; the same items open as a new bill to fix and pay. Nothing goes to the kitchen again.",
      "noReasons": "Head office has set up no correction reasons yet.",
      "done": "Bill corrected. Reprint the receipt if the customer needs it.",
      "redoFailed": "The bill was voided, but it couldn’t be rung again. Start a new order."
    }
  },
  "tableSetup": {
    "title": "Quick table setup",
    "hint": "Choose a store and how many areas and tables it has. The tables are filled in for you: rename them, change seats, add or remove tables, then save everything at once. Each area gets its own floor plan.",
    "store": "Store",
    "chooseStore": "Choose a store",
    "existing": "This store already has {{areas}} areas and {{tables}} tables. New codes skip the ones in use.",
    "areas": "Number of areas",
    "tablesPerArea": "Tables in each area",
    "seats": "Seats per table",
    "generate": "Generate tables",
    "generateAgain": "Generate again (replaces the draft)",
    "summary": "Draft: {{areas}} areas, {{tables}} tables. Nothing is saved yet.",
    "areaName": "Area {{n}}",
    "areaNameLabel": "Area name",
    "areaCode": "Area code",
    "tableCount": "Tables",
    "renumber": "Renumber",
    "removeArea": "Remove area {{name}}",
    "tablesOf": "Tables in {{name}}",
    "tableCode": "Table",
    "seatsShort": "Seats",
    "shape": "Shape",
    "shapes": {
      "square": "Square",
      "rect": "Rectangle",
      "round": "Round"
    },
    "removeTable": "Remove table {{code}}",
    "addTable": "Add table",
    "addArea": "Add area",
    "discard": "Discard draft",
    "save": "Save {{areas}} areas and {{tables}} tables",
    "fix_one": "Fix {{count}} problem before saving.",
    "fix_other": "Fix {{count}} problems before saving.",
    "saved": "Created {{areas}} areas and {{tables}} tables for {{store}}. They reach the store with the next sync.",
    "viewAreas": "View table areas",
    "problems": {
      "required": "Required",
      "format": "Letters, numbers, . _ - only",
      "duplicate": "Used twice",
      "taken": "Already in this store",
      "capacity": "1 to 100"
    }
  },
  "posSync": {
    "emptyTitle": "No stores to show",
    "empty": "Stores appear here once they exist at head office.",
    "title": "Sync to stores",
    "tab": "Sync to stores",
    "hint": "Changes to this profile, its buttons, sale types and layouts reach a store only when you sync it there. A synced store shows Up to date once it has applied the profile; Waiting for the store means it hasn't pulled it yet.",
    "changedAt": "Last changed {{time}}.",
    "columns": {
      "store": "Store",
      "terminals": "Terminals using it",
      "status": "Status",
      "synced": "Last synced"
    },
    "status": {
      "current": "Up to date",
      "outdated": "Changed since",
      "never": "Never synced",
      "pending": "Waiting for the store"
    },
    "usedBy": "Used by terminals",
    "usedYes": "Used",
    "usedNo": "Not used",
    "selected_one": "{{count}} store selected",
    "selected_other": "{{count}} stores selected",
    "sync_one": "Sync to {{count}} store",
    "sync_other": "Sync to {{count}} stores",
    "confirmTitle_one": "Sync {{profile}} to {{count}} store?",
    "confirmTitle_other": "Sync {{profile}} to {{count}} stores?",
    "confirmBody": "These stores' POS use the profile and its layouts as they are now, from their next sync, usually within a minute. Open orders aren't affected.",
    "confirm": "Sync now",
    "done_one": "Synced to {{count}} store. It applies the profile with its next sync.",
    "done_other": "Synced to {{count}} stores. They apply the profile with their next sync.",
    "syncGroup": "Sync to a store group",
    "groupEmpty": "No store of {{group}} can take this profile. Add stores to the group, or check the profile's brand."
  },
  "posSettings": {
    "invalid": "These settings aren't valid JSON, so they can't be shown as switches. Ask support to correct them.",
    "switches": {
      "allowTableSplit": "Seat two parties at one table",
      "invoiceQr": "QR on receipts for a VAT invoice",
      "printDeliveryAddress": "Delivery address on receipts"
    },
    "help": {
      "invoiceQr": "Receipts of orders without VAT details print a QR code. The customer scans it to enter their company's details on the brand website and get a VAT invoice. Needs the brand's website and an e-invoice profile.",
      "printDeliveryAddress": "Receipts of delivery orders print where the order goes and the customer's note, for the driver. The phone stays masked. Leave it off where the market's privacy rules don't allow addresses on paper."
    },
    "receiptPrint": {
      "label": "Print the receipt",
      "auto": "When the order is paid",
      "ask": "Ask on the completed screen",
      "off": "Only when asked (Reprint receipt)"
    },
    "accent": {
      "label": "POS accent colour",
      "help": "Send and selected items on this profile's terminals. Empty: the brand's main colour. A colour too light for white text is darkened.",
      "invalid": "Write a colour as #RRGGBB, e.g. #1F7A4D.",
      "useBrand": "Use the brand's colour",
      "pick": "Pick the POS accent colour"
    }
  },
  "logs": {
    "headOffice": "Head office",
    "payload": "Content",
    "copy": "Copy",
    "copied": "Copied",
    "showMore": "Show more",
    "showLess": "Show less",
    "selected_one": "{{count}} selected",
    "selected_other": "{{count}} selected",
    "columns": {
      "time": "Time",
      "store": "Store",
      "staff": "Staff",
      "action": "Action",
      "target": "Record",
      "terminal": "Terminal",
      "approval": "Manager approval",
      "received": "Received",
      "processed": "Processed",
      "nextAttempt": "Next attempt",
      "messageType": "Message",
      "table": "Table",
      "record": "Record",
      "status": "Status",
      "attempts": "Attempts",
      "error": "Last error",
      "message": "Message id",
      "sequence": "#",
      "sentTo": "Sent to",
      "resolution": "Resolution",
      "versions": "Versions",
      "profile": "POS profile",
      "by": "Synced by",
      "rows": "Rows"
    },
    "audit": {
      "title": "Audit log",
      "hint": "Who did what, where and when, at every store and at head office: logins, voids, refunds, discounts and manager approvals. Records can't be changed.",
      "actions": {
        "identity_login": "Login",
        "identity_login_failed": "Login failed",
        "identity_logout": "Logout",
        "identity_approval_granted": "Manager approval given",
        "identity_approval_refused": "Manager approval refused",
        "identity_approval_consumed": "Manager approval used",
        "identity_ho_login": "Head office login",
        "identity_ho_login_failed": "Head office login failed",
        "identity_ho_logout": "Head office logout"
      }
    },
    "sync": {
      "title": "Sync logs",
      "hint": "What went between head office and the stores. Open a row to see what it carried.",
      "received": "Received from stores",
      "sent": "Sent to stores",
      "conflicts": "Conflicts",
      "publications": "POS profile syncs",
      "statuses": {
        "received": "Queued",
        "processing": "Processing",
        "processed": "Applied",
        "failed": "Failed, retrying",
        "dead": "Stopped"
      },
      "scopes": {
        "tenant": "All stores",
        "brand": "The brand's stores",
        "store": "One store"
      },
      "resolutions": {
        "ignored_older": "Older than head office's copy",
        "rejected_wrong_tenant": "Refused: another tenant",
        "rejected_wrong_store": "Refused: another store's row",
        "rejected_wrong_id": "Refused: id mismatch",
        "rejected_not_store_data": "Refused: head office data"
      },
      "versions": "Sent v{{incoming}} · HO v{{current}}",
      "retry_one": "Retry {{count}} message",
      "retry_other": "Retry {{count}} messages",
      "retried_one": "{{count}} message queued again.",
      "retried_other": "{{count}} messages queued again."
    },
    "integration": {
      "title": "Integration logs",
      "hint": "Every call to a delivery, payment, messaging or e-invoice provider: what was asked, how it ended and how long it took. Search by our order number or the provider's own id; open a call to see its exchanges and everything else that happened to the same booking, payment, message or invoice.",
      "any": "Any",
      "ms": "{{ms}} ms",
      "trace": "Trace id",
      "columns": {
        "at": "Time",
        "capability": "Area",
        "provider": "Provider",
        "operation": "Call",
        "outcome": "Outcome",
        "duration": "Time taken"
      },
      "outcomes": {
        "ok": "Worked",
        "unavailable": "Provider unavailable (retried)",
        "uncertain": "No answer (checked before retrying)",
        "refused": "Refused",
        "account_rejected": "Account refused",
        "rejected": "Webhook refused",
        "error": "Error"
      },
      "capabilities": {
        "shipping": "Delivery",
        "payment": "Payments",
        "messaging": "Customer messages",
        "einvoice": "E-invoices"
      }
    }
  },
  "optionItem": {
    "button": "New option item",
    "title": "Create an option item",
    "hint": "An option is an item of the type “Only as an option”. Name it, then price it in a price list like any item, or give it a base price; with neither it is free.",
    "hintGroup": "The item is created and added to this option group. Price it in a price list like any item, or give it a base price; with neither it is free.",
    "kind": "Kind of option",
    "kindText": "Text only, free (e.g. “Less onion”)",
    "kindItem": "Priced, or with its own details",
    "textHint": "Type what the option says. It is free and takes the default tax category and unit; its code is made from its name.",
    "textHintGroup": "Type what the option says. It is free, added to this option group, and takes the default tax category and unit; its code is made from its name.",
    "name": "What it says",
    "namePlaceholder": "Ít hành",
    "nameEn": "In English (optional)",
    "nameRequired": "Type what the option says."
  },
  "catalog": {
    "shared": {
      "everyBrand": "Every brand",
      "brandHelp": "Leave empty to share it with every brand: every brand's menus can use it and every store gets it. A row for every brand can use only rows for every brand.",
      "share": "Share with every brand",
      "shareTitle": "Share {{name}} with every brand?",
      "shareBody": "Every brand's menus will be able to use it, and every store will receive it. This can't be undone: it won't go back to one brand.",
      "shareConfirm": "Share"
    },
    "item": {
      "basePrice": "Base price",
      "basePriceHelp": "The price of one base unit. Used when no price list has a price for the item and unit; other units follow it by their size.",
      "baseUnitHelp": "The unit the base price refers to. It becomes the item's base selling unit and cannot change once other units or prices use it."
    },
    "unit": {
      "factorHelp": "How many base units are in one of this unit, for example 6 for a tray of 6 cans. Must be more than 0.",
      "defaultHelp": "The unit a menu tile or scan sells when it names none. Only one unit can be the default.",
      "baseLocked": "The base unit follows the item and cannot be changed here.",
      "optionUnitHelp": "The unit this option adds, one of the item's selling units, for example a double portion. Leave it empty for the item's base unit. Its price follows that unit.",
      "choiceUnitHelp": "The unit the deal includes, one of the item's selling units, for example a 6-pack. Leave it empty for the item's base unit. The quantity counts in this unit."
    },
    "deal": {
      "taxHelp": "The VAT of every item in the deal, for a deal whose items are in different tax categories. Leave it empty to tax each item at its own VAT."
    },
    "cost": {
      "storeHelp": "Empty: the cost for the whole brand. A store's own cost wins at that store.",
      "itemHelp": "What one base unit costs. Without a cost, the item is costed from its recipe.",
      "dealHelp": "What one deal costs. On an order it is shared among the deal's items; without one, each item keeps its own cost.",
      "storeRecipeHelp": "A store's recipe replaces the item's recipe at that store, for its cost."
    }
  },
  "priceTable": {
    "emptyTitle": {
      "item": "No items priced",
      "deal": "No deals priced"
    },
    "title": "Prices",
    "group": "Group",
    "state": "Change",
    "groups": {
      "item": "Items",
      "deal": "Deals"
    },
    "add": {
      "item": "Add items and units",
      "deal": "Add deals"
    },
    "empty": {
      "item": "No items priced in this list yet. An item with no price here sells at its base price.",
      "deal": "No deals priced in this list yet."
    },
    "states": {
      "new": "New",
      "changed": "Changed",
      "removed": "Removed",
      "missing": "Needs a price"
    },
    "columns": {
      "code": "Code",
      "name": "Name",
      "unit": "Unit",
      "vat": "VAT",
      "price": "Price ({{currency}})",
      "from": "From",
      "to": "Until"
    },
    "taxIncluded": "Prices in {{currency}}, tax included.",
    "taxExcluded": "Prices in {{currency}}, before tax.",
    "otherEntries_one": "{{count}} deal choice upcharge isn't shown here and stays as it is.",
    "otherEntries_other": "{{count}} deal choice upcharges aren't shown here and stay as they are.",
    "unitHint": "A price is for one unit of an item. A unit with no price here uses the base unit's price times its size, else the item's base price.",
    "baseUnit": "Base unit",
    "unitSize": "{{factor}} base units",
    "unitsOf": "Units of {{name}}",
    "search": "Search code, name or unit",
    "searchCatalog": "Search by code, name or group",
    "selectShown": "Select all {{count}} shown",
    "chosen_one": "{{count}} chosen",
    "chosen_other": "{{count}} chosen",
    "inList": "In the list",
    "inactive": "Inactive",
    "showMore": "Show more ({{shown}} of {{total}})",
    "priceForAll": "Price for all",
    "priceForAllHelp": "Leave empty to enter prices in the table.",
    "addChosen_one": "Add {{count}}",
    "addChosen_other": "Add {{count}}",
    "priceOf": "Price of {{name}}",
    "unitOf": "Unit of {{name}}",
    "vatOf": "VAT of {{name}} on this price list",
    "itemVat": "Item's VAT ({{name}})",
    "itemVatUnknown": "Item's VAT",
    "dealVat": "Deal's VAT ({{name}})",
    "dealItemsVat": "Each item's VAT",
    "vatHint": "VAT is set for each item or deal on this price list and applies to all its units. Leave it on the item's or deal's VAT to use the category set there. A deal's VAT applies to every item in it.",
    "fromOf": "{{name}} from",
    "toOf": "{{name}} until",
    "remove": "Remove {{name}}",
    "undoRemove": "Keep {{name}}",
    "problems": {
      "price": "Enter a price",
      "from": "Enter a start date",
      "dates": "Must be after the start",
      "duplicate": "Two prices start this day"
    },
    "unsaved_one": "{{count}} unsaved change",
    "unsaved_other": "{{count}} unsaved changes",
    "fix_one": "Fix {{count}} row before saving.",
    "fix_other": "Fix {{count}} rows before saving.",
    "conflict": "Someone else changed these prices meanwhile. Reload to see their changes, then make yours again.",
    "discard": "Discard changes",
    "reload": "Reload",
    "save_one": "Save {{count}} change",
    "save_other": "Save {{count}} changes",
    "saved_one": "{{count}} change saved. Stores get the new prices with their next sync.",
    "saved_other": "{{count}} changes saved. Stores get the new prices with their next sync."
  },
  "priceMenus": {
    "unlink": "Unlink",
    "emptyTitle": "No menus linked",
    "title": "Menus",
    "add": "Add menus",
    "search": "Search by code or name",
    "hintLinked": "This price list prices only these menus. Other menus use their own price lists.",
    "hintNone": "Linked to no menu: this price list can price any menu that has no price list of its own.",
    "remove": "Unlink {{name}}",
    "link_one": "Link {{count}} menu",
    "link_other": "Link {{count}} menus"
  },
  "menuEditor": {
    "noPagesTitle": "No pages yet",
    "noTilesTitle": "Nothing on this page yet",
    "noLayoutTitle": "No layout yet",
    "hint": "Items and deals on sale go to stores with the next sync. Pages and tiles reach stores when the POS profiles that use this layout are synced. An item has one tile for each unit it sells; a tile with no unit sells the item's default unit.",
    "layout": "Menu layout",
    "gridSize": "{{rows}} rows × {{cols}} columns",
    "saveFirst": "Save or discard your changes before switching layout.",
    "noLayout": "This menu has no layout yet. Create one to arrange its pages.",
    "createLayout": "Create layout",
    "gridRows": "Rows",
    "gridCols": "Columns",
    "addPages": "Add pages",
    "addPagesCount_one": "Add {{count}} page",
    "addPagesCount_other": "Add {{count}} pages",
    "pageNames": "Page names",
    "pageNamesHelp": "One page per line, for example Phở, Cơm, Đồ uống.",
    "pageName": "Page name",
    "palette": "Colour",
    "samePalette": "Same as page",
    "unnamed": "Page without a name",
    "noPages": "No pages yet. Add pages to arrange the menu on the POS.",
    "noTiles": "No items or deals on this page yet.",
    "offPage": "On the menu, not on a page",
    "offPageHint": "Staff find these by search only. Removing one takes it off the menu.",
    "search": "Search by code or name",
    "already": "Already added",
    "defaultUnit": "Default unit",
    "onMenu": "On the menu",
    "kinds": {
      "item": "Item",
      "deal": "Deal",
      "page": "Page link",
      "group": "Item group"
    },
    "add": {
      "item": "Add items and units",
      "deal": "Add deals",
      "group": "Add item groups"
    },
    "addTo": {
      "item": "Add items to {{page}}",
      "deal": "Add deals to {{page}}",
      "group": "Add item groups to {{page}}"
    },
    "columns": {
      "label": "Label on the tile",
      "row": "Row",
      "col": "Column",
      "visible": "Shown",
      "sort": "Order"
    },
    "labelOf": "Label for {{name}}",
    "paletteOf": "Colour of {{name}}",
    "rowOf": "Row of {{name}}",
    "colOf": "Column of {{name}}",
    "visibleOf": "Show {{name}}",
    "sortOf": "Order of {{name}}",
    "shown": "Shown",
    "hidden": "Hidden",
    "noRoom_one": "{{count}} didn't fit on the page. Remove tiles or make the grid bigger, then add it again.",
    "noRoom_other": "{{count}} didn't fit on the page. Remove tiles or make the grid bigger, then add them again.",
    "problems": {
      "name": "Enter a page name",
      "outside": "Outside the grid",
      "overlap": "Two tiles share this cell"
    },
    "fix_one": "Fix {{count}} problem before saving.",
    "fix_other": "Fix {{count}} problems before saving.",
    "conflict": "Someone else changed this menu meanwhile. Reload to see their changes, then make yours again.",
    "saved_one": "{{count}} change saved.",
    "saved_other": "{{count}} changes saved.",
    "groupButton": "Opens the variants of {{group}}",
    "groupButtonOff": "Sells this item",
    "groupButtonOn": "Opens the variants",
    "variants_one": "{{count}} variant",
    "variants_other": "{{count}} variants",
    "addSubPage": "Add sub-page",
    "addSubPageTo": "Add a sub-page to {{parent}}",
    "addSubPageHelp": "A tile that opens it is added to {{parent}} when you save.",
    "subPageOf": "Under {{parent}}",
    "categoryTab": "Category tab",
    "move": "Move to…",
    "moveTitle": "Move {{page}}",
    "moveUnder": "Put it under",
    "moveHelp": "Under another page, it gets a tile there that opens it, and its old tile goes. As a category tab, it shows in the POS category bar.",
    "linkMissingTitle": "The POS can’t open this page",
    "linkMissingBody": "No tile on {{parent}} opens it yet.",
    "addLink": "Add its tile on {{parent}}",
    "linkOnSave": "A tile that opens this page is added to {{parent}} when you save.",
    "pageTileHint": "Opens a sub-page. To change it, move or remove that page.",
    "icon": "Icon on the POS",
    "iconHelp": "Shown beside the tab in the POS section rail.",
    "noIcon": "None (its colour)",
    "icons": {
      "food": "Food",
      "pizza": "Pizza",
      "soup": "Soup",
      "salad": "Salad",
      "sandwich": "Sandwich",
      "noodles": "Noodles",
      "rice": "Rice",
      "meat": "Meat",
      "fish": "Fish and seafood",
      "chicken": "Chicken",
      "vegetarian": "Vegetarian",
      "kids": "Kids",
      "drink": "Drinks",
      "coffee": "Coffee",
      "tea": "Tea",
      "juice": "Juice and smoothies",
      "soft_drink": "Soft drinks",
      "beer": "Beer",
      "wine": "Wine",
      "cocktail": "Cocktails",
      "dessert": "Dessert",
      "cake": "Cakes",
      "ice_cream": "Ice cream",
      "combo": "Combos",
      "promotion": "Promotions",
      "retail": "Retail",
      "gift": "Gifts",
      "star": "Featured"
    }
  },
  "posButtons": {
    "reset": {
      "button": "Reset to default",
      "title": "Reset buttons to default?",
      "body": "All buttons of {{name}} are replaced with the default set: every button the POS shows. Your own labels, icons and shortcuts are removed. Stores get the change when you sync the profile.",
      "confirm": "Reset buttons",
      "done_one": "Reset: {{count}} default button.",
      "done_other": "Reset: {{count}} default buttons."
    }
  },
  "flows": {
    "steps": "Steps",
    "emptyTitle": "No steps yet",
    "columns": {
      "number": "No.",
      "step": "Step",
      "required": "Required"
    },
    "moveUpOf": "Move {{step}} up",
    "moveDownOf": "Move {{step}} down",
    "removeOf": "Remove {{step}}",
    "hint": "The steps a sale type follows, in order. A required step must be done before the actions after it; an optional one never holds anything up. Changes are saved together.",
    "stepNumber": "Step {{number}}",
    "required": "Required",
    "optional": "Optional",
    "requiredOf": "{{step}} is required",
    "moveUp": "Move up",
    "moveDown": "Move down",
    "remove": "Remove step",
    "add": "Add step",
    "addPlaceholder": "Choose a step to add",
    "empty": "This flow has no steps yet. Add one, or start from a preset.",
    "unsaved_one": "{{count}} unsaved change",
    "unsaved_other": "{{count}} unsaved changes",
    "save_one": "Save {{count}} change",
    "save_other": "Save {{count}} changes",
    "discard": "Discard",
    "conflict": "Someone else changed this flow. Reload to see their changes.",
    "problems": {
      "repeated": "A step can appear once in a flow; only a prompt can repeat.",
      "empty": "A flow needs at least one step.",
      "acceptFirst": "Accepting an online order must be the first step, and required."
    },
    "preset": {
      "label": "Start from a preset",
      "placeholder": "Replace the steps with a preset…",
      "name": "{{en}}"
    },
    "fromPreset": {
      "button": "Create from preset",
      "title": "Create an order flow from a preset",
      "body": "The new flow starts with the preset’s steps. You can add, remove, reorder and change them afterwards.",
      "create": "Create",
      "code": "Code",
      "name": "Name",
      "placeholder": "Choose a preset…",
      "preset": "Preset"
    },
    "step": {
      "select_table": "Select table",
      "enter_guests": "Enter guests",
      "select_customer": "Select customer",
      "prompt": "Prompt",
      "take_order": "Take order",
      "send_to_kitchen": "Send to kitchen",
      "print_bill": "Print bill",
      "take_payment": "Take payment",
      "assign_driver": "Assign driver",
      "close": "Close",
      "accept_order": "Accept online order",
      "mark_ready": "Mark ready",
      "out_for_delivery": "Hand to driver",
      "hand_over": "Hand over or serve",
      "deliver": "Delivered"
    }
  },
  "delivery": {
    "providers": {
      "title": "Delivery providers",
      "intro": "The accounts HO uses to book deliveries with a provider. Credentials are entered here, kept encrypted and never shown again. Stores never receive them.",
      "add": "Add provider account",
      "empty": "No provider accounts yet. Add one to offer a provider’s delivery service.",
      "columns": {
        "name": "Account",
        "provider": "Provider",
        "environment": "Environment",
        "credentials": "Credentials",
        "status": "Status"
      },
      "env": {
        "sandbox": "Sandbox",
        "production": "Production"
      },
      "credentialsSet": "Set",
      "credentialsMissing": "Missing",
      "active": "Active",
      "inactive": "Inactive",
      "deleteTitle": "Delete provider account",
      "deleteBody": "Delete “{{name}}”? Delivery services that use it can no longer book.",
      "dialog": {
        "provider": "Provider",
        "name": "Name",
        "environment": "Environment",
        "active": "Active",
        "credentials": "Credentials",
        "credentialsHint": "Never shown again. Leave a field empty to keep what is saved.",
        "saved": "saved",
        "replace": "Replace",
        "test": "Test connection",
        "testOk": "The connection works.",
        "testFailed": "The connection failed ({{code}}).",
        "saveFirst": "Save the account before testing the connection."
      }
    },
    "services": {
      "title": "Delivery services",
      "intro": "What the cashier can choose for a delivery: the store’s own drivers, or a provider’s service booked through head office. A service is offered only where an enabled row below says so.",
      "add": "Add delivery service",
      "empty": "No delivery services yet. The store’s own drivers are always offered until you add one.",
      "columns": {
        "code": "Code",
        "name": "Name",
        "kind": "Kind",
        "provider": "Provider",
        "fee": "Fee",
        "status": "Status"
      },
      "kinds": {
        "internal": "Own drivers",
        "external": "Provider"
      },
      "feeModes": {
        "manual": "Cashier enters it",
        "fixed": "Fixed amount",
        "quote": "Quoted by the provider"
      },
      "account": "Provider account",
      "serviceType": "Provider service",
      "fixedFee": "Fixed fee",
      "sortOrder": "Order in the list",
      "noAccounts": "Add a provider account first.",
      "created": "Service saved. Choose below where it is offered, then close.",
      "deleteTitle": "Delete delivery service",
      "deleteBody": "Delete “{{name}}”? It is no longer offered at any store.",
      "scope": {
        "any": "Any",
        "everywhere": "Everywhere",
        "unknown": "Unknown ({{id}})",
        "brand": "Brand",
        "store": "Store",
        "profile": "POS profile",
        "saleType": "Sale type"
      },
      "assignments": {
        "title": "Where it is offered",
        "hint": "Offered only where an enabled row matches. The most specific wins: POS profile, then store, then brand, then everywhere. Leave a field on Any to cover all.",
        "add": "Add",
        "none": "Not offered anywhere yet.",
        "deleteTitle": "Remove this place",
        "deleteBody": "Remove “{{scope}}”? The service is no longer offered there.",
        "on": "On",
        "off": "Off"
      },
      "profiles": {
        "title": "Integration profiles",
        "hint": "Which account books this service for a brand or a store, with its own service type and settings. A store's own profile comes first, then its brand's, then the one for everyone; without one the service's account is used.",
        "none": "No profiles: every store books with the service's account.",
        "add": "Add profile",
        "save": "Save profile",
        "editing": "Changing “{{name}}”",
        "name": "Profile name",
        "scope": "For",
        "account": "Account",
        "noAccount": "Add an account of this provider first.",
        "accountGone": "Account removed",
        "serviceType": "Service type",
        "serviceTypeHelp": "Empty: the service's. You can type the provider's own code (e.g. HAN-BIKE).",
        "settingsHint": "Settings this profile uses instead of the account's. Leave a field empty to keep the account's value.",
        "accountValue": "Account's value",
        "deleteTitle": "Remove this profile?",
        "deleteBody": "Remove “{{name}}”? Its brand or store books with the next profile that applies. Deliveries already booked aren't affected."
      }
    }
  },
  "integration": {
    "setting": {
      "scenario": "Scenario",
      "stepSeconds": "Seconds per step",
      "webhookSecret": "Webhook secret",
      "baseUrl": "API address",
      "lookupUrl": "Lookup page for buyers",
      "invoiceDate": "Invoice date",
      "taxCode": "Tax code to sign in with",
      "signOnIssue": "Sign when issued (signing certificate held by the provider)",
      "username": "User name",
      "password": "Password",
      "payPageUrl": "Pay page (simulator)",
      "paymentUrl": "Payment page address",
      "apiUrl": "API address",
      "serverIp": "Server IP address",
      "vnpayTmnCode": "Terminal code (vnp_TmnCode)",
      "vnpayHashSecret": "Hash secret",
      "momoPartnerCode": "Partner code",
      "momoRequestType": "Request type",
      "momoStoreId": "MoMo store id",
      "momoAccessKey": "Access key",
      "momoSecretKey": "Secret key",
      "zalopayAppId": "App id",
      "zalopayAppUser": "App user",
      "zalopayKey1": "MAC key (key 1)",
      "zalopayKey2": "Callback key (key 2)",
      "brandname": "Brandname (SMS sender name)",
      "smsUnicode": "Diacritics: auto (Unicode SMS when the text has them) or never (sent without)",
      "zaloOaId": "Zalo OA id",
      "accessToken": "Access token",
      "gaponeClientId": "Client id",
      "gaponeClientSecret": "Client secret",
      "gaponeApiUrl": "Management API address",
      "vietguysPasscode": "Passcode (API token)",
      "zaloAppId": "Zalo app id",
      "zaloDevelopmentMode": "Development mode (only the OA's admins receive messages)",
      "zaloOauthUrl": "OAuth address",
      "zaloSecretKey": "App secret key",
      "zaloRefreshToken": "Refresh token",
      "clientId": "Client id",
      "tokenUrl": "Token address",
      "grabPaymentMethod": "Who pays the fee: CASHLESS (the merchant's Grab account) or CASH",
      "grabCityCode": "City code (optional, from Grab's list)",
      "searchTimeoutMinutes": "Minutes to find a driver before giving up",
      "clientSecret": "Client secret",
      "ahamoveMobile": "Phone of the Ahamove account",
      "ahamovePaymentMethod": "Who pays the fee: BALANCE (the account's wallet), CASH or CASH_BY_RECIPIENT",
      "apiKey": "API key",
      "xanhSmEndpoint": "API address with the partner id (…/gsm-partner-booking/{partnerId})",
      "xanhSmSenderName": "Sender name",
      "xanhSmSenderPhone": "Sender phone",
      "xanhSmPaymentMethod": "Who pays the fee: partner_pay (billed to the merchant) or cash",
      "xanhSmCodAddonId": "Cash on delivery add-on id (from Xanh SM)",
      "xanhSmPackageSize": "Package size",
      "xanhSmPackageWeight": "Package weight (kg)",
      "xanhSmRefreshToken": "Application refresh token",
      "smtpHost": "SMTP host",
      "smtpPort": "Port",
      "smtpSecurity": "Security: starttls (usually port 587), ssl (port 465) or none (a local relay only)",
      "fromAddress": "From address",
      "fromName": "From name",
      "maxPerSecond": "Messages per second (per worker)",
      "maxParallel": "Messages in flight at once (per worker)"
    }
  },
  "rolePermissions": {
    "search": "Search permissions",
    "count": "{{granted}} of {{total}} on",
    "none": "No permission matches.",
    "on": "On",
    "off": "Off",
    "allPermissions": "All permissions",
    "wholeArea": "All of {{area}}, including screens added later",
    "included": "Included",
    "feature": "Feature",
    "code": "Permission key",
    "read": "Read",
    "manage": "Manage",
    "allowed": "Allowed",
    "area": {
      "reporting": "Reports"
    },
    "wholeKey": "Everything under {{code}}, including screens added later",
    "sensitive": "Sensitive",
    "needs": "Also gives {{codes}}, which it needs",
    "dataKind": {
      "tenant": "Company",
      "brand": "Brand",
      "store": "Store"
    }
  },
  "address": {
    "country": "Country",
    "choose": "Choose or type to search",
    "street": "Street address",
    "postalCode": "Postal code",
    "unavailable": "The location list could not be loaded.",
    "notSetUp": "No locations are set up yet. Ask head office to import them. The street is enough for now.",
    "required": "This field is required.",
    "noMatch": "Nothing matches. Check the spelling or choose from the list."
  },
  "locations": {
    "title": "Import locations",
    "intro": "The countries, provinces and wards every address uses. Import the list that ships with the system, or a newer file. Stores receive the changes by sync.",
    "counts": "{{imported}} of {{total}} divisions imported",
    "invalidFile": "This file is not valid JSON. Choose a file in the same format as the built-in lists.",
    "importing": "Importing…",
    "noDatasets": "No location datasets are available to import.",
    "version": "Dataset of {{version}}",
    "import": "Import",
    "update": "Update",
    "uploadFile": "Import a file",
    "fileHint": "A JSON file in the same format as the built-in lists. Units the file no longer has are closed, never deleted.",
    "done": "Done: {{created}} added, {{updated}} changed, {{closed}} closed."
  },
  "docs": {
    "title": "NextGen POS User Guide",
    "userGuide": "User guide",
    "signIn": "Sign in to the portal",
    "home": "Docs",
    "breadcrumb": "Breadcrumb",
    "pager": "Previous and next page",
    "previous": "Previous",
    "next": "Next",
    "onThisPage": "On this page",
    "copyLink": "Copy link to {{heading}}",
    "linkCopied": "Link copied",
    "backHome": "Back to the guide",
    "search": {
      "label": "Search the guide",
      "placeholder": "Search the guide (press / to focus)",
      "results": "Search results",
      "noResults": "No results for “{{query}}”. Try fewer or different words.",
      "resultsCount_one": "{{count}} result",
      "resultsCount_other": "{{count}} results"
    },
    "callout": {
      "note": "Note",
      "tip": "Tip",
      "warning": "Warning"
    },
    "image": {
      "missing": "Screenshot not available yet",
      "enlarge": "Enlarge image: {{caption}}",
      "label": "Screenshot"
    },
    "loading": "Loading the guide",
    "notTranslated": {
      "title": "This page is not translated yet; showing the English version.",
      "subtitle": "The rest of the guide is in your language."
    },
    "groups": {
      "gettingStarted": "Getting started",
      "pos": "POS",
      "storeManager": "Store manager",
      "hoAdministrator": "HO administrator",
      "kitchenAndPrinters": "Kitchen and printers",
      "reference": "Reference"
    },
    "englishOnly": {
      "title": "The guide is available in English only.",
      "subtitle": "The screens and buttons you use are in your language; the guide text is in English, with the Vietnamese labels in brackets."
    },
    "notFound": {
      "title": "Page not found",
      "description": "This page of the guide does not exist. It may have moved: search the guide or start from the home page."
    },
    "landing": {
      "heading": "NextGen POS User Guide",
      "intro": "How to use NextGen POS day to day: at the till, in the store office and at head office. Choose the part that matches your job, or search the guide.",
      "start": "Start with the first chapter",
      "tiles": {
        "pos": {
          "title": "Using the POS",
          "text": "Orders, kitchen, bills, payment, delivery and the business day, for cashiers and waiters."
        },
        "store": {
          "title": "Store manager",
          "text": "The store admin portal: overview, orders, kitchen monitoring and the head office connection."
        },
        "ho": {
          "title": "HO administrator",
          "text": "Menus and prices, POS setup, devices, tables, delivery, staff and roles, sync and logs."
        },
        "kitchen": {
          "title": "Kitchen and printers",
          "text": "How a sent order becomes kitchen tickets, and how to connect and test printers."
        },
        "troubleshooting": {
          "title": "Troubleshooting",
          "text": "Problems and answers for the POS, printing, payment and sync, with frequently asked questions."
        }
      }
    }
  },
  "shiftSetup": {
    "copy": {
      "button": "Copy from another store",
      "title": "Copy shifts from another store",
      "confirm": "Copy shifts",
      "from": "Copy from",
      "to": "To store",
      "replaces": "Its shifts are replaced by the copied ones.",
      "settings": "Copy the shift settings too",
      "done": "{{count}} shifts copied."
    }
  },
  "rules": {
    "conditions": {
      "sale_type": "Sale type",
      "sales_channel": "Sales channel",
      "store": "Store",
      "table_area": "Table area",
      "guest_count": "Number of guests",
      "order_subtotal": "Order amount",
      "days_of_week": "Days of the week",
      "time_window": "Time of day",
      "date_range": "Dates",
      "item_in_order": "Items in the order",
      "category_in_order": "Categories in the order",
      "target_line_item": "The discounted item",
      "member_tier": "Member's tier",
      "member_points": "Points available",
      "member_orders": "Paid orders",
      "member_days_since_joined": "Days since joining",
      "member_days_since_order": "Days since the last order",
      "member_marketing_consent": "Agreed to marketing",
      "customer_is_member": "Is a member",
      "customer_orders": "Paid orders, all time",
      "customer_orders_30d": "Paid orders, last 30 days",
      "customer_orders_90d": "Paid orders, last 90 days",
      "customer_orders_365d": "Paid orders, last 365 days",
      "customer_spend": "Spent, all time",
      "customer_spend_30d": "Spent, last 30 days",
      "customer_spend_90d": "Spent, last 90 days",
      "customer_spend_365d": "Spent, last 365 days",
      "customer_average_order": "Average order",
      "customer_days_since_order": "Days since the last order",
      "customer_days_since_first_order": "Days since the first order",
      "customer_store": "Ordered at",
      "customer_channel": "Ordered through",
      "customer_category": "Bought, last 365 days",
      "customer_birth_month": "Birth month",
      "customer_birthday_this_month": "Birthday this month",
      "customer_consent_zns": "Agreed to Zalo messages",
      "customer_consent_sms": "Agreed to SMS",
      "customer_consent_email": "Agreed to email"
    },
    "groups": {
      "order": "The order",
      "where": "Where",
      "when": "When",
      "items": "Items",
      "member": "The member",
      "customer": "The person",
      "habits": "Order habits"
    },
    "params": {
      "in": "Any of",
      "min": "At least",
      "max": "At most",
      "days": "Days",
      "from": "From",
      "to": "To",
      "minQuantity": "Quantity at least",
      "items": "Items",
      "categories": "Categories"
    },
    "days": {
      "1": "Mon",
      "2": "Tue",
      "4": "Wed",
      "8": "Thu",
      "16": "Fri",
      "32": "Sat",
      "64": "Sun"
    },
    "reason": {
      "points_not_with_discounts": "Points can't be used with other discounts on the same bill, so they came off. Take the other discount off to redeem them again.",
      "points_over_limit": "Points come off when they are more than {{max}}% of the items before VAT. Redeem them again for the new total.",
      "sale_type": "Not for this sale type.",
      "sales_channel": "Not for this sales channel.",
      "store": "Not at this store.",
      "table_area": "Not for this table's area.",
      "guest_count": "Not for this number of guests ({{actual}}).",
      "order_subtotal": "Not for this order amount ({{actual}}).",
      "days_of_week": "Not on this day.",
      "time_window": "Only from {{from}} to {{to}}.",
      "date_range": "Not on this date.",
      "item_in_order": "The order doesn't have the items it needs.",
      "category_in_order": "The order doesn't have the items it needs.",
      "target_line_item": "Not for this item.",
      "excluded": "Not for this order.",
      "unusable": "Its rules can't be read at this store. Ask head office.",
      "evaluation_failed": "Its rules couldn't be checked. Ask head office."
    },
    "builder": {
      "always": "Always applies",
      "alwaysHint": "Add a condition to limit when it applies.",
      "match": "Applies when",
      "all": "All of these",
      "any": "Any of these",
      "exclude": "Except when this matches",
      "addCondition": "Add condition",
      "addGroup": "Add group",
      "remove": "Remove {{name}}",
      "group": "Group",
      "invalid": "These rules can't be used: {{problem}}",
      "loading": "Loading conditions",
      "and": "and",
      "or": "or",
      "choose": "Choose",
      "excluded": "Except"
    }
  },
  "chargeTypes": {
    "amountEntryHelp": "The cashier types the value when adding this charge on the POS; Value is what the POS suggests.",
    "maxValueHelp": "The most the cashier may type (a percent stays at most 100). Leave empty for no limit."
  },
  "promotions": {
    "downloadCodes": "Download codes (CSV)",
    "downloadVouchers": "Download vouchers (CSV)",
    "downloadFailed": "The file couldn't be downloaded. Try again."
  },
  "loyalty": {
    "points": {
      "tab": "Points",
      "balance": "{{points}} points",
      "reserved": "{{points}} held by open orders",
      "adjust": "Adjust points",
      "adjustSave": "Adjust",
      "adjustPoints": "Points",
      "adjustHelp": "Positive to give points, negative to take them away.",
      "adjustNote": "Reason",
      "empty": "No points yet",
      "columns": {
        "when": "When",
        "type": "Type",
        "points": "Points",
        "balance": "Balance",
        "note": "Note",
        "tierPoints": "Tier points",
        "expiresAt": "Expires"
      },
      "types": {
        "earn": "Earned",
        "reverse": "Taken back",
        "redeem": "Paid with",
        "refund": "Given back",
        "adjust": "Adjusted",
        "reward": "Rewards program",
        "expire": "Expired"
      },
      "tierPoints": "{{points}} tier points",
      "expiring": "{{points}} expire on {{date}}"
    },
    "cards": {
      "generate": "Generate cards",
      "generateSave": "Generate",
      "quantity": "Number of cards",
      "quantityHelp": "1 to 1,000 free cards from the club's series, to print and hand out.",
      "made": "Made {{count}} cards: {{first}} to {{last}}."
    },
    "viewOrders": "View orders",
    "history": {
      "title": "Member points",
      "hint": "Every member's points movements, newest first. Search by an account, card or order number; open a row for the member's account.",
      "capped": "Over 10,000 entries match. Narrow the filters or search to see them all.",
      "account": "Account",
      "order": "Order",
      "store": "Store"
    },
    "rewards": {
      "tab": "Rewards",
      "title": "Coupons and gift vouchers",
      "hint": "Issued to this member by hand or by a Rewards program. Codes are shown only to the member.",
      "issue": "Issue a reward",
      "issueSave": "Issue",
      "kind": "What to issue",
      "coupon": "Coupon",
      "giftVoucher": "Gift voucher",
      "promotion": "Promotion",
      "promotionHelp": "A new single-use code of an active promotion.",
      "batch": "Gift voucher batch",
      "batchHelp": "A free voucher of the batch is given to the member.",
      "validDays": "Valid for (days)",
      "validDaysHelp": "Leave empty to last as long as the promotion or voucher.",
      "note": "Note",
      "empty": "No rewards issued to this member yet.",
      "noEnd": "No end",
      "byHand": "By hand",
      "byProgram": "Rewards program",
      "columns": {
        "issued": "Issued",
        "kind": "Kind",
        "code": "Code",
        "validTo": "Valid to",
        "source": "Issued by",
        "note": "Note"
      },
      "points": "Points"
    },
    "issueList": {
      "title": "Issue rewards to members",
      "hint": "The same coupon or gift voucher to a list of members by account number, up to 1,000 at a time. Sending the same list again issues nothing twice.",
      "batchHelp": "Each member gets a free voucher of the batch; members beyond what is left are listed.",
      "accounts": "Account numbers",
      "accountsHelp_one": "{{count}} account: one per line, or separated by commas.",
      "accountsHelp_other": "{{count}} accounts: one per line, or separated by commas.",
      "tooMany": "At most 1,000 account numbers at a time.",
      "issue_one": "Issue to {{count}} member",
      "issue_other": "Issue to {{count}} members",
      "issued_one": "Issued to {{count}} member.",
      "issued_other": "Issued to {{count}} members.",
      "refused": "Not issued",
      "accountNo": "Account",
      "reason": "Why",
      "another": "Start another list"
    },
    "programs": {
      "triggers": {
        "help": "Events reward members as they happen; a schedule or conditions check every member of the club.",
        "chooseFirst": "Choose when the program rewards members to set it up.",
        "joined": "Joined the club",
        "birthday": "Birthday",
        "tier_reached": "Tier reached",
        "order_milestone": "Order milestones",
        "schedule": "On a schedule",
        "condition": "Members who meet the conditions",
        "segment_joined": "Joined a segment",
        "segment_joinedHelp": "Members are rewarded when they join the segment chosen in “Only members of segment”, from when the program starts."
      },
      "rewards": {
        "help": "What each member gets, once per occasion.",
        "chooseFirst": "Choose what the program gives to set it up.",
        "points": "Points",
        "coupon": "Coupon",
        "gift_voucher": "Gift voucher"
      },
      "settings": {
        "includeExisting": "Also members who joined before the program started",
        "includeExistingHelp": "Off: only members who join from now on.",
        "daysBefore": "Days before the birthday",
        "daysBeforeHelp": "0 to 60; 0 rewards on the day. A 29 February birthday counts on 28 February in other years.",
        "tierIds": "Tiers",
        "tierIdsHelp": "None chosen: any tier.",
        "orders": "Number of paid orders",
        "ordersHelp": "Orders that earned points, 1 to 10,000.",
        "repeat": "Every time that number is reached again",
        "repeatHelp": "Off: once, at that number.",
        "mode": "How often",
        "modeHelp": "Every active member of the club, once each time.",
        "onDate": "Date",
        "onDateHelp": "In the club's time zone.",
        "day": "Day of the month",
        "dayHelp": "1 to 28.",
        "weekday": "Day of the week",
        "weekdayHelp": "",
        "time": "Time",
        "timeHelp": "From this time of the day.",
        "period": "How often a member can be rewarded",
        "periodHelp": "Every member is checked each day; one who meets the conditions is rewarded at most this often.",
        "points": "Points",
        "pointsHelp": "Above 0, at most 2 decimals.",
        "promotionId": "Promotion",
        "promotionIdHelp": "An active promotion; each member gets a single-use code of it.",
        "voucherBatchId": "Gift voucher batch",
        "voucherBatchIdHelp": "Each member gets one free voucher of the batch while it has some.",
        "validDays": "Valid for (days)",
        "validDaysHelp": "Empty: as long as the promotion or voucher lasts.",
        "modes": {
          "once": "Once",
          "monthly": "Every month",
          "weekly": "Every week"
        },
        "weekdays": {
          "1": "Monday",
          "2": "Tuesday",
          "3": "Wednesday",
          "4": "Thursday",
          "5": "Friday",
          "6": "Saturday",
          "7": "Sunday"
        },
        "periods": {
          "once": "Once",
          "year": "Once a year",
          "month": "Once a month",
          "week": "Once a week"
        }
      }
    }
  },
  "printTemplates": {
    "preview": "Preview",
    "fields": "Available fields",
    "help": "Tags and functions",
    "previewHint": "A made-up {{document}} laid out on {{width}} mm paper, as a store prints it. Bold and double-size lines are shown as printed.",
    "chooseType": "Choose the document to see a preview.",
    "previewError": "Line {{line}}, column {{column}}: {{message}}",
    "previewErrorNoLine": "The template can't be printed: {{message}}",
    "previewFailed": "The preview isn't available right now.",
    "startFromBuiltIn": "Start from the built-in layout",
    "replaceTitle": "Replace the template?",
    "replaceBody": "The template text is replaced with the built-in layout of this document. What you typed is lost.",
    "replace": "Replace",
    "footerOnly": "Footer text prints under the built-in bill or receipt, as typed. Choose Template to lay out the whole document.",
    "fieldsHint": "What a {{document}} has, with a made-up example. Lists are used with for, e.g. {{loop}}.",
    "field": "Field",
    "example": "Example",
    "drawer": "Cash drawer opens",
    "cut": "Paper cut",
    "helpIntro": "A template is Scriban text. Each line of output is one printed line; empty lines are left out. Start a line with tags to style it.",
    "tags": "Tags",
    "functions": "Functions",
    "tag": {
      "center": "Centre the line",
      "right": "Align the line right",
      "b": "Bold",
      "double": "Double size (half as many characters fit)",
      "rule": "A line across the paper (<rule => draws with =)",
      "feed": "Empty lines (<feed 2> for two)",
      "cut": "Cut the paper",
      "drawer": "Open the cash drawer (only when the payment asks for it)",
      "qr": "A centred QR code of a link (<qr 5 link> for larger dots) (the receipt's VAT invoice link is invoice_form_url)"
    },
    "function": {
      "money": "An amount in the order's currency",
      "qty": "A quantity without trailing zeros",
      "time": "A time in the store's time zone; a format such as \"HH:mm\" is optional",
      "row": "A label with a value at the right end of the line",
      "cols": "Columns; a negative width aligns right",
      "rule": "A line of dashes across the paper",
      "pad": "pad_left, pad_right, fit: fit text to a width",
      "t": "The text in the template's language"
    },
    "paper": {
      "columns": "paper.columns: characters per line (32 or 48)"
    }
  },
  "einvoice": {
    "status": {
      "not_configured": "No profile",
      "pending": "Waiting to send",
      "submitting": "Sending",
      "uncertain": "Checking with provider",
      "awaiting_number": "Waiting for number",
      "issued": "Issued",
      "failed": "Failed",
      "cancelled": "Not sent (order voided)",
      "not_reissued": "Not invoiced again (original issued)"
    },
    "needsAdjustment": "Refunded: adjust",
    "list": {
      "title": "E-invoices",
      "intro": "The e-invoice head office issues for every paid order, in each store's order. Correct and resend failed ones; adjust refunded ones at the provider.",
      "needsAdjustmentOnly": "Refunded after issue only",
      "back": "Back to e-invoices",
      "needsCancellationOnly": "Voided after issue only"
    },
    "columns": {
      "paidAt": "Paid",
      "order": "Order",
      "store": "Store",
      "buyer": "Buyer",
      "total": "Total",
      "invoiceNo": "Invoice no.",
      "status": "Status"
    },
    "buyer": {
      "retail": "Retail (buyer does not take the invoice)",
      "company": "Company",
      "companyName": "Company name",
      "taxCode": "Tax code",
      "address": "Address",
      "email": "Email",
      "emailKept": "Leave empty to keep the saved address."
    },
    "detail": {
      "intro": "What was sent to the provider and what it answered.",
      "seller": "Seller (tax code · template · series)",
      "lookupCode": "Lookup code",
      "taxAuthorityCode": "Tax authority code",
      "issuedAt": "Issued",
      "emailedAt": "Emailed",
      "error": "Error",
      "nextAttempt": "Next attempt",
      "attempts": "Attempts",
      "transactionUuid": "Transaction ID",
      "refunded": "Refunded",
      "adjustmentNote": "Adjustment note",
      "adjustmentNoteHelp": "What was done at the provider, e.g. the adjustment invoice's number.",
      "pdf": "Download PDF",
      "email": "Email invoice",
      "send": "Send",
      "emailToBuyer": "Leave empty to send to the buyer ({{email}}).",
      "emailNeeded": "The buyer has no email: enter one.",
      "emailed": "The invoice was sent by email.",
      "fixBuyer": "Correct buyer",
      "saveAndSend": "Save and send again",
      "retry": "Send again",
      "retried": "Queued to send again.",
      "sendNow": "Send now",
      "sentNow": "It goes to the provider within a few seconds, with any earlier invoices of this store that were still waiting.",
      "adjustmentDone": "Mark adjusted",
      "needsAdjustmentTitle": "Refunded after the invoice was issued",
      "needsAdjustmentBody": "{{amount}} was refunded. Issue an adjustment invoice at the provider, then mark it adjusted.",
      "lines": "Invoice lines",
      "linesIntro": "Amounts before tax, as sent to the provider; the totals per tax rate are the receipt's.",
      "buyerFromReceipt": "Company sent from the receipt QR",
      "cancellationDone": "Mark adjusted",
      "needsCancellationTitle": "Its order was voided after the invoice was issued",
      "needsCancellationBody": "The paid bill was voided the same day and rung again. The new bill isn’t invoiced again: adjust or cancel this invoice by hand on the provider’s portal so it matches the POS, then mark it adjusted.",
      "notReissuedTitle": "Not invoiced again",
      "notReissuedBody": "This bill was rung again after a paid bill was voided, and that bill’s invoice was already issued. Accounting adjusts that invoice by hand to match this bill."
    },
    "lines": {
      "name": "Item",
      "quantity": "Qty",
      "amount": "Amount before tax",
      "rate": "VAT",
      "tax": "Tax",
      "noVat": "No VAT",
      "atRate": "Total at {{rate}}"
    },
    "providers": {
      "title": "E-invoice providers",
      "intro": "The accounts head office issues e-invoices with, one or more per legal entity. Credentials are entered here and never shown again.",
      "add": "Add e-invoice account",
      "empty": "Add the account of your e-invoice provider, for the legal entity it issues for.",
      "legalEntityHelp": "The company the account issues invoices for.",
      "chooseLegalEntity": "Choose a legal entity"
    },
    "needsCancellation": "Voided: adjust by hand"
  },
  "itemGroups": {
    "hint": "One product shown as one item: its variants are separate items (own price, stock and kitchen name), chosen by size, colour or another dimension. Each variant has one value per dimension. Changes are saved together.",
    "tab": "Variants",
    "dimensions": "Dimensions",
    "noDimensions": "No dimensions yet",
    "noDimensionsHelp": "Add a dimension, such as size, then its values.",
    "size": "Kích thước",
    "nameEn": "Name (English)",
    "display": "Shown as",
    "addDimension": "Add dimension",
    "removeDimension": "Remove dimension {{name}}",
    "valuesOf": "Values of {{name}}",
    "addValue": "Add value",
    "removeValue": "Remove value {{name}}",
    "valueField": {
      "code": "Code",
      "label": "Label",
      "labelEn": "Label (English)",
      "shortLabel": "Short label",
      "swatchColor": "Swatch colour"
    },
    "members": "Variants",
    "noMembers": "No variants yet",
    "noMembersHelp": "Add the items that are this product's variants.",
    "addMember": "Add an item",
    "searchItem": "Search by code or name",
    "choose": "Choose",
    "valueOf": "{{dimension}} of {{item}}",
    "defaultOf": "{{item}} is the default",
    "moveUp": "Move {{item}} up",
    "moveDown": "Move {{item}} down",
    "removeMember": "Remove {{item}}",
    "unsaved": "Unsaved changes",
    "discard": "Discard",
    "save": "Save variants",
    "conflict": "Someone else changed this group. Discard to load their version.",
    "problem": {
      "dimensionCode": "Every dimension needs a code (letters, digits, - or _), different from the others.",
      "dimensionName": "Give every dimension a name.",
      "noValues": "Every dimension needs at least one value.",
      "valueCode": "Every value needs a code, different from the others in its dimension.",
      "valueLabel": "Give every value a label.",
      "swatch": "Write a swatch colour like #C73F00.",
      "memberValue": "Choose a value of each dimension for every variant.",
      "duplicate": "Two variants have the same values.",
      "needsDimension": "Add a dimension before adding a second variant."
    },
    "suggest": {
      "button": "Suggest groups",
      "title": "Suggested item groups",
      "help": "Items whose names differ only by a size tag, such as \"Margherita (R)\" and \"Margherita (L)\". Tick the ones to create; you can change them afterwards.",
      "none": "No suggestions: no items of this brand differ only by a size tag.",
      "create_one": "Create {{count}} group",
      "create_other": "Create {{count}} groups"
    }
  },
  "tableQr": {
    "title": "Table QR codes",
    "intro": "Guests scan the code on their table to see the store's menu and send orders the cashier confirms. Make codes for new tables, print them, and change a code when its card is lost or copied.",
    "store": "Store",
    "chooseStore": "Choose a store",
    "pickStore": "Choose a store",
    "pickStoreHint": "Its tables and their QR codes show here.",
    "tableLabel": "Table QR codes of {{store}}",
    "columns": {
      "table": "Table",
      "area": "Area",
      "status": "Status",
      "rotation": "Version",
      "rotatedAt": "Last changed",
      "active": "QR ordering"
    },
    "status": {
      "none": "No code yet",
      "active": "On",
      "off": "Off",
      "tableOff": "Table not in use"
    },
    "on": "On",
    "off": "Off",
    "activeLabel": "QR ordering at table {{code}}",
    "generate_one": "Make a code for {{count}} table",
    "generate_other": "Make codes for {{count}} tables",
    "generating": "Making codes",
    "generated_one": "{{count}} code made. Download the print sheet to print it.",
    "generated_other": "{{count}} codes made. Download the print sheet to print them.",
    "download": "Download print sheet",
    "downloading": "Preparing the sheet",
    "rotate": "Change code",
    "rotateTitle": "Change the code of table {{code}}?",
    "rotateBody": "The card printed for table {{code}} stops working at once; guests must scan the new one. Print the new card and replace the old one on the table.",
    "rotated": "Table {{code}} has a new code. Print it and replace the card on the table.",
    "rotateAll": "Change all codes",
    "rotateAllTitle": "Change every code of {{store}}?",
    "rotateAllBody_one": "The {{count}} printed card at this store stops working at once. Print the new sheet and replace the card.",
    "rotateAllBody_other": "All {{count}} printed cards at this store stop working at once. Print the new sheet and replace the card on every table.",
    "rotatedAll_one": "{{count}} code changed. Download the print sheet and replace the card.",
    "rotatedAll_other": "{{count}} codes changed. Download the print sheet and replace the cards.",
    "turnedOn": "QR ordering at table {{code}} is on.",
    "turnedOff": "QR ordering at table {{code}} is off. Guests scanning its card see that it isn't available.",
    "emptyTitle": "No tables",
    "empty": "This store has no tables yet. Set them up under Tables.",
    "copyLink": "Copy link",
    "copied": "The link of table {{code}} is copied. Anyone with it can order at that table: share it only to try it.",
    "linkMissing": "Table {{code}} has no link to copy. Change its code to get one."
  },
  "images": {
    "title": "Images",
    "intro": "Upload an image once and use its address for logos, item images and the website. Images are checked, turned upright, shrunk to 2048 px and kept as WebP.",
    "columns": {
      "image": "Image",
      "name": "Name",
      "size": "Size",
      "brand": "Brand",
      "uploaded": "Uploaded"
    },
    "allBrands": "All brands",
    "untitled": "Untitled image",
    "size": "{{width}} × {{height}} · {{kb}} KB",
    "copy": "Copy address",
    "copied": "The address of \"{{name}}\" is copied. Paste it into an image field.",
    "copyFailed": "The address couldn't be copied. Select it and copy it yourself: {{url}}",
    "deleteTitle": "Delete image?",
    "deleteBody": "\"{{name}}\" is removed from this list and from the pickers. Pages that already show it keep showing it.",
    "deleted": "Image deleted.",
    "empty": "No images yet. Upload one with Upload images.",
    "emptyReadOnly": "No images yet.",
    "upload": {
      "title": "Upload images",
      "hint": "JPEG, PNG, WebP or GIF, up to 10 MB each.",
      "drop": "Drag and drop images here or click to choose them",
      "dropOne": "Drag and drop an image here or click to choose it",
      "brandHelp": "Leave empty to use the image for every brand.",
      "altText": "Description",
      "altTextHelp": "Says what the image shows, for people who can't see it.",
      "invalidType": "This file isn't a JPEG, PNG, WebP or GIF image.",
      "tooLarge": "The image is larger than 10 MB.",
      "uploading": "Uploading",
      "uploaded": "Uploaded",
      "remove": "Remove {{name}}",
      "done_one": "{{count}} image uploaded.",
      "done_other": "{{count}} images uploaded.",
      "existing_one": "{{count}} image was already uploaded, so the existing one is used.",
      "existing_other": "{{count}} images were already uploaded, so the existing ones are used."
    },
    "picker": {
      "choose": "Choose image",
      "title": "Choose an image",
      "legend": "Uploaded images",
      "use": "Use image",
      "preview": "Current image: {{label}}",
      "empty": "No images yet. Upload one above."
    },
    "search": "Search by name or description",
    "noDescription": "No description",
    "dimensions": "{{width}} × {{height}} px",
    "fileSize": "{{kb}} KB"
  },
  "brandSites": {
    "theme": {
      "previewName": "Your brand",
      "primary": "Main colour",
      "primaryHelp": "Buttons and links; white text on it must be readable (4.5:1).",
      "accent": "Highlight colour",
      "accentHelp": "Optional: deal and \"new\" badges.",
      "hex": "Write a colour as #RRGGBB, e.g. #C73F00.",
      "fontPair": "Fonts",
      "fonts": {
        "be-vietnam-pro": "Be Vietnam Pro",
        "inter": "Inter",
        "nunito": "Nunito",
        "roboto-slab+inter": "Roboto Slab headings, Inter text"
      },
      "radius": "Corners",
      "radii": {
        "sharp": "Sharp",
        "soft": "Soft",
        "round": "Round"
      },
      "neutralTint": "Greys",
      "tints": {
        "warm": "Warm",
        "cool": "Cool",
        "pure": "Neutral"
      },
      "darkMode": "Dark mode",
      "darkOff": "Off",
      "darkAuto": "Follows the device",
      "preview": "Preview",
      "sampleText": "Rich broth, rare beef, fresh herbs.",
      "sampleButton": "Add to cart",
      "sampleChip": "Pizza",
      "sampleBadge": "Deal",
      "sampleLink": "See the menu",
      "contrastOk": "Every text is readable (WCAG AA)",
      "contrastProblems_one": "{{count}} colour pair is hard to read",
      "contrastProblems_other": "{{count}} colour pairs are hard to read",
      "broken": "The saved theme isn't valid JSON; choosing values here replaces it."
    }
  },
  "messaging": {
    "providers": {
      "title": "Messaging accounts",
      "intro": "The Zalo ZNS, SMS and email (SMTP) accounts that send customer messages. Each message tries its channels in the order set under Customer message templates › Channel order.",
      "empty": "No messaging accounts yet.",
      "add": "Add account",
      "brand": "Brand",
      "brandHelp": "The account sends for one brand, or for all of them.",
      "allBrands": "All brands"
    },
    "templates": {
      "title": "Customer message templates",
      "intro": "What customers are told when their online order is accepted, ready, on its way, completed or not taken, when their points are about to expire, and the brand's marketing emails, per channel and language. A message without a template isn't sent.",
      "add": "Add template",
      "brand": "Brand",
      "empty": "This brand has no message templates yet.",
      "columns": {
        "event": "Message",
        "channel": "Channel",
        "language": "Language",
        "content": "Content",
        "status": "Status",
        "purpose": "Purpose"
      },
      "znsId": "ZNS template id",
      "znsHelp": "The template id Zalo approved for the brand's Official Account.",
      "smsBody": "SMS text",
      "smsHelp": "You can use {{values}}.",
      "deleteTitle": "Delete this template?",
      "deleteBody": "This message won't be sent on that channel and language any more.",
      "parameters": {
        "title": "ZNS parameters",
        "help": "Name each parameter as in the template Zalo approved and fill it like an SMS text, with {{values}}. With no parameters, the values are sent under these names.",
        "name": "Parameter",
        "value": "Value",
        "add": "Add parameter",
        "remove": "Remove parameter"
      },
      "tabs": {
        "templates": "Templates",
        "channels": "Channel order",
        "email": "Email settings"
      },
      "purpose": {
        "marketing": "Marketing",
        "service": "Service"
      },
      "name": "Template name",
      "nameHelp": "Marketing emails are chosen by name when a campaign is sent; a brand can have several.",
      "marketingEmailOnly": "Marketing templates are emails for now."
    },
    "events": {
      "order_accepted": "Order accepted",
      "order_ready": "Order ready",
      "order_on_delivery": "Order on its way",
      "order_completed": "Order completed",
      "order_not_taken": "Order not taken",
      "login_code": "Sign-in code",
      "reservation_confirmed": "Reservation confirmed",
      "reservation_declined": "Reservation declined",
      "reservation_cancelled": "Reservation cancelled",
      "points_expiring": "Points expiring",
      "marketing": "Marketing"
    },
    "channels": {
      "zalo_zns": "Zalo ZNS",
      "sms": "SMS",
      "email": "Email"
    },
    "log": {
      "title": "Customer messages",
      "intro": "What customers were told, and what wasn't sent and why. Messages taken by a test-mode account are marked \"Test\": nothing reached the customer. Phone numbers are masked.",
      "empty": "No messages for this brand yet.",
      "allStatuses": "All statuses",
      "test": "Test",
      "sampleMessage": "test message",
      "columns": {
        "time": "Time",
        "recipient": "Recipient",
        "status": "Status"
      }
    },
    "status": {
      "pending": "Waiting",
      "sending": "Sending",
      "sent": "Sent",
      "failed": "Will retry",
      "skipped": "Not sent",
      "dead": "Failed"
    },
    "errors": {
      "no_messaging_account": "the brand has no messaging account",
      "no_template": "no template for this message",
      "recipient_unreachable": "this channel can't reach the recipient",
      "message_refused": "the provider refused the message",
      "account_rejected": "the account or its keys don't work",
      "unavailable": "the provider is unavailable",
      "other": "another error"
    },
    "test": {
      "action": "Send test",
      "title": "Send a test message",
      "intro": "The test goes through the brand's messaging accounts, with order TEST-0001 and a test store. With a test-mode account nothing is delivered: see what would be sent in Customer messages.",
      "recipient": "Phone number",
      "recipientHelp": "For example 0912 846 435.",
      "send": "Send test",
      "queued": "Test message queued. See the result in Customer messages.",
      "emailRecipient": "Email address",
      "emailRecipientHelp": "For example name@example.com."
    },
    "email": {
      "subject": "Subject",
      "unsubscribe": "Unsubscribe from these emails",
      "preview": {
        "title": "Preview",
        "help": "With sample values, in the brand's email layout. Links and buttons in the preview go nowhere."
      },
      "sender": {
        "override": "Sender (optional)",
        "brandDefault": "Brand's default",
        "account": "SMTP profile",
        "accountHelp": "The email account this goes through.",
        "noAccounts": "No email accounts yet: add one under Messaging accounts.",
        "accountGone": "Account no longer available",
        "fromName": "From name",
        "fromAddress": "From address",
        "replyTo": "Reply-to address",
        "emailInvalid": "Enter an email address such as name@example.com."
      },
      "settings": {
        "title": "Brand email settings",
        "intro": "Who the brand's emails are from, and the layout every email of the brand is wrapped in.",
        "service": "Service emails (order updates, points reminders)",
        "marketing": "Marketing emails",
        "accountDefault": "Default: the brand's email account, else the company's",
        "layout": "Layout",
        "logo": "Logo address",
        "logoHelp": "An image starting with https://, shown at the top of every email.",
        "accent": "Accent colour",
        "background": "Background colour",
        "colourHelp": "For example #0f62fe.",
        "colourInvalid": "Enter a colour as # and six digits or letters a–f.",
        "footer": "Footer",
        "footerHelp": "Lines under every email: the business's name, address, hotline.",
        "links": "Footer links",
        "linksHelp": "Up to {{max}} links, such as the website, Facebook or Zalo.",
        "linkLabel": "Link text",
        "linkUrl": "Link",
        "removeLink": "Remove link",
        "addLink": "Add link",
        "invalid": "Check the highlighted fields.",
        "pick": "Pick a colour",
        "logoInvalid": "Enter an address starting with https://.",
        "linkInvalid": "Enter a link starting with https://, http://, mailto: or tel:."
      },
      "sample": {
        "subject": "Your order {order_number} is ready",
        "heading": "Your order is ready",
        "text": "Order {order_number} is waiting for you at {store_name}.",
        "button": "Track your order"
      },
      "body": "Content",
      "bodyHelp": "Insert the message's values from the toolbar, or type them as {name}.",
      "bodyHelpNoValues": "Marketing emails have no values yet; campaigns will add their own."
    },
    "routes": {
      "intro": "The channels each message goes by, first tried first, for every brand. A channel is used when the brand has an account and a template for it and the customer has that address; otherwise, or when it can't reach the customer, the next one is tried.",
      "default": "Default order",
      "up": "Move up",
      "down": "Move down",
      "noneOn": "Turn on at least one channel.",
      "save": "Save",
      "saving": "Saving",
      "saved": "Saved"
    },
    "values": {
      "order_number": "Order number",
      "store_name": "Store name",
      "prep_minutes": "Minutes to prepare",
      "reason": "Reason",
      "reference": "Booking reference",
      "time": "Time",
      "party_size": "Party size",
      "code": "Code",
      "minutes": "Minutes valid",
      "points": "Points",
      "expiry_date": "Expiry date",
      "club_name": "Club name",
      "account_no": "Account number"
    }
  },
  "translations": {
    "title": "Translations",
    "intro": "What customers read on the websites, apps and table QR pages, in each language the brand offers. A text without a translation shows in the brand's default language.",
    "brand": "Brand",
    "kind": "Kind of text",
    "allKinds": "All kinds",
    "show": "Show",
    "allTexts": "All texts",
    "missingIn": "Missing in {{language}}",
    "summary": "Translations missing per language",
    "noSite": "This brand has no website yet: the platform's customer languages are shown.",
    "missingCount_one": "{{language}}: {{count}} missing",
    "missingCount_other": "{{language}}: {{count}} missing",
    "complete": "{{language}}: complete",
    "defaultLanguage": "{{language}} (default)",
    "missing": "Missing",
    "search": "Search texts",
    "empty": "No texts match.",
    "emptyMissing": "Nothing is missing in this language.",
    "columns": {
      "text": "Text"
    },
    "save": "Save",
    "saveCount_one": "Save {{count}} change",
    "saveCount_other": "Save {{count}} changes",
    "saved_one": "{{count}} text saved.",
    "saved_other": "{{count}} texts saved.",
    "refused_one": "{{count}} change wasn't saved. Load the page again and check it.",
    "refused_other": "{{count}} changes weren't saved. Load the page again and check them.",
    "download": "Download CSV",
    "downloadFailed": "The file couldn't be downloaded. Try again.",
    "import": "Import CSV",
    "importing": "Importing translations…",
    "imported": "Imported: {{updated}} texts changed, {{unchanged}} already as in the file.",
    "importRefused_one": "{{count}} line wasn't saved: line {{lines}}.",
    "importRefused_other": "{{count}} lines weren't saved: lines {{lines}}.",
    "importHeader": "This file isn't a translations export. Download the CSV, fill in the language columns and import that file.",
    "kinds": {
      "category": "Category",
      "item": "Item",
      "modifier_group": "Option group",
      "item_group": "Item group",
      "dimension": "Variant choice",
      "dimension_value": "Variant",
      "deal": "Deal",
      "deal_component": "Deal part"
    },
    "fields": {
      "name": "Name",
      "label": "Label",
      "description": "Description"
    }
  },
  "menuImport": {
    "title": "Import menu data",
    "intro": "Categories, items, options, prices, menus and POS buttons from one Excel workbook. Rows are matched by code, and nothing is removed.",
    "brand": "Brand",
    "chooseBrand": "Choose a brand",
    "download": "Download workbook",
    "downloadFailed": "The workbook couldn't be downloaded. Try again.",
    "fileHint": "Download the workbook, change it, then add it here. Only .xlsx files up to 10 MB.",
    "drop": "Drag and drop the workbook here or click to choose it",
    "invalidType": "Only Excel workbooks (.xlsx) can be imported.",
    "tooLarge": "The file is larger than 10 MB.",
    "remove": "Remove {{name}}",
    "checking": "Checking the workbook",
    "importing": "Importing",
    "summary": "What the import does",
    "emptyFile": "The workbook has no rows to import.",
    "problems_one": "{{count}} problem; nothing can be imported until it's fixed.",
    "problems_other": "{{count}} problems; nothing can be imported until they're fixed.",
    "firstProblems_one": "Only the first problem is listed.",
    "firstProblems_other": "Only the first {{count}} problems are listed.",
    "problemsTitle": "Problems",
    "searchProblems": "Search problems",
    "noMatch": "No problems match the search.",
    "import": "Import",
    "done": "Imported: {{created}} created, {{updated}} changed, {{restored}} restored, {{unchanged}} unchanged. Stores get the changes when you publish master data, as for any change.",
    "toolbar": "Import",
    "columns": {
      "sheet": "Sheet",
      "created": "New",
      "updated": "Changed",
      "restored": "Restored",
      "unchanged": "Unchanged",
      "row": "Row",
      "column": "Column",
      "message": "Problem"
    },
    "sheet": {
      "categories": "Categories",
      "items": "Items",
      "units": "Units",
      "option_groups": "Option groups",
      "options": "Options",
      "item_options": "Item options",
      "deals": "Deals",
      "deal_components": "Deal components",
      "deal_choices": "Deal choices",
      "menus": "Menus",
      "layouts": "Layouts",
      "price_lists": "Price lists",
      "prices": "Prices",
      "menu_items": "Menu items",
      "buttons": "POS buttons"
    }
  },
  "channelStores": {
    "addAll": {
      "button": "Add stores",
      "title": "Put stores on a channel",
      "confirm": "Add stores",
      "channel": "Online channel",
      "brand": "Brand",
      "brandHelp": "Leave empty for every store of every brand.",
      "status": "Add them as",
      "closed": "Closed: open each store when it is ready",
      "open": "Open: they take orders at once",
      "done": "{{added}} stores added; {{alreadyOn}} were on the channel already and were left as they were."
    }
  },
  "onlinePayments": {
    "providers": {
      "title": "Payment accounts",
      "intro": "The MoMo, ZaloPay and VNPay accounts customers pay with, on the website and app and at the counter (a QR or wallet tender names its account), for a brand or all of them. Keys are entered here, kept encrypted and never shown again; stores and the website never receive them.",
      "empty": "No payment accounts yet.",
      "add": "Add payment account"
    }
  },
  "payments": {
    "profiles": {
      "title": "Payment profiles",
      "intro": "Which merchant accounts take payments: each profile belongs to one company and has an account per provider (MoMo, VNPAY, ZaloPay…). Use a profile for a brand, a store, a sales channel or a mix; the most specific wins, and a store only ever pays into its own company's accounts.",
      "add": "Add profile",
      "editTitle": "Edit payment profile",
      "empty": "No payment profiles yet. Without one, each provider's account for the store, brand or tenant is used.",
      "columns": {
        "name": "Profile",
        "entity": "Company",
        "accounts": "Accounts",
        "places": "Used for",
        "status": "Status"
      },
      "noAccounts": "No accounts",
      "placeCount_one": "{{count}} place",
      "placeCount_other": "{{count}} places",
      "active": "Active",
      "inactive": "Off",
      "accounts": "Accounts",
      "places": "Where it is used",
      "deleteTitle": "Delete this payment profile",
      "deleteBody": "Its stores and channels will no longer use it. Payments already taken are kept.",
      "code": "Code",
      "name": "Name",
      "entity": "Company (legal entity)",
      "entityHelp": "The merchant whose accounts these are. Only its stores can use the profile.",
      "accountsTitle": "Accounts of {{name}}",
      "noAccount": "None (not offered)",
      "placesTitle": "Where {{name}} is used",
      "addPlace": "Add",
      "noPlaces": "Not used anywhere yet.",
      "place": "Brand: {{brand}} · Store: {{store}} · Channel: {{channel}}",
      "removePlace": "Stop using it here",
      "all": "All",
      "brand": "Brand",
      "store": "Store",
      "channel": "Sales channel"
    },
    "reconciliation": {
      "title": "Payment reconciliation",
      "intro": "Every payment through a provider, at the counter (QR and wallet tenders) and on the website and app: what the provider confirmed and refunded, and whether the store recorded it as the order's tender. A paid payment not recorded yet is usually still on its way from the store, or waiting for the store to accept the order.",
      "columns": {
        "time": "Time",
        "reference": "Order",
        "channel": "Where",
        "store": "Store",
        "account": "Account",
        "amount": "Amount",
        "refunded": "Refunded",
        "match": "Reconciliation"
      },
      "filters": {
        "provider": "Provider",
        "store": "Store",
        "entity": "Company",
        "source": "Where",
        "state": "Payment",
        "all": "All",
        "date": "Created"
      },
      "source": {
        "store_payment": "Counter (POS)",
        "online_order": "Website or app"
      },
      "state": {
        "open": "Waiting for the customer",
        "succeeded": "Paid",
        "refunded": "Refunded",
        "not_paid": "Not paid"
      },
      "match": {
        "matched": "Matched",
        "not_recorded": "Not recorded",
        "amount_differs": "Amount differs",
        "refunded": "Refunded",
        "open": "Waiting",
        "not_paid": "Not paid"
      },
      "totals": "{{count}} payments, {{paid}} paid ({{succeeded}}), {{refunded}} refunded",
      "detail": {
        "intent": "Payment id",
        "providerTransaction": "Provider transaction",
        "recorded": "Recorded at the store",
        "recordedOrder": "Store order",
        "paidAt": "Paid at",
        "late": "Paid after it ended (refunded)",
        "state": "Provider state",
        "entity": "Company"
      }
    }
  },
  "crm": {
    "segments": {
      "people": {
        "tab": "People",
        "title": "People in this segment",
        "empty": "Nobody in this segment yet",
        "emptyHint": "Segments are worked out every day after the day's figures, or soon after you choose Refresh now.",
        "name": "Name",
        "noName": "No name",
        "phone": "Phone",
        "email": "Email",
        "kind": "Kind",
        "member": "Member",
        "guest": "Guest",
        "orders": "Orders, last 365 days",
        "spend": "Spent, last 365 days",
        "lastOrder": "Last order"
      },
      "trend": {
        "day": "Day",
        "people": "People",
        "summary": "{{count}} people now."
      },
      "list": {
        "tab": "List",
        "notList": "Only an uploaded-list segment takes a list. Its people come from its conditions or from the segments it combines.",
        "label": "People, one per line",
        "help": "A phone number, an email or a member account number on each line. Phones and emails are kept only as keys, never as text.",
        "mode": "With this list",
        "add": "Add these people",
        "replace": "Replace the list with these people",
        "upload": "Upload {{count}} lines",
        "saved": "{{count}} people saved. The segment is worked out again in a few minutes.",
        "invalid": "{{count}} lines weren't a phone, an email or an account number.",
        "unknown": "No member has these account numbers: {{numbers}}."
      },
      "refresh": {
        "button": "Refresh now",
        "asked": "Refresh asked"
      },
      "conditions": {
        "notRules": "Only a segment by conditions has conditions."
      },
      "estimate": {
        "button": "Estimate people",
        "result": "About {{count}} of {{people}} people.",
        "sample": "From a sample of {{sampled}} people; the segment's own run counts everyone."
      },
      "export": {
        "masked": "Export people",
        "revealed": "Export with phone and email"
      }
    }
  },
  "cms": {
    "blocks": {
      "title": "Blocks",
      "add": "Add a block",
      "addItem": "Add",
      "up": "Move up",
      "down": "Move down",
      "remove": "Remove",
      "types": {
        "hero": "Banner slides",
        "order_starter": "Start an order",
        "rich_text": "Text",
        "image": "Image",
        "tiles": "Tiles",
        "cta": "Call to action",
        "featured_items": "Featured dishes",
        "deals_row": "Deals",
        "store_list": "Stores",
        "app_download": "App download",
        "faq": "Questions and answers",
        "form": "Form"
      }
    },
    "fields": {
      "slides": "Slides",
      "image": "Image",
      "imageNarrow": "Image for phones",
      "title": "Title",
      "text": "Text",
      "buttonLabel": "Button",
      "href": "Link",
      "linkHelp": "A page of the website (/order-online) or an https address.",
      "alt": "Describe the image",
      "caption": "Caption",
      "tiles": "Tiles",
      "categoryId": "Category (empty: all)",
      "limit": "How many",
      "headerLinks": "Header menu",
      "footerColumns": "Footer columns",
      "links": "Links",
      "label": "Label",
      "social": "Social links",
      "network": "Network",
      "apps": "Apps",
      "ios": "App Store link",
      "android": "Google Play link",
      "legal": "Company details",
      "companyName": "Company name",
      "address": "Address",
      "registration": "Registration",
      "taxCode": "Tax code",
      "hotline": "Hotline",
      "email": "Email",
      "type": "Type (e.g. Deal)",
      "days": "Days (none: every day)",
      "hours": "Hours",
      "from": "From (hour)",
      "to": "To (hour)",
      "channels": "Ways of ordering (none: all)",
      "dealId": "Deal to order",
      "orderHref": "Order button goes to (empty: order online)",
      "terms": "Terms",
      "items": "Questions",
      "question": "Question",
      "answer": "Answer",
      "formId": "Form",
      "fields": "Fields",
      "key": "Key (letters, digits, _)",
      "kind": "Kind",
      "required": "Required",
      "help": "Help text",
      "options": "Choices (for a list)",
      "value": "Value",
      "submitLabel": "Button",
      "successText": "Text once sent",
      "consent": "Consent to tick (empty: none)"
    },
    "options": {
      "network": {
        "facebook": "Facebook",
        "instagram": "Instagram",
        "tiktok": "TikTok",
        "youtube": "YouTube",
        "zalo": "Zalo",
        "x": "X"
      },
      "days": {
        "0": "Sunday",
        "1": "Monday",
        "2": "Tuesday",
        "3": "Wednesday",
        "4": "Thursday",
        "5": "Friday",
        "6": "Saturday"
      },
      "channels": {
        "delivery": "Delivery",
        "pickup": "Pickup",
        "dine_in": "Dine in"
      },
      "kind": {
        "text": "Short text",
        "email": "Email",
        "phone": "Phone",
        "textarea": "Long text",
        "number": "Number",
        "date": "Date",
        "select": "List",
        "checkbox": "Checkbox"
      }
    },
    "publish": {
      "button": "Publish",
      "unpublish": "Take down",
      "title": "Publish to the website",
      "confirm": "Publish",
      "hint": "The draft as it is now becomes a new version on the website.",
      "from": "Show from",
      "fromHelp": "Empty: now.",
      "until": "Show until",
      "untilHelp": "Empty: until it is taken down.",
      "note": "Note"
    },
    "versions": {
      "tab": "Versions",
      "title": "Published versions",
      "empty": "Not published yet",
      "version": "Version",
      "onWebsite": "On the website",
      "publishedAt": "Published",
      "window": "Shown",
      "note": "Note",
      "restore": "Make it the draft",
      "restored": "That version is the draft now. Publish it to put it back on the website.",
      "changes": "The draft has changes that aren't on the website yet."
    },
    "editor": {
      "toolbar": "Formatting for {{label}}",
      "style": "Text style",
      "paragraph": "Paragraph",
      "heading": "Heading",
      "subheading": "Subheading",
      "smallHeading": "Small heading",
      "bold": "Bold (Ctrl+B)",
      "italic": "Italic (Ctrl+I)",
      "link": "Link (Ctrl+K)",
      "unlink": "Remove link",
      "bulletList": "Bulleted list",
      "orderedList": "Numbered list",
      "quote": "Quote",
      "image": "Image",
      "table": "Table",
      "divider": "Divider",
      "undo": "Undo (Ctrl+Z)",
      "redo": "Redo (Ctrl+Shift+Z)",
      "tableTools": "Table",
      "addRow": "Add a row below",
      "addColumn": "Add a column to the right",
      "deleteRow": "Delete this row",
      "deleteColumn": "Delete this column",
      "deleteTable": "Delete the table",
      "linkTitle": "Link",
      "linkText": "Text to show",
      "linkTextRequired": "Enter the text to show.",
      "linkAddress": "Address",
      "linkInvalid": "Use a page of the website (/stores) or an address starting with https://.",
      "apply": "Apply",
      "altTitle": "Describe the image",
      "altHelp": "Read aloud by screen readers and shown when the image can't load.",
      "altRequired": "Describe what the image shows.",
      "insert": "Insert",
      "count": "{{count}} of {{max}} characters",
      "tooLong": "Too long: at most {{max}} characters. Shorten the text or split it into another block.",
      "insertValue": "Insert value",
      "importHtml": "Import HTML",
      "copyHtml": "Copy as HTML",
      "copied": "Copied as HTML and plain text.",
      "importTitle": "Import HTML",
      "importSource": "HTML source",
      "importHelp": "Paste the source of an email or a web page.",
      "importNote": "Only the words and their formats are kept: headings, bold, italic, links, lists, quotes, tables, pictures and dividers. Layout and styling aren't: the brand's email layout wraps every email. The import replaces the current text.",
      "import": "Import",
      "emailLinkHelp": "An address starting with https://, mailto: or tel:, a value such as {tracking_url}, or an https address with values in it.",
      "emailLinkInvalid": "Enter a link starting with https://, mailto: or tel:, or a value."
    },
    "promotion": {
      "title": "Promotion details"
    },
    "forms": {
      "title": "Forms",
      "choose": "Choose a form",
      "none": "This brand has no forms yet: make one under CMS › Forms.",
      "brandFirst": "Choose the brand first."
    },
    "preview": {
      "button": "Preview",
      "ready": "Preview opened in a new tab. The link shows the saved draft until {{until}}; save first to include your latest changes."
    }
  },
  "customerAccounts": {
    "title": "Customers",
    "intro": "Customers' accounts on the website and app: find one by an exact phone number or email, or the beginning of a name. Contact details are masked.",
    "search": "Phone, email or name",
    "allStatuses": "All statuses",
    "empty": "No website accounts yet.",
    "noMatch": "No account matches. Phone and email must be exact; a name matches from its beginning.",
    "unnamed": "Account without a name",
    "detailIntro": "Devices, activity and support actions. Actions take effect on the website within a few seconds.",
    "status": {
      "active": "Active",
      "locked": "Locked",
      "erased": "Erased"
    },
    "columns": {
      "name": "Name",
      "phone": "Phone",
      "email": "Email",
      "member": "Member account",
      "lastLogin": "Last sign-in",
      "status": "Status",
      "device": "Device",
      "network": "Network",
      "channel": "Channel",
      "signedIn": "Signed in",
      "lastUsed": "Last used",
      "when": "When",
      "event": "Event",
      "actor": "By",
      "detail": "Detail",
      "userAgent": "Browser (user agent)"
    },
    "fields": {
      "status": "Status",
      "phone": "Phone",
      "email": "Email",
      "member": "Member account",
      "password": "Password",
      "createdAt": "Created",
      "lastLogin": "Last sign-in"
    },
    "profile": "Account",
    "devices": "Devices",
    "activity": "Activity",
    "noDevices": "No device is signed in.",
    "noActivity": "No activity.",
    "unknownDevice": "Unknown device",
    "more": "Show older activity",
    "passwordSet": "Set",
    "passwordNone": "Not set (signs in with a code)",
    "passwordLocked": "Locked until {{until}} after wrong tries",
    "reveal": "Reveal contact",
    "revealed": "Full contact details (this view was recorded)",
    "sent": "Sent. The website applies it within a few seconds.",
    "actor": {
      "customer": "Customer",
      "staff": "Staff",
      "system": "System"
    },
    "commands": {
      "lock": "Lock account",
      "unlock": "Unlock account",
      "sign_out": "Sign out everywhere",
      "clear_lockout": "Clear password lock",
      "erase": "Erase account"
    },
    "confirm": {
      "lock": {
        "title": "Lock this account?",
        "text": "Every device is signed out and the customer can't sign in until the account is unlocked."
      },
      "unlock": {
        "title": "Unlock this account?",
        "text": "The customer can sign in again."
      },
      "sign_out": {
        "title": "Sign out every device?",
        "text": "The customer has to sign in again on each device."
      },
      "clear_lockout": {
        "title": "Clear the password lock?",
        "text": "The customer can try their password again at once."
      },
      "erase": {
        "title": "Erase this account?",
        "text": "The customer's details, password, addresses and devices are removed and their online orders keep only redacted details. This can't be undone. Use it only on the customer's request."
      }
    },
    "events": {
      "account_created": "Account created",
      "signed_in": "Signed in",
      "code_failed": "Wrong code entered",
      "password_failed": "Wrong password entered",
      "password_locked": "Password locked (wrong tries)",
      "signed_out": "Signed out",
      "session_ended": "A device signed out",
      "session_reuse": "A session used twice: device signed out",
      "password_set": "Password set",
      "password_reset": "Password reset with a code",
      "profile_changed": "Profile changed",
      "address_added": "Address added",
      "address_changed": "Address changed",
      "address_removed": "Address removed",
      "staff_locked": "Locked by staff",
      "staff_unlocked": "Unlocked by staff",
      "staff_signed_out": "Signed out everywhere by staff",
      "staff_lockout_cleared": "Password lock cleared by staff",
      "staff_erased": "Erased by staff",
      "account_copied": "Copied to HO (account from before the activity log)"
    },
    "revealNetwork": "Reveal IP addresses and devices"
  },
  "formSubmissions": {
    "title": "Form submissions",
    "intro": "What visitors sent through the website's forms. Opening one is recorded in the audit log; submissions are deleted after a year.",
    "allStatuses": "All states",
    "empty": "Nothing sent yet.",
    "status": {
      "new": "New",
      "handled": "Handled",
      "spam": "Spam"
    },
    "columns": {
      "when": "Sent",
      "form": "Form",
      "preview": "First answer",
      "status": "State"
    },
    "answers": "Answers",
    "consent": "Agreed to be contacted",
    "handledAt": "Handled",
    "note": "Note",
    "noteHelp": "Saved with the state you choose.",
    "markHandled": "Mark handled",
    "markSpam": "Mark as spam",
    "reopen": "Mark new",
    "saved": "Saved",
    "sentAt": "Sent {{when}}",
    "yes": "Yes",
    "no": "No"
  },
  "onlineOrders": {
    "title": "Orders",
    "intro": "Every website and app order: where it is, what was ordered and how it is paid. The store answers orders; nothing is changed here. Phones are masked.",
    "search": "Order number or customer phone",
    "empty": "No online orders yet.",
    "progress": "Order progress",
    "summary": "Order",
    "items": "Items",
    "receivedAt": "Received {{when}}",
    "reveal": "Reveal contact",
    "revealed": "Customer contact (recorded in the audit log)",
    "prepaid": "paid online",
    "minutes_one": "{{count}} minute",
    "minutes_other": "{{count}} minutes",
    "columns": {
      "number": "Order",
      "received": "Received",
      "store": "Store",
      "channel": "Channel",
      "customer": "Customer",
      "fulfilment": "Type",
      "payment": "Payment",
      "total": "Total",
      "status": "Status",
      "item": "Item",
      "quantity": "Qty",
      "amount": "Amount"
    },
    "fields": {
      "phone": "Phone",
      "note": "Note to the store",
      "prepMinutes": "Prep time",
      "acceptBy": "Store must answer by"
    },
    "fulfilment": {
      "delivery": "Delivery",
      "pickup": "Pickup",
      "dine_in": "Dine in"
    },
    "payment": {
      "cod": "Cash on delivery",
      "pay_at_pickup": "Pay at pickup",
      "online": "Online"
    },
    "status": {
      "received": "Received",
      "awaiting_payment": "Waiting for payment",
      "relayed": "Sent to the store",
      "accepted": "Accepted",
      "preparing": "Preparing",
      "ready": "Ready",
      "out_for_delivery": "On its way",
      "delivered": "Completed",
      "rejected": "Not taken",
      "cancelled": "Cancelled"
    },
    "steps": {
      "received": "Received",
      "relayed": "Sent to the store",
      "accepted": "Accepted",
      "ready": "Ready",
      "outForDelivery": "On its way",
      "completed": "Completed",
      "served": "Served"
    },
    "reasons": {
      "store_unavailable": "The store wasn't taking orders",
      "store_busy": "The store had as many open orders as it takes",
      "not_answered": "The store didn't answer in time",
      "payment_failed": "The payment failed",
      "payment_expired": "The payment wasn't made in time",
      "sold_out": "Sold out",
      "too_busy": "The store was too busy",
      "closing": "The store was closing",
      "out_of_area": "Outside the delivery area",
      "other": "Another reason",
      "customer_cancelled": "The customer cancelled it"
    },
    "totals": {
      "title": "Totals",
      "subtotal": "Items",
      "discounts": "Discounts",
      "charges": "Charges",
      "deliveryFee": "Delivery fee",
      "tax": "VAT",
      "total": "Total"
    }
  },
  "posLayouts": {
    "saleTypes": "Sale types listed",
    "saleTypesPlaceholder": "Every sale type",
    "saleTypesHelp": "The section lists the open orders of these sale types. None chosen: every sale type."
  },
  "salesChannels": {
    "title": "Sales channels",
    "tab": "Sales channels",
    "intro": "Where this store sells. Switch a channel off to stop selling on it here; its settings are kept. Online channels take orders during their ordering hours, or the store's opening hours when none are set.",
    "channel": "Channel",
    "status": "Status",
    "orderingHours": "Online ordering hours",
    "sells": "Sells here",
    "sellsOn": "Sell on {{name}} at this store",
    "alwaysOn": "Always on",
    "off": "Off",
    "state": {
      "open": "Taking orders",
      "paused": "Paused",
      "closed": "Closed"
    },
    "windows_one": "{{count}} window",
    "windows_other": "{{count}} windows",
    "sameAsOpening": "Store opening hours",
    "editHours": "Edit hours",
    "closeHours": "Close",
    "hoursFor": "Online ordering hours: {{name}}",
    "hoursHelp": "Add one or more time windows for each day, e.g. 10:00–14:00 and 17:00–21:30. A window ending at or before its start runs past midnight. Orders are taken only inside a window and while the store is open. With no windows at all, the store's opening hours apply.",
    "copyFirstDay": "Same hours every day",
    "useOpeningHours": "Use opening hours",
    "copyFrom": "Copy from another channel",
    "opens": "From",
    "closes": "To",
    "removeWindow": "Remove this window",
    "addWindow": "Add window",
    "closedThisDay": "No online orders",
    "openingHoursThisDay": "Opening hours"
  },
  "reservations": {
    "tab": "Reservations",
    "intro": "Table bookings at this store: taken on the website and app and by staff at the POS. One set of rules for every channel. Online bookings wait for the store to confirm them on the POS. Changes reach the store and the website when master data is published (System › Publish master data).",
    "saved": "Saved. Publish master data (System › Publish master data) to send it to the store and the website.",
    "minutes_one": "{{count}} minute",
    "minutes_other": "{{count}} minutes",
    "hours": "Reservation hours",
    "hoursHelp": "When tables can be booked, each weekday; several windows a day if needed. Slots start at the beginning of each window. A window ending at or before its start runs past midnight. No windows: the store's opening hours.",
    "opens": "From",
    "closes": "To",
    "removeWindow": "Remove this window",
    "addWindow": "Add window",
    "noBookingsThisDay": "No bookings",
    "openingHoursThisDay": "Opening hours",
    "fields": {
      "enabled": "Take table bookings",
      "on": "On",
      "off": "Off",
      "slotMinutes": "Slot length",
      "daysAhead": "Days ahead customers can book",
      "maxParty": "Largest party",
      "slotBookings": "Most bookings per slot",
      "slotGuests": "Most guests per slot",
      "acceptMinutes": "Minutes for the store to answer an online booking",
      "noLimit": "Leave empty for no limit"
    }
  },
  "reports": {
    "allStores": "All stores",
    "store": "Store",
    "period": "Period",
    "compareWith": "Compare with",
    "compare": {
      "previous": "Previous period",
      "last_year": "Same weekdays last year",
      "none": "No comparison"
    },
    "vs": "vs {{value}}",
    "new": "New",
    "downloadCsv": "Download CSV",
    "date": "Date",
    "orders": "Orders",
    "guests": "Guests",
    "total": "Total sales",
    "averageCheck": "Average check",
    "discounts": "Discounts",
    "tax": "Tax",
    "refunds": "Refunds",
    "voids": "Voids",
    "quantity": "Quantity",
    "net": "Net",
    "share": "Share",
    "channel": "Channel",
    "saleType": "Sale type",
    "noSales": "No sales in this period",
    "weekdays": {
      "1": "Mon",
      "2": "Tue",
      "3": "Wed",
      "4": "Thu",
      "5": "Fri",
      "6": "Sat",
      "7": "Sun"
    },
    "sales": {
      "title": "Sales",
      "description": "Paid orders across stores or for one, against the previous period or the same weekdays last year. Figures follow the stores' sync, about a minute behind.",
      "byDay": "Sales by day",
      "byDaySummary": "Sales by day, {{total}} in all",
      "byStore": "Sales by store",
      "byChannel": "Sales by channel and sale type",
      "byGuestType": "Guests by type",
      "guestType": "Guest type",
      "guests": "Guests",
      "minutesOpen": "Minutes open",
      "growth": "Growth",
      "compareTotal": "Compared total"
    },
    "products": {
      "title": "Product mix",
      "description": "What each item, deal and category sold: quantity, net sales, share, and the margin over standard cost where the item has one. Items sold inside deals count as items; a deal row is what its items don't carry.",
      "byCategory": "By category",
      "top": "Top products",
      "product": "Product",
      "kind": "Kind",
      "category": "Category",
      "margin": "Margin",
      "cost": "Standard cost",
      "voided": "Voided",
      "openItems": "Open-price items",
      "noCategory": "No category",
      "kinds": {
        "item": "Item",
        "deal": "Deal",
        "open": "Open price",
        "category": "Category"
      },
      "bestSeller": "Best seller",
      "costedMargin": "Margin on costed products",
      "costedCaption": "Products with a standard cost: {{share}} of net sales"
    },
    "hours": {
      "title": "Sales by hour",
      "description": "When orders are placed: sales and orders by weekday and the hour orders were opened, in each store's time zone.",
      "hour": "Hour",
      "grid": "Sales and orders by weekday and hour",
      "summary": "Sales by the hour orders were opened",
      "note": "Each cell: sales · orders, for the weekdays in the period.",
      "busiestHour": "Busiest hour",
      "busiestDay": "Busiest weekday"
    },
    "payments": {
      "title": "Payments and tax",
      "description": "What was taken per payment method, for bank reconciliation, and the output tax per store and rate as the stores recorded it, for the VAT return.",
      "byMethod": "Takings by payment method",
      "nonRefundable": "Non-refundable vouchers",
      "forfeited": "Excess kept",
      "method": "Method",
      "paid": "Paid",
      "refunded": "Refunded",
      "count": "Payments",
      "none": "No payments in this period",
      "taxByStore": "Output tax by store and rate",
      "rate": "Rate",
      "taxable": "Taxable",
      "netTaken": "Net taken",
      "outputTax": "Output tax"
    },
    "qr": {
      "title": "QR reconciliation",
      "description": "Every QR, bank transfer and e-wallet payment the stores confirmed by hand (when the provider couldn't confirm it), next to what the provider shows: a payment head office received, or a line of a settlement or bank statement you uploaded. What needs a look comes first.",
      "upload": "Upload statement (CSV)",
      "uploaded": "{{imported}} lines added, {{duplicates}} already uploaded, {{skipped}} money out skipped.",
      "rejected": "Lines not read: {{lines}}.",
      "reason": {
        "paid_at_invalid": "date",
        "amount_invalid": "amount",
        "store_unknown": "store code"
      },
      "capped": "More than 5,000 confirmations: showing the first 5,000. Choose a shorter period or one store.",
      "confirmations": "Confirmed by hand",
      "matched": "Proven",
      "toCheck": "To check",
      "count_one": "{{count}} payment",
      "count_other": "{{count}} payments",
      "confirmationsTitle": "Manual confirmations",
      "status": "Status",
      "paidAt": "Paid",
      "store": "Store",
      "method": "Method",
      "reference": "Reference",
      "amount": "Amount",
      "evidence": "Provider shows",
      "evidenceAmount": "Provider amount",
      "staff": "Confirmed by",
      "approved": "approved",
      "none": "No payments confirmed by hand in this period",
      "match": {
        "not_found": "Not found",
        "amount_differs": "Amount differs",
        "reference": "Matched by reference",
        "gateway": "Paid to head office",
        "amount_time": "Same amount and time"
      },
      "how": "Matching: a statement line naming the payment's reference first, then a payment the provider confirmed to head office for the same store and amount within 30 minutes, then a line for the same amount within 30 minutes. Each provider payment or line proves one payment only.",
      "uploads": "Uploaded statements",
      "file": "File",
      "uploadedAt": "Uploaded",
      "lines": "Lines",
      "duplicates": "Already uploaded",
      "noUploads": "No statements uploaded yet",
      "templateHint": "Export the bank's or provider's statement and arrange it as the template: paid_at, amount, and if you have them transaction_id, memo, store_code.",
      "template": "Download template",
      "removeTitle": "Remove this upload?",
      "removeBody": "{{file}} and its lines are removed. Payments it proved show as not found until another statement proves them."
    },
    "controls": {
      "title": "Voids and discounts",
      "description": "What was voided, discounted, refunded or sold over the menu price, at which store, by whom, who approved it and why. Voids after the kitchen had the item and a staff member's discounts as a share of their sales are the usual signs to look into.",
      "allKinds": "All",
      "kindLabel": "Kind",
      "kind": {
        "line_void": "Line voided",
        "order_void": "Order voided",
        "discount": "Discount",
        "refund": "Refund",
        "price_override": "Price changed"
      },
      "voids": "Voids",
      "voidsCaption": "{{count}} voids, {{afterSend}} after the kitchen had them",
      "discounts": "Discounts",
      "shareCaption": "{{count}} discounts · {{share}} of sales",
      "refunds": "Refunds",
      "count_one": "{{count}} refund",
      "count_other": "{{count}} refunds",
      "overrides": "Prices changed",
      "approvedCaption": "{{count}} approved by a manager",
      "byStaff": "By staff member",
      "staff": "Staff",
      "sales": "Sales of their lines",
      "unknown": "Unknown",
      "staffNote": "Each line: how many · how much. Discounts also as a share of the sales of the lines the staff member added. The 50 staff members with most events.",
      "events": "Events",
      "when": "When",
      "store": "Store",
      "order": "Order",
      "item": "Item",
      "amount": "Amount",
      "kitchen": "Kitchen",
      "afterSend": "After sending",
      "reason": "Reason",
      "approvedBy": "Approved by",
      "none": "Nothing voided, discounted or refunded in this period",
      "detail": {
        "manual": "Manual",
        "promotion": "Promotion",
        "coupon": "Code",
        "member": "Member",
        "marketplace": "Marketplace",
        "points": "Points",
        "cash": "Cash refund",
        "card": "Card refund",
        "qr": "QR refund",
        "bank_transfer": "Transfer refund",
        "e_wallet": "E-wallet refund"
      }
    },
    "journal": {
      "title": "Sales journal",
      "description": "For the accountant: one row per store and business day with the sales, discounts, charges, tax at each rate, rounding, total, refunds and what each payment method took, as the stores recorded them. The CSV has one column per tax rate and per payment method, ready to import or check against the bank. At most 31 days at a time.",
      "date": "Business day",
      "storeCode": "Store code",
      "store": "Store",
      "orders": "Orders",
      "subtotal": "Items",
      "discounts": "Discounts",
      "charges": "Charges",
      "tax": "Tax",
      "taxable": "taxable",
      "rounding": "Rounding",
      "total": "Total",
      "refunds": "Refunds",
      "tenders": "Takings by method",
      "note": "Sales are paid orders by their business day; refunds and takings by the day they were made. Total = items − discounts + charges (+ tax when added on top) + rounding.",
      "byDay": "By store and business day"
    },
    "einvoices": {
      "title": "E-invoice reconciliation",
      "description": "Per store and payment date, the paid orders next to their e-invoices: issued, still waiting (held or on their way), failed, missing (no e-invoice request at head office), or not set up (the store had no e-invoice profile). Invoices refunded after they were issued must be adjusted.",
      "date": "Payment date",
      "store": "Store",
      "paidOrders": "Paid orders",
      "paidTotal": "Paid total",
      "issued": "Issued",
      "issuedAmount": "Issued amount",
      "waiting": "Waiting",
      "failed": "Failed",
      "missing": "Missing",
      "notConfigured": "Not set up",
      "company": "Company buyers",
      "toAdjust": "To adjust",
      "issuedTile": "Invoiced",
      "ofPaid": "{{count}} invoices of {{orders}} paid orders ({{paid}})",
      "count_one": "{{count}} invoice",
      "count_other": "{{count}} invoices",
      "toCheck": "To check",
      "toCheckCaption": "{{failed}} failed · {{missing}} missing · {{notConfigured}} not set up",
      "toAdjustCaption": "Refunded after they were issued",
      "problems": "To look at",
      "ok": "All invoiced",
      "failedTag": "{{count}} failed",
      "missingTag": "{{count}} missing",
      "notConfiguredTag": "{{count}} not set up",
      "adjustTag": "{{count}} to adjust",
      "note": "Dates are the day the orders were paid, in each store's time zone. Waiting invoices are normal for today: the profile holds them before sending.",
      "openInvoices": "Open e-invoices to retry or fix them",
      "byDay": "By store and payment date"
    },
    "menu": {
      "title": "Menu engineering",
      "description": "Which items sell well and earn well: popularity against margin, from the standard cost of each item.",
      "item": "Item",
      "category": "Category",
      "cost": "Standard cost",
      "mix": "Share of mix",
      "margin": "Margin per item",
      "class": "Class",
      "classes": {
        "star": "Stars",
        "plowhorse": "Plowhorses",
        "puzzle": "Puzzles",
        "dog": "Dogs",
        "no_cost": "No cost"
      },
      "hints": {
        "star": "Popular and profitable: keep them",
        "plowhorse": "Popular, low margin: review price or cost",
        "puzzle": "Profitable, rarely ordered: promote them",
        "dog": "Neither: rework or remove"
      },
      "thresholds": "Popular means at least {{popular}} of the items sold; profitable means a margin per item of at least {{margin}}.",
      "note": "Items sold inside deals count with their share of the deal price. Items without a standard cost are listed as No cost and left out of the average margin. At most the 500 best-selling products are compared.",
      "items": "Items"
    },
    "staff": {
      "title": "Staff performance",
      "description": "Each staff member's paid orders: sales, guests, average check and items per guest, with their voids and discounts.",
      "name": "Staff member",
      "unknown": "Not recorded",
      "itemsPerGuest": "Items per guest",
      "voidAmount": "Voided amount",
      "discountAmount": "Discount amount",
      "note": "Orders count for the staff member who opened them. Voids and discounts count for whoever made them, with their share of that person's sales. The 200 staff members with most sales are shown.",
      "byStaff": "By staff member"
    },
    "operations": {
      "title": "Operations",
      "description": "Table turnover and seat yield, how fast stores answer website and app orders, and table bookings.",
      "store": "Store",
      "parties": "Parties",
      "covers": "Covers",
      "minutes": "Minutes at the table",
      "spendPerGuest": "Spend per guest",
      "tables": "Tables",
      "seats": "Seats",
      "turns": "Turns a day",
      "revpash": "RevPASH",
      "partiesCaption_one": "{{count}} party seated",
      "partiesCaption_other": "{{count}} parties seated",
      "onlineOrders": "Online orders",
      "accepted": "Accepted",
      "rejected": "Rejected",
      "cancelled": "Cancelled",
      "rejectedCaption": "{{share}} rejected",
      "noShowCaption": "{{share}} no-shows",
      "rejectedTag_one": "{{count}} rejected",
      "rejectedTag_other": "{{count}} rejected",
      "cancelledTag_one": "{{count}} cancelled",
      "cancelledTag_other": "{{count}} cancelled",
      "acceptTime": "Time to accept",
      "acceptP90": "Slowest 10%",
      "seconds_one": "{{count}} second",
      "seconds_other": "{{count}} seconds",
      "readyTime": "Minutes to ready",
      "deliveryTime": "Minutes on delivery",
      "tablesTitle": "Tables",
      "onlineTitle": "Website and app orders",
      "bookingsTitle": "Table bookings",
      "tablesNote": "A party is a table session with a paid order. Turns per table use the tables set up now; RevPASH is table sales per seat for each hour the store traded.",
      "reasons": "Why orders were rejected",
      "reason": "Reason",
      "noReason": "No reason given",
      "count": "Orders",
      "bookings": "Bookings",
      "online": "Online",
      "seated": "Seated",
      "noShows": "No-shows",
      "declined": "Declined",
      "expired": "Expired",
      "pending": "Waiting for an answer",
      "noTables": "No table service in this period",
      "noOnline": "No online orders in this period",
      "noBookings": "No bookings in this period",
      "seatedCoversHeader": "Seated covers"
    },
    "customers": {
      "title": "Customers and promotions",
      "description": "Members' share of sales, what promotions and discounts gave away, and what membership owes in points and gift vouchers.",
      "memberSales": "Member sales",
      "memberCaption": "{{share}} of sales · {{orders}} orders · {{members}} members",
      "newMembers": "New members",
      "points": "Points earned",
      "pointsCaption": "{{redeemed}} redeemed",
      "liabilities": "Gift voucher balances",
      "liabilitiesCaption": "{{vouchers}} active vouchers · {{points}} points held",
      "pointsEarned": "Points earned",
      "pointsRedeemed": "Points redeemed",
      "pointsOutstanding": "Points held",
      "vouchersOutstanding": "Gift voucher balances",
      "discountsTitle": "Discounts by source",
      "source": "Source",
      "name": "Name",
      "uses": "Times used",
      "orders": "Orders",
      "amount": "Amount",
      "sources": {
        "manual": "Manual",
        "promotion": "Promotion",
        "coupon": "Discount code",
        "member": "Member price",
        "marketplace": "Marketplace",
        "points": "Points"
      },
      "noDiscounts": "No discounts in this period",
      "note": "Points are calculated at head office only, from synced orders. Points held and gift voucher balances are what is owed now, across every store, whatever the period."
    },
    "periodFrom": "Period from",
    "periodTo": "to",
    "stock": {
      "balances": "Stock on hand",
      "balancesHint": "What every store holds of each stock-tracked item, in its base unit, valued at standard cost, as the stores last sent it.",
      "movements": "Stock movements",
      "movementsHint": "Sales, goods received, waste, adjustments, counts and transfers at the stores in the period, with their totals by type.",
      "transfers": "Stock transfers",
      "transfersHint": "Transfers between stores: what was sent, what arrived and the differences. Select a transfer to see its lines.",
      "counts": "Stock counts",
      "countsHint": "Posted counts and their variances, valued at standard cost. Select a count to see its lines.",
      "store": "Store",
      "item": "Item",
      "onHand": "On hand",
      "unitCost": "Unit cost",
      "value": "Value",
      "lastMovement": "Last movement",
      "searchItems": "Search items",
      "belowZeroOnly": "Below zero only",
      "valueTotal": "Stock value",
      "valueCaption": "At standard cost, items with a cost only",
      "lines": "Lines",
      "noBalances": "No stock yet",
      "when": "When",
      "type": "Type",
      "allTypes": "All types",
      "quantity": "Quantity",
      "note": "Note",
      "count": "Movements",
      "byType": "Totals by type",
      "byTypeHint": "Quantities in each item's base unit; value at the cost recorded with each movement.",
      "movementList": "Movements",
      "noMovements": "No movements in this period",
      "number": "Number",
      "fromStore": "From",
      "toStore": "To",
      "status": "Status",
      "sentAt": "Sent",
      "receivedAt": "Received",
      "postedAt": "Posted",
      "lineCount": "Lines",
      "differenceLines": "Lines with differences",
      "differencesOnly": "With differences only",
      "noTransfers": "No transfers",
      "quantitySent": "Sent",
      "quantityReceived": "Received",
      "missing": "Not received",
      "linesOf": "Lines of {{number}}",
      "variancesOnly": "With variances only",
      "varianceLines": "Lines with variances",
      "varianceValue": "Variance value",
      "noCounts": "No posted counts",
      "expected": "Expected",
      "counted": "Counted",
      "difference": "Difference",
      "state": {
        "sent": "Sent",
        "received": "Received",
        "draft": "Draft",
        "cancelled": "Cancelled"
      },
      "countType": {
        "full": "Full count",
        "partial": "Partial count",
        "spot": "Spot check"
      },
      "txn": {
        "sale": "Sale",
        "receipt": "Goods received",
        "waste": "Waste",
        "adjustment": "Adjustment",
        "count": "Count",
        "transfer_out": "Transfer out",
        "transfer_in": "Transfer in"
      }
    }
  },
  "terminalApp": {
    "title": "Connect to the store",
    "intro": "Type the store server's address, or scan the QR code from store admin → System → POS app.",
    "address": "Store address",
    "addressHelp": "For example s123.pos.example.vn, or http://192.168.1.20:5100 on the store's network",
    "invalid": "Write the store's address, e.g. s123.pos.example.vn. Plain http works only for an address on the store's network, e.g. http://192.168.1.20:5100.",
    "connect": "Connect",
    "scan": "Scan QR code",
    "checking": "Checking the store…",
    "unreachable": "The store doesn't answer",
    "unreachableHelp": "Check the address, that this device is on the store's network, and that the store's certificate is installed.",
    "scanFailed": "The camera couldn't read a store QR code.",
    "saveFailed": "The app couldn't save the store. Try again.",
    "incompatible": "This app doesn't match the store server",
    "incompatibleHelp": "Install the tablet app this store offers (store admin → System → POS app), then open it again. Orders can't be taken until then.",
    "settings": "Server settings",
    "settingsIntro": "The store server this tablet uses. Change it when the server gets a new address or the tablet moves to another store.",
    "current": "Store server: {{address}}",
    "save": "Save and connect"
  },
  "providers": {
    "health": {
      "column": "Health",
      "attentionTitle": "These accounts need someone: their keys, settings or tokens were refused",
      "lastSuccess": "Last worked {{at}}",
      "status": {
        "healthy": "Healthy",
        "failing": "Failing",
        "needs_attention": "Needs attention",
        "idle": "No calls yet"
      }
    }
  },
  "duplicate": {
    "action": "Duplicate",
    "title": "Duplicate {{name}}",
    "confirm": "Duplicate",
    "defaultName": "{{name}} (copy)",
    "menuHint": "The copy has the same items, deals and layouts, and is used nowhere until you add rows to its Where and when tab. Price lists linked to this menu don't price the copy: link them on their Menus tab.",
    "priceListHint": "The copy has the same prices, dates and linked menus, and applies nowhere until you add rows to its Where and when tab.",
    "posProfileHint": "The copy has the same sale types, buttons and settings, its own POS layout, and the payment methods, delivery services and prompts enabled for this profile. No terminal uses it and it isn't synced to any store yet.",
    "roleHint": "The copy has the same permissions and limits. It is never the system admin role, and no one holds it until you give it to staff."
  },
  "roleTemplates": {
    "action": "Create from template",
    "title": "Create a role: {{template}}",
    "hint": "The role starts with the permissions and limits this job usually needs. Change them afterwards like any role.",
    "accounting": "Accounting",
    "marketing": "Marketing",
    "area_manager": "Area manager",
    "store_manager": "Store manager",
    "cashier": "Cashier",
    "kitchen": "Kitchen"
  },
  "roleHolders": {
    "title": "Who holds it",
    "staff": "Staff",
    "status": "Status",
    "where": "Where",
    "tenant": "Whole company",
    "emptyTitle": "No one holds this role",
    "empty": "Give it to staff on their Role assignments tab."
  },
  "signIn": {
    "copied": "Copied",
    "twoStep": {
      "title": "Two-step sign-in",
      "hint": "A code from an authenticator app after your password. Permissions over personal data, money and access work only in a session signed in this way.",
      "on": "Two-step sign-in on",
      "off": "Two-step sign-in off",
      "recoveryLeft_one": "{{count}} recovery code left",
      "recoveryLeft_other": "{{count}} recovery codes left",
      "done": "Two-step sign-in is on.",
      "recoveryHint": "Keep these recovery codes somewhere safe. Each signs you in once if you lose your phone. They won't be shown again.",
      "saved": "I've saved them",
      "setUp": "Set up an authenticator app",
      "step1": "In your authenticator app (Microsoft Authenticator, Google Authenticator...), add an account with this key, or open the link on your phone.",
      "key": "Key",
      "openApp": "Open in authenticator app",
      "step2": "The 6-digit code the app shows now",
      "confirm": "Turn on",
      "turnOff": "Turn off two-step sign-in",
      "turnOffConfirm": "Turn off two-step sign-in?",
      "turnOffBody": "Your authenticator app and recovery codes stop working. Permissions over personal data, money and access stop working until you set it up again.",
      "signInAgain": "This session was signed in without a code. Sign in again with your code to use every permission, or to turn it off.",
      "needed": "Some of your permissions need two-step sign-in",
      "neededHint": "Set up an authenticator app from your account menu, then sign in again."
    },
    "sso": {
      "title": "Sign-in",
      "hint": "Staff sign in with the company's own identity provider (OpenID Connect: Microsoft Entra ID, Google Workspace, Okta...). Their roles are still set here. Once on, passwords work only for break-glass accounts.",
      "enabled": "Sign in with the company's provider",
      "displayName": "Button name",
      "displayNameHint": "Shown on the sign-in screen, e.g. Microsoft.",
      "authority": "Provider address (issuer)",
      "authorityHint": "Starts with https://, e.g. https://login.microsoftonline.com/<tenant id>/v2.0",
      "clientId": "Client id",
      "clientSecret": "Client secret",
      "secretSet": "A secret is set. Type a new one to replace it.",
      "secretNotSet": "No secret set yet.",
      "mfaValues": "Values that mean the provider asked for a second factor",
      "mfaValuesHint": "amr or acr values, separated by commas. Usually mfa.",
      "emailDomains": "Email domains",
      "emailDomainsHint": "A first sign-in links a staff member by email only in these domains, separated by commas. Empty: any verified email.",
      "callback": "Redirect address to register at the provider",
      "test": "Test the provider",
      "testOk": "The provider answered: {{issuer}}",
      "testFailed": "The provider couldn't be reached at this address.",
      "saved": "Saved."
    },
    "staff": {
      "tab": "Sign-in",
      "linked": "Linked to the company's provider",
      "notLinked": "Not linked to the provider",
      "breakGlass": "Break-glass account",
      "breakGlassHint": "Signs in with staff code, password and code even while the company's provider is on, in case it is down. Only company-wide admins change this.",
      "reset": "Reset two-step sign-in",
      "unlink": "Unlink provider account",
      "resetConfirm": "Reset two-step sign-in?",
      "unlinkConfirm": "Unlink the provider account?",
      "resetBody": "Their authenticator app and recovery codes stop working. They set up a new app after their next sign-in.",
      "unlinkBody": "Their next sign-in with the provider links them again by email."
    }
  },
  "customerSecurity": {
    "title": "Customer account security",
    "hint": "Which changes website and app customers confirm with a one-time code sent to their phone. A customer who just signed in with a code isn't asked again. Changes reach the website within about 15 minutes.",
    "codeFor": "Ask for a code to change",
    "changes": {
      "password": "Their password",
      "profile": "Profile details: name, email, birthday, gender, nationality, home address",
      "addresses": "Saved delivery addresses"
    },
    "default": "Not chosen yet: a code for the password and profile details.",
    "saved": "Saved."
  }
}
